import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Activity, CheckCircle, Clock, AlertCircle, Wifi, WifiOff, ExternalLink, Upload, XCircle, RefreshCw, Plus, Trash2, X, Info, AlertTriangle, Download } from "lucide-react";
import { isPlatformAdmin, demoAPI, isDemoWorkspace, getUser } from "../lib/auth";
import { publicDisplayUrl } from "../lib/utils";
const API_BASE = "";

/** Sample bulk CSV matching online MCC parse format (3rd column optional). */
const SAMPLE_BULK_CSV = `merchant_name,url,bank_provided_mcc
Sephora,https://www.sephora.com,5977
Amazon,https://www.amazon.com,5942
Nike,https://www.nike.com,5661
Blue Bell Bakery,https://www.example-bakery.com,
`;

const getHybridHeaders = (baseHeaders = {}) => {
  const token = localStorage.getItem('access_token');
  return token ? { ...baseHeaders, Authorization: `Bearer ${token}` } : baseHeaders;
};

const assertDemoScanAllowed = async (urls) => {
  if (!isDemoWorkspace()) return;
  const viewing = localStorage.getItem('viewing_tenant_id');
  await demoAPI.scanCheck(urls, {
    workspace_is_demo: true,
    tenant_id: viewing || undefined,
  });
};

const getTenantScopeParam = () => {
  const viewingTenantId = localStorage.getItem('viewing_tenant_id');
  return viewingTenantId ? `?tenant_id=${encodeURIComponent(viewingTenantId)}` : '';
};

const getViewingTenantBody = () => {
  const viewingTenantId = localStorage.getItem('viewing_tenant_id');
  if (!viewingTenantId || ['all', 'null', 'undefined'].includes(String(viewingTenantId).toLowerCase())) {
    return {};
  }
  const n = parseInt(viewingTenantId, 10);
  return Number.isFinite(n) ? { tenant_id: n } : {};
};

const fetchMerchantDetailFromApi = async (merchantUrl) => {
  if (!merchantUrl) return null;
  const tenantScope = getTenantScopeParam();
  const detailUrl = `${API_BASE}/api/merchants/${encodeURIComponent(merchantUrl)}${tenantScope}`;
  const response = await fetch(detailUrl, { headers: getHybridHeaders() });
  if (!response.ok) {
    console.warn('Could not load merchant detail:', merchantUrl, response.status);
    return null;
  }
  return response.json();
};

/** Resolve + store server IP geo at scan start (single or bulk). Fire-and-forget OK. */
const resolveUrlLocationsAtScanStart = async (urls, { force = true, async = true } = {}) => {
  const list = (urls || []).map((u) => String(u || '').trim()).filter(Boolean);
  if (!list.length) return;
  try {
    if (list.length === 1) {
      await fetch(`${API_BASE}/api/v1/url-location/resolve/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getHybridHeaders() },
        body: JSON.stringify({ merchant_url: list[0], force, async }),
      });
    } else {
      await fetch(`${API_BASE}/api/v1/url-location/resolve-batch/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getHybridHeaders() },
        body: JSON.stringify({ urls: list, force, async: true }),
      });
    }
  } catch (err) {
    console.warn('URL location resolve at scan start failed:', err);
  }
};

const normalizeProductsForDisplay = (merchantDetail, atsResults) => {
  if (merchantDetail?.products_detailed?.length) {
    return merchantDetail.products_detailed.map((p) => ({
      title: p.name || p.title || 'Product',
      category: p.category || 'Products',
      clean_text: p.description || p.meta_description || '',
      currency: p.currency,
      source_url: publicDisplayUrl(p.source_url),
    }));
  }
  if (atsResults?.products_services?.length) {
    return atsResults.products_services.map((p) => ({
      title: p.title || p.name || 'Product',
      category: p.category || 'Products',
      clean_text: p.clean_text || p.description || '',
    }));
  }
  return [];
};

function CrawlPage({ onConnectionChange } = {}) {
  const AUDIT_LOG_MAX_ENTRIES = 400;
  const user = getUser();
  const isPoc = !!user?.is_poc;
  const platformAdmin = isPlatformAdmin();

  const resolveAuditScope = () => {
    const u = getUser();
    const uid = u?.id || u?.sub || 'anon';
    const viewing = localStorage.getItem('viewing_tenant_id');
    const viewingName = localStorage.getItem('viewing_tenant_name')
      || localStorage.getItem('viewing_tenant_slug')
      || '';
    const tenantId = (isPlatformAdmin() && viewing && !['all', 'null', 'undefined'].includes(String(viewing).toLowerCase()))
      ? String(viewing)
      : String(u?.tenant_id || 'none');
    const tenantName = (isPlatformAdmin() && viewingName)
      ? viewingName
      : (u?.tenant_name || u?.tenant_code || (isPlatformAdmin() ? 'Paylegit Core' : tenantId));
    // Per-user + tenant key so POC / tenant users never share Core crawl history in the same browser.
    const storageKey = `mcc_crawl_audit_log_v2:${uid}:${isPlatformAdmin() ? 'platform' : tenantId}`;
    return { storageKey, tenantId, tenantName };
  };

  const [pocQuota, setPocQuota] = useState(null);

  // Single/Multi URL state
  const [urls, setUrls] = useState([{ merchantName: "", url: "", currentMcc: "" }]);
  const [isCrawling, setIsCrawling] = useState(false);
  const [crawlProgress, setCrawlProgress] = useState(null);
  
  // Bulk upload state
  const [csvFile, setCsvFile] = useState(null);
  const [bulkBatches, setBulkBatches] = useState([]);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({});
  const [failedUrls, setFailedUrls] = useState([]);
  const [retryQueue, setRetryQueue] = useState([]);
  const [bulkUrlList, setBulkUrlList] = useState([]); // Parsed URLs from CSV for editing
  const [showBulkUrlEditor, setShowBulkUrlEditor] = useState(false);
  const [bulkDetailedProgress, setBulkDetailedProgress] = useState({}); // Detailed URL-level tracking
  const [selectedBatchView, setSelectedBatchView] = useState(null); // Which batch to view in detail

  // Above-500 large batch state (OpenAI backend)
  const [largeBatchCsvFile, setLargeBatchCsvFile] = useState(null);
  const [largeBatchUrlList, setLargeBatchUrlList] = useState([]);
  const [isLargeBatchProcessing, setIsLargeBatchProcessing] = useState(false);
  const [largeBatchResults, setLargeBatchResults] = useState(null);
  const [largeBatchError, setLargeBatchError] = useState(null);
  const [largeBatchDetailedProgress, setLargeBatchDetailedProgress] = useState({}); // Per-URL tracking for >500
  const [historyPage, setHistoryPage] = useState(0); // Pagination for Merchant Trigger History

  // Vertex AI (Gemini) classification state
  const [vertexCsvFile, setVertexCsvFile] = useState(null);
  const [vertexUrlList, setVertexUrlList] = useState([]);
  const [isVertexProcessing, setIsVertexProcessing] = useState(false);
  const [vertexResults, setVertexResults] = useState(null);
  const [vertexError, setVertexError] = useState(null);
  const [vertexSingleUrl, setVertexSingleUrl] = useState('');
  const [vertexLiveProgress, setVertexLiveProgress] = useState(null);

  const [bdCsvFile, setBdCsvFile] = useState(null);
  const [bdUrlList, setBdUrlList] = useState([]);
  const [isBdProcessing, setIsBdProcessing] = useState(false);
  const [bdResults, setBdResults] = useState(null);
  const [bdError, setBdError] = useState(null);
  const [bdManualRows, setBdManualRows] = useState([{ merchantName: '', url: '', currentMcc: '' }]);
  const [bdLiveProgress, setBdLiveProgress] = useState(null);
  const [bdConfigured, setBdConfigured] = useState(null);
  const [crawlTab, setCrawlTab] = useState('primary');
  const [fallbackSubTab, setFallbackSubTab] = useState('fallback-single');
  const failoverGuardRef = useRef(new Set());

  useEffect(() => {
    if (isPoc && crawlTab !== 'primary') setCrawlTab('primary');
  }, [isPoc, crawlTab]);

  useEffect(() => {
    if (!isPoc) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/poc/quota`)
      .then(async (res) => {
        const d = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && d?.is_poc) {
          setPocQuota({
            used: d.used,
            quota: d.quota,
            remaining: d.remaining,
            request_extend_email: d.request_extend_email || 'Contact@paylegit.in',
          });
        }
      })
      .catch(() => null);
    return () => { cancelled = true; };
  }, [isPoc]);

  const assertPocQuotaAllows = async (urlCount) => {
    if (!isPoc) return;
    const res = await fetch(`${API_BASE}/api/poc/quota`);
    const d = await res.json().catch(() => ({}));
    if (!res.ok || !d?.is_poc) return;
    const remaining = Number(d.remaining ?? 0);
    setPocQuota({
      used: d.used,
      quota: d.quota,
      remaining,
      request_extend_email: d.request_extend_email || 'Contact@paylegit.in',
    });
    if (urlCount > remaining) {
      const mail = d.request_extend_email || 'Contact@paylegit.in';
      throw new Error(
        remaining <= 0
          ? `POC merchant quota exhausted (${d.used}/${d.quota}). Contact ${mail} to extend.`
          : `Only ${remaining} merchant(s) remaining on your POC quota (${d.used}/${d.quota}). Reduce the batch or contact ${mail}.`,
      );
    }
  };


  
  // Manual Retry Queue state (NEW)
  const [manualRetryQueue, setManualRetryQueue] = useState([]);
  const [selectedRetryIds, setSelectedRetryIds] = useState([]);
  const [isRetrying, setIsRetrying] = useState(false);
  const [showManualRetryQueue, setShowManualRetryQueue] = useState(true);
  
  // Backup Crawl state
  const [backupCrawlLoading, setBackupCrawlLoading] = useState(false);
  const [backupCrawlBatchId, setBackupCrawlBatchId] = useState(null);
  const [backupCrawlStatus, setBackupCrawlStatus] = useState(null);
  const [backupCrawlHistory, setBackupCrawlHistory] = useState([]);
  const [showBackupCrawlHistory, setShowBackupCrawlHistory] = useState(false);
  
  // Crawl Summary Stats (NEW - for Total Merchants, Completed, In-Progress, Success Rate)
  const [crawlSummary, setCrawlSummary] = useState({
    total_merchants: 0,
    completed: 0,
    in_progress: 0,
    failed: 0,
    success_rate: 0
  });
  
  // Time-based filtering for crawl summary
  const [timeFilter, setTimeFilter] = useState('daily');
  
  // Time-based filtering for bulk upload
  const [bulkTimeFilter, setBulkTimeFilter] = useState('daily');
  
  // Legacy state (kept for compatibility)
  const [url, setUrl] = useState("");
  const [atsResults, setAtsResults] = useState(null);
  const [merchantDetail, setMerchantDetail] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [crawlID, setCrawlID] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isClassifying, setIsClassifying] = useState(false);
  const [parseResults, setParseResults] = useState(null);
  const [mccResults, setMccResults] = useState(null);
  const [parsingProgress, setParsingProgress] = useState(null);
  const [mccProgress, setMccProgress] = useState(null);
  const [mccPollingInterval, setMccPollingInterval] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [processingMerchants, setProcessingMerchants] = useState([]); // Track all URLs being processed
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [failureDetails, setFailureDetails] = useState(null);
  const [showFailures, setShowFailures] = useState(false);
  const [toasts, setToasts] = useState([]); // stacked toast notifications
  const [notifiedMerchants, setNotifiedMerchants] = useState(new Set()); // Track already notified merchants
  const [stageTiming, setStageTiming] = useState({}); // Track stage1/2/3 timing for each merchant
  const [backendStatus, setBackendStatus] = useState({}); // Track backend processing status
  const [perUrlStatus, setPerUrlStatus] = useState({}); // Per-URL pipeline step tracker
  const [auditLog, setAuditLog] = useState([]);
  const wsRef = useRef(null);
  const auditDedupeRef = useRef(new Set());
  const navigate = useNavigate();

  // WebSocket connection management
  const connectWebSocket = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    const ws = new WebSocket('/api/fetchers/ws');

    ws.onopen = () => {
      console.log('WebSocket connected');
      setWsConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        handleWebSocketMessage(message);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setWsConnected(false);
      setCrawlID(null);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setWsConnected(false);
    };

    wsRef.current = ws;
  };

  const disconnectWebSocket = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setWsConnected(false);
    setCrawlID(null);
  };

  // Enhanced WebSocket message handling for better real-time updates
  const handleWebSocketMessage = (message) => {
    console.log('WebSocket message:', message);

    switch (message.type) {
      case 'connected':
        setCrawlID(message.data.crawl_id);
        break;
      case 'progress':
        setCrawlProgress(message.data);
        break;
      case 'merchant_update':
        // Handle individual merchant status updates
        const { merchant_url, status, stage, progress, timing } = message.data;
        console.log(`Merchant update: ${merchant_url} - ${status} (${stage})`);
        
        // Update bulk detailed progress if this merchant is in our batch
        setBulkDetailedProgress(prev => {
          if (prev[merchant_url]) {
            return {
              ...prev,
              [merchant_url]: {
                ...prev[merchant_url],
                status: status || prev[merchant_url].status,
                stage: stage || prev[merchant_url].stage,
                progress: progress !== undefined ? progress : prev[merchant_url].progress,
                ...timing
              }
            };
          }
          return prev;
        });
        break;
      case 'batch_complete':
        // Handle batch completion notifications
        const { batch_id, success_count, fail_count } = message.data;
        console.log(`Batch ${batch_id} completed: ${success_count} success, ${fail_count} failed`);
        
        // Update batch status
        setBulkBatches(prev => prev.map(batch => 
          batch.id === batch_id 
            ? { ...batch, status: 'completed', completed: success_count, failed: fail_count, progress: 100 }
            : batch
        ));
        break;
      default:
        console.log('Unknown message type:', message.type);
    }
  };

  // Show toast notification (stacked, auto-dismiss, supports action button)
  const showToast = (type, title, message, action = null) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, type, title, message, action }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 8000);
  };

  const dismissToast = (id) => setToasts(prev => prev.filter(t => t.id !== id));

  const formatMccAuditResult = (mcc, category, confidence) => {
    if (!mcc) return 'Classified';
    let text = String(mcc);
    if (category) text += ` — ${category}`;
    if (confidence != null && confidence !== '') {
      const pct = Number(confidence) <= 1 ? (Number(confidence) * 100).toFixed(0) : Number(confidence).toFixed(0);
      text += ` (${pct}% conf)`;
    }
    return text;
  };

  const formatEngineLabel = (engine) => {
    const labels = {
      openrouter: 'OpenRouter',
      'gemini-cache': 'Gemini (24h cache)',
      openai: 'OpenAI',
      'openai-fallback': 'OpenAI (fallback)',
      backup_crawl: 'Backup crawl',
    };
    return labels[engine] || (engine ? String(engine) : 'MCC Pipeline');
  };

  const formatAuditResult = (item) => {
    if (item.status === 'processing') return item.reason || item.details || 'In progress...';
    if (item.status === 'failed') return item.details || item.reason || 'Failed';
    const details = item.details || '';
    if (/^Engine:\s*/i.test(details)) return item.reason || 'Completed';
    return details || item.reason || 'Completed';
  };

  const recordScanBilling = async ({ merchant_url, merchant_name, upload_type = 'single' }) => {
    try {
      await fetch(`${API_BASE}/api/v1/auth/billing/`, {
        method: 'POST',
        headers: getHybridHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          merchant_url,
          merchant_name: merchant_name || '',
          scan_type: 'classification',
          upload_type: upload_type === 'bulk' ? 'bulk' : 'single',
          merchant_count: 1,
          status: 'completed',
          scan_session_id: `ui:${upload_type}:${merchant_url}:${Date.now()}`,
          ...getViewingTenantBody(),
        }),
      });
    } catch (_) {
      /* billing push is best-effort */
    }
  };

  const addAuditLog = ({ scope, merchantName, url, stage, status, reason, details, dedupeKey }) => {
    if (dedupeKey && auditDedupeRef.current.has(dedupeKey)) {
      return;
    }
    if (dedupeKey) {
      auditDedupeRef.current.add(dedupeKey);
    }

    const { tenantId, tenantName } = resolveAuditScope();
    const entry = {
      id: Date.now() + Math.random(),
      ts: new Date().toISOString(),
      scope: scope || 'single',
      merchantName: merchantName || '',
      url: url || '',
      stage: stage || 'pipeline',
      status: status || 'info',
      reason: reason || '',
      details: details || '',
      tenantId,
      tenantName,
    };

    setAuditLog(prev => [entry, ...prev].slice(0, AUDIT_LOG_MAX_ENTRIES));
  };

  const clearAuditLog = () => {
    setAuditLog([]);
    auditDedupeRef.current.clear();
    try {
      localStorage.removeItem(resolveAuditScope().storageKey);
    } catch (_) { /* ignore */ }
  };

  const normalizeUrlForProcessing = (rawUrl) => {
    const input = (rawUrl || '').trim();
    if (!input) {
      return { ok: false, error: 'URL is required' };
    }

    const toNormalizedUrl = (candidate) => {
      const parsed = new URL(candidate);
      if (!parsed.hostname) return null;
      if (!['http:', 'https:'].includes(parsed.protocol)) return null;
      // Reject hostnames without a dot (except localhost) — catches "not-a-url"
      const host = parsed.hostname.toLowerCase();
      if (host !== 'localhost' && !host.includes('.')) return null;
      if (/\s/.test(host) || host.startsWith('.') || host.endsWith('.')) return null;
      // Canonical: strip trailing slash so /shop and /shop/ are the same key
      const path =
        !parsed.pathname || parsed.pathname === '/'
          ? ''
          : parsed.pathname.replace(/\/+$/, '');
      return `${parsed.protocol}//${host}${path}${parsed.search}`;
    };

    // Primary pass: trim + ensure scheme.
    const compact = input.replace(/\s+/g, '');
    const withScheme = /^https?:\/\//i.test(compact) ? compact : `https://${compact}`;

    try {
      const normalized = toNormalizedUrl(withScheme);
      if (normalized) {
        return { ok: true, url: normalized };
      }
    } catch (_) {
      // Continue to fallback pass.
    }

    // Fallback pass: strip odd prefixes/slashes and force https.
    const stripped = compact
      .replace(/^[a-z]+:\/\//i, '')
      .replace(/^\/+/, '')
      .replace(/^www\./i, 'www.');

    if (stripped) {
      const fallback = `https://${stripped}`;
      try {
        const normalized = toNormalizedUrl(fallback);
        if (normalized) {
          return { ok: true, url: normalized, warning: 'Input auto-formatted using fallback normalization.' };
        }
      } catch (_) {
        // fall through
      }
      return { ok: false, error: `Invalid or malformed URL: ${input}` };
    }

    return { ok: false, error: 'URL is required' };
  };

  /**
   * Shared URL gate for Primary / Fallback / Backup:
   * local normalize → url_validator service → alert invalid → return only valid merchants.
   */
  const validateMerchantsForCrawl = async (incoming, { pipelineLabel = 'Crawler' } = {}) => {
    const localValid = [];
    const localInvalid = [];
    for (const item of incoming || []) {
      const raw = item.url || item.merchant_url || item.merchantUrl || '';
      const name = item.merchant_name || item.merchantName || item.name || '';
      const normalized = normalizeUrlForProcessing(raw);
      if (!normalized.ok || !normalized.url) {
        localInvalid.push({
          url: raw || '(empty)',
          merchant_name: name,
          reason: normalized.error || 'Invalid URL',
        });
        continue;
      }
      localValid.push({
        ...item,
        merchant_name: name,
        merchantName: name,
        url: normalized.url,
        bank_provided_mcc: item.bank_provided_mcc || item.currentMcc || item.bank_mcc || '',
        currentMcc: item.currentMcc || item.bank_provided_mcc || '',
      });
    }

    if (localValid.length === 0) {
      const sample = localInvalid.slice(0, 5).map((x) => x.url).join(', ');
      showToast(
        'error',
        'Invalid URL(s)',
        `${pipelineLabel}: no valid URLs to process.${sample ? ` Examples: ${sample}` : ''} Please provide valid URLs.`
      );
      return { ok: false, merchants: [], invalid: localInvalid };
    }

    let serviceInvalid = [];
    try {
      const res = await fetch(`${API_BASE}/api/url-validator/validate/batch`, {
        method: 'POST',
        headers: getHybridHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          urls: localValid.map((m) => ({ url: m.url, name: m.merchant_name || '' })),
          max_workers: Math.min(10, localValid.length),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        const bad = [...(data.malformed || []), ...(data.invalid || [])];
        const badKeys = new Set(
          bad.map((r) => String(r.original_url || r.normalized_url || '').toLowerCase().replace(/\/$/, ''))
        );
        const byOrig = {};
        for (const r of data.valid || []) {
          const key = String(r.original_url || '').toLowerCase().replace(/\/$/, '');
          byOrig[key] = r;
        }
        const kept = [];
        for (const m of localValid) {
          const key = m.url.toLowerCase().replace(/\/$/, '');
          if (badKeys.has(key)) {
            const match = bad.find(
              (r) => String(r.original_url || '').toLowerCase().replace(/\/$/, '') === key
            );
            serviceInvalid.push({
              url: m.url,
              merchant_name: m.merchant_name,
              reason: (match?.errors || []).join('; ') || 'Malformed or invalid URL',
            });
            continue;
          }
          const vr = byOrig[key];
          const finalUrl = (vr?.final_url || vr?.normalized_url || m.url || '').trim();
          kept.push({
            ...m,
            url: finalUrl || m.url,
            merchant_url: finalUrl || m.url,
          });
        }
        localValid.length = 0;
        localValid.push(...kept);
      }
    } catch (err) {
      console.warn('URL validator service unavailable; using local normalization only:', err);
    }

    const allInvalid = [...localInvalid, ...serviceInvalid];
    if (allInvalid.length > 0) {
      const lines = allInvalid
        .slice(0, 8)
        .map((x) => `• ${x.url}${x.reason ? ` — ${x.reason}` : ''}`)
        .join('\n');
      showToast(
        'warning',
        `${allInvalid.length} invalid URL(s) skipped`,
        `${pipelineLabel} skipped invalid/malformed URLs. Provide a valid URL to include them.\n${lines}`
      );
    }

    if (localValid.length === 0) {
      showToast('error', 'No valid URLs', `${pipelineLabel}: all URLs were invalid. Please correct and retry.`);
      return { ok: false, merchants: [], invalid: allInvalid };
    }

    return { ok: true, merchants: localValid, invalid: allInvalid };
  };

  const isActiveBulkStatus = (progress) => {
    if (!progress) return false;
    const s = (progress.status || '').toLowerCase();
    const stage = (progress.stage || '').toLowerCase();
    return ['processing', 'fetching', 'parsing', 'classifying'].includes(s)
      || ['fetch', 'parse', 'mcc'].includes(stage);
  };

  useEffect(() => {
    try {
      const { storageKey } = resolveAuditScope();
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setAuditLog(parsed.slice(0, AUDIT_LOG_MAX_ENTRIES));
          return;
        }
      }
      // Do not migrate the old shared key for non-platform users (avoids leaking Core history into POC).
      if (isPlatformAdmin()) {
        const legacy = localStorage.getItem('mcc_crawl_audit_log_v1');
        if (legacy) {
          const parsed = JSON.parse(legacy);
          if (Array.isArray(parsed) && parsed.length) {
            setAuditLog(parsed.slice(0, AUDIT_LOG_MAX_ENTRIES));
          }
        }
      } else {
        setAuditLog([]);
      }
    } catch (e) {
      console.warn('Failed to restore audit history:', e);
    }
  }, []);

  useEffect(() => {
    try {
      const { storageKey } = resolveAuditScope();
      localStorage.setItem(storageKey, JSON.stringify(auditLog.slice(0, AUDIT_LOG_MAX_ENTRIES)));
    } catch (e) {
      console.warn('Failed to persist audit history:', e);
    }
  }, [auditLog]);

  useEffect(() => {
    fetch(`${API_BASE}/api/v1/brightdata-google-ai/status`, { headers: getHybridHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && typeof d.configured === 'boolean') setBdConfigured(d.configured);
      })
      .catch(() => {});
  }, []);

  // Fetch Crawl Summary Statistics
  const fetchCrawlSummary = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/analytics?timeline=${timeFilter}`);
      if (response.ok) {
        const data = await response.json();
        const summary = {
          total_merchants: data.total_merchants || 0,
          completed: data.classified_merchants || 0,
          in_progress: (data.total_merchants || 0) - (data.classified_merchants || 0) - (data.failed_merchants || 0),
          failed: data.failed_merchants || 0,
          success_rate: data.success_rate || 0
        };
        setCrawlSummary(summary);
      }
    } catch (err) {
      console.error('Error fetching crawl summary:', err);
    }
  };

  // Fetch Bulk Upload Status with time-based filtering
  const fetchBulkUploadStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/enterprise/bulk-uploads/?period=${bulkTimeFilter}`);
      if (response.ok) {
        const data = await response.json();
        // Update bulk batches with the filtered data
        if (data.batches) {
          setBulkBatches(data.batches);
        }
      }
    } catch (err) {
      console.error('Error fetching bulk upload status:', err);
    }
  };

  // Fetch parsing progress from backend
  // explicitURLs bypasses the stale processingMerchants closure when called right after setProcessingMerchants
  const fetchParsingProgress = async (merchantURL = null, explicitURLs = null) => {
    console.log('🔄 fetchParsingProgress called with:', merchantURL, 'explicit:', explicitURLs);
    console.log('📋 Current processingMerchants state:', processingMerchants);
    
    // If no specific merchant, check all processing merchants
    const merchantsToCheck = merchantURL ? [merchantURL] : (explicitURLs || processingMerchants);
    
    console.log('✅ merchantsToCheck:', merchantsToCheck);
    
    if (merchantsToCheck.length === 0) {
      console.log('❌ Exiting: merchantsToCheck is empty');
      return;
    }

    try {
      console.log('🌐 Fetching from:', `${API_BASE}/api/merchants/progress`);
      // Fetch progress from the progress API
      const response = await fetch(`${API_BASE}/api/merchants/progress`);
      const data = await response.json();
      
      if (!Array.isArray(data) || data.length === 0) {
        console.log("⏳ No progress data available yet (backend still processing)");
        return;
      }

      console.log(`📊 Progress data received: ${data.length} items`);
      console.log('🔍 Looking for merchants:', merchantsToCheck);
      console.log('📦 Available URLs in progress:', data.map(p => p.url));

      // CRITICAL FIX: Filter to ONLY merchant root aggregated progress (page_id starts with "merchant__")
      // Individual page progress shows only fetch stage; merchant root shows parse + MCC stages
      const merchantRootProgress = data.filter(progress => 
        progress.page_id && progress.page_id.startsWith("merchant__")
      );
      
      console.log(`🎯 Filtered to ${merchantRootProgress.length} merchant root entries (from ${data.length} total)`);

      // Filter progress data for our merchants (try multiple URL format matches)
      const relevantProgress = merchantRootProgress.filter(progress => {
        return merchantsToCheck.some(merchantUrl => {
          // Try exact match
          if (progress.url === merchantUrl) return true;
          
          // Try with/without trailing slash
          if (progress.url === merchantUrl + '/') return true;
          if (progress.url + '/' === merchantUrl) return true;
          
          // Try with/without protocol
          const progressUrlNoProtocol = progress.url.replace(/^https?:\/\//, '');
          const merchantUrlNoProtocol = merchantUrl.replace(/^https?:\/\//, '');
          if (progressUrlNoProtocol === merchantUrlNoProtocol) return true;
          if (progressUrlNoProtocol + '/' === merchantUrlNoProtocol) return true;
          if (progressUrlNoProtocol === merchantUrlNoProtocol + '/') return true;
          
          return false;
        });
      });

      if (relevantProgress.length === 0) {
        console.log('⚠️ No matching progress found for our merchants');
        return;
      }

      console.log(`✅ Found ${relevantProgress.length} relevant progress items`);

      // Process each merchant's progress - properly read stages object
      let allCompleted = true;
      let anyFetching = false;
      let anyParsing = false;
      let anyMCC = false;
      let completedCount = 0;
      let failedCount = 0;

      relevantProgress.forEach(progress => {
        // Check pipeline stages using the stages object from backend
        const stages = progress.stages || {};
        
        console.log(`📈 ${progress.url}: status=${progress.status}, fetch=${stages.fetch?.status}, parse=${stages.parse?.status}, mcc=${stages.mcc?.status}`);

        // Update per-URL step tracker based on parsers stage data
        setPerUrlStatus(prev => {
          const existing = prev[progress.url] || { steps: [] };
          const now = new Date().toLocaleTimeString();
          const updatedSteps = [...existing.steps];

          const upsertStep = (id, label, status, detail) => {
            const idx = updatedSteps.findIndex(s => s.id === id);
            if (idx >= 0) {
              updatedSteps[idx] = { ...updatedSteps[idx], label, status, detail, time: now };
            } else {
              updatedSteps.push({ id, label, status, detail, time: now });
            }
          };

          if (stages.fetch?.status === 'running') upsertStep('fetch', '🌐 Fetching website', 'active', '');
          if (stages.fetch?.status === 'completed') upsertStep('fetch', '🌐 Website fetched', 'done', '');
          if (stages.parse?.status === 'running') upsertStep('parse', '📝 Parsing content', 'active', '');
          if (stages.parse?.status === 'completed') upsertStep('parse', '📝 Content parsed', 'done', '');
          if (stages.mcc?.status === 'running') upsertStep('classify', '🤖 AI Classification', 'active', '');
          if (stages.mcc?.status === 'completed') upsertStep('classify', '🤖 AI Classification done', 'done', '');

          return { ...prev, [progress.url]: { ...existing, steps: updatedSteps } };
        });
        
        if (progress.status === 'completed') {
          completedCount++;
        } else if (progress.status === 'error') {
          // Check if this is a timeout/EOF error (backend still processing)
          const errorMsg = progress.error_message || 
                         progress.stages?.fetch?.error_message ||
                         progress.stages?.parse?.error_message ||
                         progress.stages?.mcc?.error_message ||
                         '';
          
          const isTimeoutError = errorMsg.includes('EOF') || 
                                errorMsg.includes('timeout') || 
                                errorMsg.includes('connection') ||
                                errorMsg.includes('ECONNRESET');
          
          if (isTimeoutError) {
            // This is a connection timeout, not a real failure - backend is still processing
            console.log(`⏳ Connection timeout for ${progress.url}, continuing to poll...`);
            allCompleted = false;
            anyMCC = true; // Assume MCC stage since that's typically where timeouts occur
          } else {
            // Real error
            failedCount++;
            
            // Show failure notification only once per merchant
            const merchantKey = progress.url;
            if (!notifiedMerchants.has(merchantKey)) {
              setNotifiedMerchants(prev => new Set([...prev, merchantKey]));
              
              const failedStage = progress.stages?.fetch?.status === 'failed' ? 'Fetch' :
                                 progress.stages?.parse?.status === 'failed' ? 'Parse' :
                                 progress.stages?.mcc?.status === 'failed' ? 'MCC Classification' : 'Processing';
              
              const failureMessage = `${progress.merchant_name || progress.url}\n` +
                `✗ Failed at: ${failedStage}\n` +
                `✗ Error: ${errorMsg.substring(0, 100)}${errorMsg.length > 100 ? '...' : ''}`;
              
              showToast('error', '❌ Processing Failed', failureMessage);

              addAuditLog({
                scope: 'single',
                merchantName: progress.merchant_name || '',
                url: progress.url,
                stage: failedStage.toLowerCase(),
                status: 'failed',
                reason: failedStage,
                details: errorMsg,
                dedupeKey: `single|${failedStage}|failed|${progress.url}|${errorMsg}`
              });
            }
          }
        } else {
          allCompleted = false;
          
          // Determine current stage from stages object
          if (stages.fetch && stages.fetch.status === 'running') {
            anyFetching = true;
          } else if (stages.parse && stages.parse.status === 'running') {
            anyParsing = true;
          } else if (stages.mcc && stages.mcc.status === 'running') {
            anyMCC = true;
          }
        }
      });

      // Update crawl progress if still fetching
      if (anyFetching) {
        const fetchCompleted = relevantProgress.filter(p => p.stages?.fetch?.status === 'completed').length;
        setCrawlProgress({
          status: 'processing',
          message: 'Fetching webpages...',
          discovered_urls: relevantProgress.length,
          processed_urls: fetchCompleted,
          total_urls: relevantProgress.length,
          elapsed_time: Math.max(...relevantProgress.map(p => p.elapsed_time || 0))
        });
        console.log(`🔄 Fetch stage: ${fetchCompleted}/${relevantProgress.length} completed`);
      }

      // Update parsing progress if parsing stage is active or completed
      if (anyParsing || relevantProgress.some(p => p.stages?.parse?.status === 'completed')) {
        setIsParsing(true);
        const parsedCount = relevantProgress.filter(p => p.stages?.parse?.status === 'completed').length;
        const totalPages = relevantProgress.length;
        
        setParsingProgress({
          status: anyParsing ? 'parsing' : 'completed',
          progress_percentage: Math.round((parsedCount / totalPages) * 100),
          parsed_pages: parsedCount,
          total_pages: totalPages,
          error_count: relevantProgress.filter(p => p.stages?.parse?.status === 'failed').length,
          current_page: relevantProgress.find(p => p.stages?.parse?.status === 'running')?.url || ''
        });
        
        console.log(`📝 Parse stage: ${parsedCount}/${totalPages} completed (${anyParsing ? 'active' : 'done'})`);
        
        // If parsing just completed, transition to MCC and load stored products from DB
        if (!anyParsing && parsedCount === totalPages) {
          console.log('✅ Parsing completed, transitioning to MCC...');
          setIsParsing(false);
          if (!anyMCC) {
            setIsClassifying(true);
          }
          const detailUrl = merchantsToCheck[0] || url.trim();
          if (detailUrl) {
            fetchMerchantDetailFromApi(detailUrl).then((detail) => {
              if (detail) setMerchantDetail(detail);
            });
          }
        }
      }

      // Update MCC classification progress (parsers module view — shows "running" until hybrid is done)
      if (anyMCC || relevantProgress.some(p => p.stages?.mcc?.status === 'completed')) {
        setIsClassifying(true);
        setIsParsing(false);

        const mccCompleted = relevantProgress.filter(p => p.stages?.mcc?.status === 'completed').length;
        const mccRunning   = relevantProgress.filter(p => p.stages?.mcc?.status === 'running').length;

        setMccProgress({
          status: 'classifying',
          processed: mccCompleted,
          total: relevantProgress.length,
          progress_percentage: Math.round((mccCompleted / relevantProgress.length) * 100),
          message: `Classifying merchant categories… (${mccRunning} active, ${mccCompleted}/${relevantProgress.length} done)`,
          primary_mcc: null
        });

        setCrawlProgress(prev => ({
          ...prev,
          status: 'processing',
          message: `MCC Classification in progress (${mccCompleted}/${relevantProgress.length})`
        }));

        console.log(`🎯 MCC stage (parsers): ${mccCompleted}/${relevantProgress.length} (${mccRunning} running)`);
      }

      // ── Cross-check hybrid orchestrator for REAL MCC completion ──────────
      // The parsers module never flips stages.mcc to "completed" — only the
      // hybrid orchestrator (separate service) knows when MCC is truly done.
      // Fire whenever at least one merchant is in MCC stage OR all parse is done.
      const parseAllDone = !anyFetching && !anyParsing;
      if (anyMCC || parseAllDone) {
        try {
          const normalizeUrl = (u = '') =>
            u.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');

          let hybridCompletedCount = 0;

          for (const merchantUrl of merchantsToCheck) {
            const hybridResp = await fetch(`${API_BASE}/api/hybrid/merchants?include_unclassified=true&merchant_url=${encodeURIComponent(merchantUrl)}&limit=1`, {
              headers: getHybridHeaders(),
            });
            if (!hybridResp.ok) {
              continue;
            }

            const hybridData = await hybridResp.json();
            const merchants  = hybridData.merchants || [];
            const merchant = merchants.find(m =>
              normalizeUrl(m.merchant_url) === normalizeUrl(merchantUrl)
            );

            if (merchant?.status === 'failed' || merchant?.error_message) {
              const reason = merchant.error_message || 'MCC processing failed';
              setBackendStatus(prev => ({
                ...prev,
                [merchantUrl]: {
                  ...(prev[merchantUrl] || {}),
                  status: 'failed',
                  error: reason,
                  mcc_processed: false
                }
              }));
              addAuditLog({
                scope: 'single',
                merchantName: merchant.merchant_name || '',
                url: merchantUrl,
                stage: 'mcc',
                status: 'failed',
                reason: 'MCC failed',
                details: reason,
                dedupeKey: `single|mcc|failed|${merchantUrl}|${reason}`
              });
              continue;
            }

            // Only treat as done when a real, non-empty MCC code is present
            if (!merchant?.mcc_primary || String(merchant.mcc_primary).trim() === '') continue;

            hybridCompletedCount++;

            // Fire completion UI + toast only once per merchant
            const mKey = merchantUrl;
            if (notifiedMerchants.has(mKey)) continue;
            setNotifiedMerchants(prev => new Set([...prev, mKey]));

            const confidence = merchant.mcc_confidence != null
              ? (merchant.mcc_confidence * 100).toFixed(1) : 'N/A';
            const totalMs = (merchant.fetch_time_ms || 0)
              + (merchant.parse_time_ms || 0)
              + (merchant.processing_time_ms || 0);

            const formattedResults = {
              primary_mcc: {
                mcc_code:   merchant.mcc_primary,
                mcc_name:   merchant.mcc_category || 'N/A',
                confidence: merchant.mcc_confidence || 0,
                evidence:   []
              },
              secondary_mcc: merchant.mcc_secondary ? {
                mcc_code:   merchant.mcc_secondary,
                mcc_name:   merchant.mcc_secondary_category || 'N/A',
                confidence: merchant.mcc_secondary_confidence || 0,
                evidence:   []
              } : null,
              tertiary_mcc: merchant.mcc_tertiary ? {
                mcc_code:   merchant.mcc_tertiary,
                mcc_name:   merchant.mcc_tertiary_category || 'N/A',
                confidence: merchant.mcc_tertiary_confidence || 0,
                evidence:   []
              } : null,
            };

            setMccResults(formattedResults);
            fetchMerchantDetailFromApi(merchantUrl).then((detail) => {
              if (detail) setMerchantDetail(detail);
            });
            setMccProgress({
              status: 'completed',
              processed: merchantsToCheck.length,
              total: merchantsToCheck.length,
              progress_percentage: 100,
              message: `MCC ${merchant.mcc_primary} – ${merchant.mcc_category || ''}`,
              primary_mcc: formattedResults.primary_mcc
            });
            setParsingProgress(prev => prev ? { ...prev, status: 'completed', progress_percentage: 100 } : {
              status: 'completed', progress_percentage: 100, parsed_pages: 1, total_pages: 1, error_count: 0, current_page: ''
            });
            setCrawlProgress(prev => ({
              ...prev,
              status: 'completed',
              message: '✅ Pipeline complete!'
            }));
            setIsClassifying(false);
            setIsParsing(false);
            setIsCrawling(false);

            // Mark this merchant as fully classified in the per-URL step tracker
            setPerUrlStatus(prev => {
              const existing = prev[merchantUrl] || { steps: [] };
              const finalConf = merchant.mcc_confidence != null ? ` (${(merchant.mcc_confidence * 100).toFixed(0)}%)` : '';
              const timing = merchant.fetch_time_ms || merchant.processing_time_ms
                ? ` · ${((( merchant.fetch_time_ms || 0) + (merchant.parse_time_ms || 0) + (merchant.processing_time_ms || 0)) / 1000).toFixed(1)}s`
                : '';
              const steps = [...existing.steps];
              const ensureStep = (id, label, status, detail) => {
                const idx = steps.findIndex(s => s.id === id);
                const entry = { id, label, status, detail, time: new Date().toLocaleTimeString() };
                if (idx >= 0) steps[idx] = entry; else steps.push(entry);
              };
              ensureStep('fetch', '🌐 Website fetched', 'done', `${merchant.fetch_time_ms ? (merchant.fetch_time_ms/1000).toFixed(1)+'s' : ''}`);
              ensureStep('parse', '📝 Content parsed', 'done', `${merchant.parse_time_ms ? (merchant.parse_time_ms/1000).toFixed(1)+'s' : ''}`);
              ensureStep('classify', `✅ MCC ${merchant.mcc_primary} – ${merchant.mcc_category || 'N/A'}`, 'done', `Confidence${finalConf}${timing}`);
              return { ...prev, [merchantUrl]: { steps, mcc: merchant.mcc_primary, category: merchant.mcc_category, confidence: merchant.mcc_confidence } };
            });

            showToast(
              'success', '✅ MCC Report Ready!',
              `${merchant.merchant_name || merchantUrl}\n` +
              `MCC: ${merchant.mcc_primary} – ${merchant.mcc_category || 'N/A'}\n` +
              `Confidence: ${confidence}%` +
              (totalMs > 0 ? `\nTime: ${(totalMs / 1000).toFixed(1)}s` : ''),
              { label: 'View in Reports →', onClick: () => navigate('/reports') }
            );

            console.log(`✅ Hybrid MCC complete: ${merchantUrl} → ${merchant.mcc_primary}`);
          }

          // Stop polling when every submitted merchant has a real MCC code
          if (hybridCompletedCount >= merchantsToCheck.length) {
            stopParsingProgressPolling();
            stopMCCProgressPolling();
            console.log('🏁 All merchants MCC-classified. Polling stopped.');
          }
        } catch (hybridErr) {
          console.log('⚠️ Hybrid orchestrator check failed:', hybridErr);
        }
      }

      // Parsers-module "all completed" path (fallback — fires when parsers itself marks done)
      if (allCompleted || (completedCount + failedCount === relevantProgress.length && relevantProgress.length > 0)) {
        console.log(`✅ Parsers pipeline completed: ${completedCount} successful, ${failedCount} failed`);
        setIsParsing(false);
        setIsClassifying(false);
        setIsCrawling(false);
        stopParsingProgressPolling();
      }
    } catch (error) {
      console.error('❌ Failed to fetch parsing progress:', error);
    }
  };

  // Fetch failure details for URLs that failed
  const fetchFailureDetails = async (merchantURL) => {
    try {
      const cleanURL = merchantURL.replace(/^www\./, '');
      const response = await fetch(`${API_BASE}/api/merchants/failures?merchant_url=${encodeURIComponent(cleanURL)}`);
      
      if (response.ok) {
        const data = await response.json();
        setFailureDetails(data);
      } else {
        console.error('Failed to fetch failure details:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching failure details:', error);
    }
  };

  // Enhanced progress polling with better real-time monitoring
  // merchantURLorURLs: single URL string OR array of URLs (bypasses stale processingMerchants closure)
  const startParsingProgressPolling = (merchantURLorURLs = null) => {
    // Normalise to an explicit array (or null to fall back to processingMerchants state)
    const explicitURLs = merchantURLorURLs
      ? (Array.isArray(merchantURLorURLs) ? merchantURLorURLs : [merchantURLorURLs])
      : null;

    // If a single string URL was passed (legacy callers), also register it in processingMerchants
    if (merchantURLorURLs && typeof merchantURLorURLs === 'string') {
      setProcessingMerchants(prev =>
        prev.includes(merchantURLorURLs) ? prev : [...prev, merchantURLorURLs]
      );
    }

    // Clear any existing interval
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    // Initial fetch — pass explicit URLs to avoid stale-state closure
    fetchParsingProgress(null, explicitURLs);

    // Set up polling every 2 seconds for faster updates, with backoff for long-running tasks
    let pollCount = 0;
    const interval = setInterval(() => {
      pollCount++;
      fetchParsingProgress(null, explicitURLs);
      
      // After 60 polls (2 minutes), slow down to every 5 seconds to reduce server load
      if (pollCount === 60) {
        clearInterval(interval);
        const slowInterval = setInterval(() => fetchParsingProgress(null, explicitURLs), 5000);
        setPollingInterval(slowInterval);
      }
    }, 2000);
    setPollingInterval(interval);
  };

  // Stop polling
  const stopParsingProgressPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  // Fetch MCC classification results with timing and status
  const fetchMCCResults = async (merchantURL = null) => {
    const merchantsToCheck = merchantURL ? [merchantURL] : processingMerchants;
    
    if (merchantsToCheck.length === 0) return;

    try {
      for (const url of merchantsToCheck) {
        // Fetch from hybrid orchestrator to get timing data
        const response = await fetch(`/api/hybrid/merchants?include_unclassified=true&merchant_url=${encodeURIComponent(url)}&limit=1`, {
          headers: getHybridHeaders(),
        });
        if (!response.ok) continue;
        
        const data = await response.json();
        const normalizeUrl = (u = '') =>
          u.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
        const merchant = data.merchants?.find(m => normalizeUrl(m.merchant_url) === normalizeUrl(url));
        
        if (merchant) {
          console.log('📊 Merchant data received:', merchant);
          
          // Update timing information
          setStageTiming(prev => ({
            ...prev,
            [url]: {
              stage1_time_ms: merchant.stage1_time_ms || 0,
              stage2_time_ms: merchant.stage2_time_ms || 0,
              stage3_time_ms: merchant.stage3_time_ms || 0,
              total_time_ms: merchant.processing_time_ms || 0,
              fetch_time_ms: merchant.fetch_time_ms || 0,
              parse_time_ms: merchant.parse_time_ms || 0
            }
          }));
          
          // Update backend status
          setBackendStatus(prev => ({
            ...prev,
            [url]: {
              mcc_processed: !!merchant.mcc_primary,
              mcc_code: merchant.mcc_primary,
              mcc_name: merchant.mcc_category,
              confidence: merchant.mcc_confidence,
              error: merchant.error_message,
              status: merchant.status || (merchant.mcc_primary ? 'completed' : (merchant.error_message ? 'failed' : 'processing'))
            }
          }));
          
          if (merchant.mcc_primary) {
            console.log('✅ MCC classification found:', merchant.mcc_primary);
            setMccResults({
              mcc_code: merchant.mcc_primary,
              category: merchant.mcc_category,
              confidence: merchant.mcc_confidence,
              primary_mcc: {
                mcc_code: merchant.mcc_primary,
                mcc_name: merchant.mcc_category,
                confidence: merchant.mcc_confidence
              }
            });
            setMccProgress({ status: 'completed', primary_mcc: { mcc_code: merchant.mcc_primary, mcc_name: merchant.mcc_category } });
            setIsClassifying(false);
            stopMCCProgressPolling();

            addAuditLog({
              scope: 'single',
              merchantName: merchant.merchant_name || '',
              url,
              stage: 'mcc',
              status: 'success',
              reason: 'MCC classified',
              details: `${merchant.mcc_primary || 'N/A'} - ${merchant.mcc_category || 'N/A'}`,
              dedupeKey: `single|mcc|success|${url}|${merchant.mcc_primary || ''}`
            });
          } else if (merchant.mistral_processed === false) {
            console.log('⚠️ Merchant parsed but MCC not triggered');
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch MCC results:', error);
    }
  };

  // Start MCC results polling
  const startMCCProgressPolling = (merchantURL = null) => {
    // Clear any existing interval
    if (mccPollingInterval) {
      clearInterval(mccPollingInterval);
    }

    // Initial fetch
    fetchMCCResults(merchantURL);

    // Set up polling every 3 seconds
    const interval = setInterval(() => fetchMCCResults(merchantURL), 3000);
    setMccPollingInterval(interval);
  };

  // Stop MCC polling
  const stopMCCProgressPolling = () => {
    if (mccPollingInterval) {
      clearInterval(mccPollingInterval);
      setMccPollingInterval(null);
    }
  };

  // Multi-URL management
  const addUrlField = () => {
    if (urls.length < 10) {
      setUrls([...urls, { merchantName: "", url: "", currentMcc: "" }]);
    }
  };

  const removeUrlField = (index) => {
    if (urls.length > 1) {
      setUrls(urls.filter((_, i) => i !== index));
    }
  };

  const updateUrlField = (index, field, value) => {
    const newUrls = [...urls];
    newUrls[index][field] = value;
    setUrls(newUrls);
  };

  // Bulk CSV processing
  const handleCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setCsvFile(file);
      
      // Parse CSV immediately to show URLs for editing
      try {
        const text = await file.text();
        const data = parseCsv(text);
        setBulkUrlList(data);
        setShowBulkUrlEditor(true);
      } catch (error) {
        console.error("Failed to parse CSV:", error);
        showToast('error', 'CSV Parse Error', 'Failed to parse the CSV file. Please check the format and try again.');
      }
    }
  };
  
  const updateBulkUrl = (index, field, value) => {
    const newList = [...bulkUrlList];
    newList[index][field] = value;
    setBulkUrlList(newList);
  };

  const normalizeBulkUrlAtIndex = (index) => {
    const item = bulkUrlList[index];
    if (!item || !item.url) return;

    const normalized = normalizeUrlForProcessing(item.url);
    if (normalized.ok && normalized.url !== item.url) {
      updateBulkUrl(index, 'url', normalized.url);
    }
  };
  
  const removeBulkUrl = (index) => {
    setBulkUrlList(bulkUrlList.filter((_, i) => i !== index));
  };

  const parseCsv = (text) => {
    const lines = text.trim().split('\n');
    const data = [];
    
    for (let i = 1; i < lines.length; i++) { // Skip header
      const line = lines[i].trim();
      if (!line) continue;
      
      const [merchantName, url, bankMcc] = line.split(',').map(s => s.trim().replace(/^["']|["']$/g, ''));
      if (url) {
        const normalized = normalizeUrlForProcessing(url);
        if (normalized.ok && normalized.url) {
          data.push({
            merchantName: merchantName || '',
            url: normalized.url,
            currentMcc: (bankMcc || '').replace(/\D/g, '').slice(0, 4),
          });
        }
      }
    }
    
    return data;
  };

  const createBatches = (data, batchSize) => {
    const batches = [];
    for (let i = 0; i < data.length; i += batchSize) {
      batches.push({
        id: Math.floor(i / batchSize) + 1,
        urls: data.slice(i, i + batchSize),
        status: 'pending',
        progress: 0,
        completed: 0,
        failed: 0,
        retries: 0
      });
    }
    return batches;
  };

  const processBulkUpload = async () => {
    if (bulkUrlList.length === 0) {
      showToast('warning', 'No URLs', 'Please upload a CSV file with URLs first.');
      return;
    }

    setIsBulkProcessing(true);
    setFailedUrls([]);
    setRetryQueue([]);
    setBulkDetailedProgress({});
    setShowBulkUrlEditor(false);

    try {
      const data = bulkUrlList.reduce((acc, item) => {
        const normalized = normalizeUrlForProcessing(item.url);
        if (normalized.ok && normalized.url) {
          acc.push({ ...item, url: normalized.url });
        }
        return acc;
      }, []);
      
      if (data.length === 0) {
        showToast('warning', 'No Valid URLs', 'No valid URLs were found in the CSV file.');
        setIsBulkProcessing(false);
        return;
      }

      try {
        await assertPocQuotaAllows(data.length);
      } catch (quotaErr) {
        showToast('error', 'POC quota', quotaErr?.message || 'Merchant quota exceeded');
        setIsBulkProcessing(false);
        return;
      }

      try {
        await assertDemoScanAllowed(data.map((d) => d.url));
      } catch (gateErr) {
        showToast('error', 'Scan blocked', gateErr?.message || 'Scan not allowed for Demo partner');
        setIsBulkProcessing(false);
        return;
      }

      setBulkUrlList(data);

      data.forEach(item => {
        addAuditLog({
          scope: 'bulk<=500',
          merchantName: item.merchantName || '',
          url: item.url,
          stage: 'pipeline',
          status: 'processing',
          reason: 'Queued in CSV bulk run',
          details: 'Submitted to bulk scan queue',
          dedupeKey: `bulk<=500|queued|${item.url}`
        });
      });

      const totalUrls = data.length;
      console.log(`[BULK] Starting parallel upload of ${totalUrls} URLs`);

      // Resolve server IP locations at the start of bulk scan (async store to DB)
      resolveUrlLocationsAtScanStart(data.map((item) => item.url));

      // Initialize detailed progress for all URLs
      const initialProgress = {};
      data.forEach((item, index) => {
        initialProgress[item.url] = {
          status: 'submitting',
          stage: 'queue',
          progress: 0,
          batchId: 0, // Will be updated after submission
          discovered_urls: 0,
          parsed_pages: 0,
          total_pages: 0,
          mcc_code: null
        };
      });
      setBulkDetailedProgress(initialProgress);

      // Step 1: Submit ALL URLs to parsers module bulk_scan endpoint
      // The Go parsers module handles batching and parallel processing
      const submitResponse = await fetch(`${API_BASE}/api/hybrid/classify-openai`, {
        method: "POST",
        headers: getHybridHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ 
          merchants: data.map(item => ({
            merchant_name: item.merchantName,
            url: item.url,
            bank_provided_mcc: item.currentMcc || '',
          }))
        }),
        signal: AbortSignal.timeout(3600000) // 1 hour timeout for OpenAI batch
      });

      if (!submitResponse.ok) {
        throw new Error(`Bulk submission failed: ${submitResponse.statusText}`);
      }

      const submitResult = await submitResponse.json();
      console.log(`[BULK] Submission result:`, submitResult);

      // Update batch info from server response
      const numBatches = submitResult.num_batches || 10;
      const batchSize = submitResult.batch_size || Math.ceil(totalUrls / 10);
      
      // Add all submitted URLs to processingMerchants for progress tracking
      const submittedUrls = data.map(item => {
        return item.url.replace('http://', 'https://');
      });
      setProcessingMerchants(prev => [...new Set([...prev, ...submittedUrls])]);
      console.log(`[BULK] Monitoring progress for ${submittedUrls.length} merchants`);
      
      // Create batch display cards
      const batches = [];
      for (let i = 0; i < numBatches; i++) {
        batches.push({
          id: i + 1,
          urls: data.slice(i * batchSize, (i + 1) * batchSize),
          status: 'processing',
          progress: 0,
          completed: 0,
          failed: 0,
          retries: 0
        });
      }
      setBulkBatches(batches);

      // Update detailed progress for created merchants
      if (submitResult.created) {
        submitResult.created.forEach(m => {
          if (bulkDetailedProgress[m.url]) {
            setBulkDetailedProgress(prev => ({
              ...prev,
              [m.url]: { 
                ...prev[m.url], 
                status: 'queued', 
                stage: 'fetch',
                progress: 10,
                merchantId: m.id,
                batchId: m.batch
              }
            }));
          }
        });
      }

      // Mark rejected URLs
      if (submitResult.rejected) {
        submitResult.rejected.forEach(r => {
          setBulkDetailedProgress(prev => ({
            ...prev,
            [r.original_url]: { 
              ...prev[r.original_url], 
              status: 'failed', 
              stage: 'error',
              progress: 0,
              error: r.error
            }
          }));
          setFailedUrls(prev => [...prev, { 
            url: r.original_url, 
            merchantName: r.name,
            reason: r.error,
            retryCount: 0
          }]);

          addAuditLog({
            scope: 'bulk<=500',
            merchantName: r.name || '',
            url: r.original_url,
            stage: 'validation',
            status: 'failed',
            reason: 'Rejected before processing',
            details: r.error || '',
            dedupeKey: `bulk<=500|rejected|${r.original_url}|${r.error || ''}`
          });
        });
      }

      // Step 2: Normalize URL helper (handles protocol/www/trailing-slash mismatches)
      const normalizeUrl = (u) => (u || '').toLowerCase()
        .replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');

      // Better totalCreated fallback — backend may use different field names
      const totalCreated = submitResult.total_created
        || submitResult.total_accepted
        || (submitResult.created || []).length
        || data.length;

      // Immediately confirm enqueue — don't wait until the loop to give feedback
      showToast('info', '📤 Batch Submitted!',
        `${totalCreated} merchant${totalCreated !== 1 ? 's' : ''} queued for processing.\n` +
        `Real-time status updates below. A notification fires as each merchant's MCC classification completes.`,
        { label: 'View Reports →', onClick: () => navigate('/reports') }
      );

      console.log(`[BULK] Monitoring ${totalCreated} merchants`);

      const mccCompletedUrls = new Set(); // URLs that got a real MCC code from hybrid orchestrator
      const notifiedUrls    = new Set(); // URLs that already received a completion toast
      let pollAttempts = 0;
      const maxPollAttempts = 400; // ~20 min at 3s intervals

      while (pollAttempts < maxPollAttempts) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        pollAttempts++;

        try {
          // ── Source 1: parsers module — tracks fetch + parse stages ──
          const progressResponse = await fetch(`${API_BASE}/api/merchants/progress`);
          if (progressResponse.ok) {
            const progressData = await progressResponse.json();
            if (Array.isArray(progressData)) {
              const batchCounts = {};

              progressData.forEach((progress) => {
                // Normalize the backend URL before matching
                const progressNorm = normalizeUrl(progress.url);
                const dataIndex = data.findIndex(item => normalizeUrl(item.url) === progressNorm);
                if (dataIndex === -1) return; // Not our batch

                const itemUrl = data[dataIndex].url; // Use ORIGINAL submitted URL as key
                const stages  = progress.stages || {};
                const batchId = Math.floor(dataIndex / batchSize) + 1;

                if (!batchCounts[batchId]) batchCounts[batchId] = { completed: 0, failed: 0, total: 0 };
                batchCounts[batchId].total++;

                // Determine the active pipeline stage
                let currentStage = 'queue';
                if      (stages.mcc?.status  === 'running')   currentStage = 'mcc';
                else if (stages.parse?.status === 'running')  currentStage = 'parse';
                else if (stages.fetch?.status === 'running')  currentStage = 'fetch';
                else if (stages.mcc?.status  === 'completed') currentStage = 'complete';

                if (progress.status === 'completed' || stages.mcc?.status === 'completed') {
                  batchCounts[batchId].completed++;
                } else if (progress.status === 'error') {
                  batchCounts[batchId].failed++;

                  addAuditLog({
                    scope: 'bulk<=500',
                    merchantName: data[dataIndex]?.merchantName || '',
                    url: itemUrl,
                    stage: currentStage,
                    status: 'failed',
                    reason: 'Processing error',
                    details: progress.error_message || 'Unknown backend error',
                    dedupeKey: `bulk<=500|processing|failed|${itemUrl}|${progress.error_message || ''}`
                  });
                }

                const stageProgressPct = progress.progress_percentage
                  || (currentStage === 'complete' ? 100
                    : currentStage === 'mcc'  ? 75
                    : currentStage === 'parse' ? 50
                    : currentStage === 'fetch' ? 25 : 10);

                setBulkDetailedProgress(prev => ({
                  ...prev,
                  [itemUrl]: {
                    ...prev[itemUrl],
                    batchId,
                    status: progress.status === 'error' ? 'failed'
                      : currentStage === 'complete' ? 'completed'
                      : currentStage === 'mcc'    ? 'classifying'
                      : currentStage === 'parse'  ? 'parsing'
                      : currentStage === 'fetch'  ? 'fetching' : 'queued',
                    stage: currentStage,
                    progress: stageProgressPct,
                    message: progress.message || '',
                    error:   progress.error_message || '',
                    fetch_status: stages.fetch?.status || 'pending',
                    parse_status: stages.parse?.status || 'pending',
                    mcc_status:   stages.mcc?.status   || 'pending',
                    fetch_time: stages.fetch?.duration || 0,
                    parse_time: stages.parse?.duration || 0,
                    mcc_time:   stages.mcc?.duration   || 0,
                    total_time:    progress.total_duration || 0,
                    elapsed_time:  progress.elapsed_time   || 0,
                    discovered_urls: progress.discovered_urls || 0,
                    parsed_pages:    progress.parsed_pages    || 0,
                    total_pages:     progress.total_pages     || 0,
                  }
                }));
              });

              setBulkBatches(prev => prev.map(batch => {
                const s   = batchCounts[batch.id] || { completed: 0, failed: 0, total: 0 };
                const pct = s.total > 0 ? Math.round(((s.completed + s.failed) / s.total) * 100) : 0;
                return { ...batch, progress: pct, completed: s.completed, failed: s.failed,
                  status: pct >= 100 ? 'completed' : 'processing' };
              }));
            }
          }

          // ── Source 2: hybrid orchestrator — tracks MCC classification ──
          // The parsers module NEVER marks MCC done; only hybrid orchestrator knows.
          try {
            const hybridResponse = await fetch(`${API_BASE}/api/hybrid/merchants?include_unclassified=true&limit=2000`, {
              headers: getHybridHeaders(),
            });
            if (hybridResponse.ok) {
              const hybridData = await hybridResponse.json();
              (hybridData.merchants || []).forEach(merchant => {
                // Only consider merchants with a real MCC code
                if (!merchant.mcc_primary || String(merchant.mcc_primary).trim() === '') return;

                const mNorm     = normalizeUrl(merchant.merchant_url);
                const dataIndex = data.findIndex(item => normalizeUrl(item.url) === mNorm);
                if (dataIndex === -1) return;

                const itemUrl = data[dataIndex].url;
                if (notifiedUrls.has(itemUrl)) return; // already notified
                notifiedUrls.add(itemUrl);
                mccCompletedUrls.add(itemUrl);

                const totalMs    = (merchant.fetch_time_ms || 0) + (merchant.parse_time_ms || 0) + (merchant.processing_time_ms || 0);
                const confidence = merchant.mcc_confidence ? (merchant.mcc_confidence * 100).toFixed(1) : 'N/A';

                // Update per-row to "completed" + fill in MCC fields
                setBulkDetailedProgress(prev => ({
                  ...prev,
                  [itemUrl]: {
                    ...prev[itemUrl],
                    status: 'completed', stage: 'complete', progress: 100,
                    mcc_status: 'completed',
                    mcc_code:   merchant.mcc_primary,
                    mcc_name:   merchant.mcc_category || '',
                    confidence: merchant.mcc_confidence ? merchant.mcc_confidence * 100 : null,
                    total_time: (totalMs / 1000).toFixed(1),
                  }
                }));

                // Per-merchant success toast (linked to real backend data)
                showToast(
                  'success', '✅ MCC Report Ready!',
                  `${data[dataIndex].merchantName || itemUrl}\n` +
                  `MCC: ${merchant.mcc_primary} – ${merchant.mcc_category || 'N/A'}\n` +
                  `Confidence: ${confidence}%` +
                  (totalMs > 0 ? `\nTime: ${(totalMs / 1000).toFixed(1)}s` : ''),
                  { label: 'View in Reports →', onClick: () => navigate('/reports') }
                );

                addAuditLog({
                  scope: 'bulk<=500',
                  merchantName: data[dataIndex].merchantName || '',
                  url: itemUrl,
                  stage: 'mcc',
                  status: 'success',
                  reason: 'MCC classified',
                  details: `${merchant.mcc_primary || 'N/A'} - ${merchant.mcc_category || 'N/A'}`,
                  dedupeKey: `bulk<=500|mcc|success|${itemUrl}|${merchant.mcc_primary || ''}`
                });
              });

              // Exit early when all submitted merchants have MCC done
              if (totalCreated > 0 && mccCompletedUrls.size >= totalCreated) {
                console.log('[BULK] All merchants MCC-classified, stopping poll');
                break;
              }
            }
          } catch (hybridErr) {
            // Hybrid not reachable — keep polling parsers only
          }

          console.log(`[BULK] Poll ${pollAttempts}: ${mccCompletedUrls.size}/${totalCreated} MCC done`);
        } catch (e) {
          console.log('[BULK] Progress poll failed:', e);
        }
      }

      setIsBulkProcessing(false);

      // Final toast — only for the timeout case (per-merchant toasts already fired above)
      if (pollAttempts >= maxPollAttempts) {
        showToast('info', '⏱ Still Processing in Background',
          `${mccCompletedUrls.size} of ${totalCreated} merchants completed MCC classification.\n` +
          `Remaining merchants continue in background — check Reports for completed results.`,
          { label: 'Check Reports →', onClick: () => navigate('/reports') }
        );
      }

    } catch (error) {
      console.error("[BULK] Processing failed:", error);
      showToast('error', 'Bulk Processing Error', `Failed to process bulk upload: ${error.message}`);
      setIsBulkProcessing(false);

      addAuditLog({
        scope: 'bulk<=500',
        merchantName: '',
        url: '',
        stage: 'pipeline',
        status: 'failed',
        reason: 'Bulk run failed',
        details: error.message || 'Unknown error',
        dedupeKey: `bulk<=500|pipeline|failed|${error.message || 'unknown'}`
      });
    }
  };

  // ── Vertex AI (Gemini) handlers ───────────────────────────────────────────

  const downloadSampleBulkCsv = () => {
    const blob = new Blob([SAMPLE_BULK_CSV], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "sample_mcc_bulk_upload.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleVertexCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setVertexCsvFile(file);
    setVertexResults(null);
    setVertexError(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const lines = ev.target.result.split('\n');
      const data = [];
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        if (i === 0 && /merchant/i.test(line) && /url/i.test(line)) continue;

        const [merchantName, url, bankMcc] = line.split(',').map(s => s.trim().replace(/^["']|["']$/g, ''));
        if (url) {
          const normalized = normalizeUrlForProcessing(url);
          if (normalized.ok && normalized.url) {
            data.push({ merchant_name: merchantName || '', url: normalized.url, bank_provided_mcc: bankMcc || '' });
          }
        }
      }
      setVertexUrlList(data);
    };
    reader.readAsText(file);
  };

  const processVertexSingle = async () => {
    const gated = await validateMerchantsForCrawl(
      [{ url: vertexSingleUrl, merchant_name: '' }],
      { pipelineLabel: 'Fallback Single' }
    );
    if (!gated.ok) return;
    const normalizedUrl = gated.merchants[0].url;

    setIsVertexProcessing(true);
    setVertexResults(null);
    setVertexError(null);

    try {
      try {
        await assertDemoScanAllowed([normalizedUrl]);
      } catch (gateErr) {
        showToast('error', 'Scan blocked', gateErr?.message || 'Scan not allowed for Demo partner');
        setIsVertexProcessing(false);
        return;
      }

      const response = await fetch(`${API_BASE}/api/hybrid/classify-vertex`, {
        method: 'POST',
        headers: getHybridHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ merchant_url: normalizedUrl }),
        signal: AbortSignal.timeout(300000), // 5 min timeout for single URL
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Server error ${response.status}: ${errText}`);
      }

      const result = await response.json();
      setVertexResults(result);

      addAuditLog({
        scope: 'vertex',
        merchantName: '',
        url: normalizedUrl,
        stage: 'pipeline',
        status: result.status === 'success' ? 'success' : 'failed',
        reason: 'Single merchant classification',
        details: result.status === 'success'
          ? formatMccAuditResult(result.primary_mcc_code, result.primary_mcc_category, result.confidence)
          : (result.message || result.error || 'Classification failed'),
        dedupeKey: `vertex|single|${normalizedUrl}|${result.status}`
      });
    } catch (err) {
      // Vertex AI failed — automatically fall back to OpenAI
      console.warn('⚠️ Vertex AI single failed, trying OpenAI fallback:', err.message);
      try {
        const fallbackRes = await fetch(`${API_BASE}/api/hybrid/classify-openai`, {
          method: 'POST',
          headers: getHybridHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ merchant_url: normalizedUrl }),
          signal: AbortSignal.timeout(300000),
        });
        if (!fallbackRes.ok) throw new Error(`OpenAI fallback error ${fallbackRes.status}`);
        const fallbackResult = await fallbackRes.json();
        setVertexResults({ ...fallbackResult, _engine: 'openai-fallback' });
        addAuditLog({
          scope: 'vertex',
          merchantName: '',
          url: normalizedUrl,
          stage: 'pipeline',
          status: 'success',
          reason: 'OpenAI fallback succeeded',
          details: formatMccAuditResult(fallbackResult.primary_mcc_code, fallbackResult.primary_mcc_category, fallbackResult.confidence),
          dedupeKey: `vertex|single|openai-fallback|${normalizedUrl}`
        });
      } catch (fallbackErr) {
        setVertexError(err.message);
        addAuditLog({
          scope: 'vertex',
          merchantName: '',
          url: normalizedUrl,
          stage: 'pipeline',
          status: 'failed',
          reason: 'Vertex AI + OpenAI fallback both failed',
          details: fallbackErr.message || 'Unknown error',
          dedupeKey: `vertex|single|failed|${err.message || 'unknown'}`
        });
      }
    } finally {
      setIsVertexProcessing(false);
    }
  };

  const processVertexBatch = async () => {
    const gated = await validateMerchantsForCrawl(vertexUrlList, { pipelineLabel: 'Fallback Bulk' });
    if (!gated.ok) return;
    const data = gated.merchants.map((item) => ({
      ...item,
      merchant_name: item.merchant_name || item.merchantName || '',
      url: item.url,
      bank_provided_mcc: item.bank_provided_mcc || item.currentMcc || '',
    }));

    if (data.length > 10000) {
      showToast('warning', 'Too Many URLs', 'Maximum 10,000 URLs allowed per Fallback batch.');
      return;
    }

    try {
      await assertDemoScanAllowed(data.map((d) => d.url));
    } catch (gateErr) {
      showToast('error', 'Scan blocked', gateErr?.message || 'Scan not allowed for Demo partner');
      return;
    }

    setVertexUrlList(data);
    setIsVertexProcessing(true);
    setVertexResults(null);
    setVertexError(null);
    setVertexLiveProgress({
      running: true,
      total: data.length,
      success: 0,
      failed: 0,
      fetching: 0,
      stage1: 0,
      stage2: 0,
      batch_results: [],
    });

    try {
    showToast('info', 'Core Crawler submitted',
      `${data.length} URL${data.length !== 1 ? 's' : ''} split into 10 parallel batches. Fetch/parse runs ahead of Stage 1 and Stage 2.`);

    let backendDone = false;
    let backendResult = null;
    let backendError = null;

    const backendPromise = (async () => {
      try {
        const response = await fetch(`${API_BASE}/api/hybrid/classify-vertex`, {
          method: 'POST',
          headers: getHybridHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ merchants: data }),
          signal: AbortSignal.timeout(3600000),
        });
        if (!response.ok) {
          const errText = await response.text();
          throw new Error(`Server error ${response.status}: ${errText}`);
        }
        backendResult = await response.json();
      } catch (err) {
        backendError = err.message;
      } finally {
        backendDone = true;
      }
    })();

    while (!backendDone) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      try {
        const progRes = await fetch(`${API_BASE}/api/hybrid/classify-vertex-progress`, {
          headers: getHybridHeaders(),
        });
        if (progRes.ok) {
          const prog = await progRes.json();
          setVertexLiveProgress(prog);
        }
      } catch (e) {
        console.warn('[Core Crawler] progress poll failed', e);
      }
    }

    await backendPromise;

    if (backendError) {
      console.warn('Core Crawler batch failed, trying OpenAI fallback:', backendError);
      try {
        const fallbackRes = await fetch(`${API_BASE}/api/hybrid/classify-openai`, {
          method: 'POST',
          headers: getHybridHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ merchants: data }),
          signal: AbortSignal.timeout(3600000),
        });
        if (!fallbackRes.ok) throw new Error(`OpenAI fallback error ${fallbackRes.status}`);
        const fallbackResult = await fallbackRes.json();
        setVertexResults({ ...fallbackResult, _engine: 'openai-fallback' });
        addAuditLog({
          scope: 'vertex',
          merchantName: '',
          url: '',
          stage: 'pipeline',
          status: 'success',
          reason: 'OpenAI fallback batch succeeded',
          details: `Vertex AI unavailable — ${fallbackResult.total_success || 0} classified via OpenAI fallback`,
          dedupeKey: `vertex|batch|openai-fallback|success`
        });
      } catch (fallbackErr) {
        setVertexError(backendError);
        addAuditLog({
          scope: 'vertex',
          merchantName: '',
          url: '',
          stage: 'pipeline',
          status: 'failed',
          reason: 'Core Crawler batch failed (Vertex + OpenAI fallback)',
          details: fallbackErr.message || 'Unknown error',
          dedupeKey: `vertex|batch|failed|${backendError}`
        });
      }
    } else if (backendResult) {
      setVertexResults(backendResult);
      setVertexLiveProgress({
        ...backendResult,
        running: false,
        total: backendResult.total_merchants,
        success: backendResult.total_success,
        failed: backendResult.total_failed,
      });
      const failedUrls = backendResult.failed_urls || [];
      const failedSet = new Set(failedUrls);
      for (const item of data) {
        const ok = !failedSet.has(item.url);
        addAuditLog({
          scope: 'fallback',
          merchantName: item.merchant_name || '',
          url: item.url,
          stage: 'pipeline',
          status: ok ? 'success' : 'failed',
          reason: ok ? 'Fallback bulk completed' : 'Fallback bulk failed',
          details: ok ? 'Classified' : 'Failed in Fallback bulk batch',
          dedupeKey: `fallback|bulk|${ok ? 'ok' : 'fail'}|${item.url}`,
        });
        if (ok) {
          recordScanBilling({
            merchant_url: item.url,
            merchant_name: item.merchant_name || '',
            upload_type: 'bulk',
          });
        }
      }
      if (failedUrls.length) {
        const failedItems = data.filter((d) => failedSet.has(d.url));
        showToast('info', 'Primary crawler', `Running ${failedItems.length} failed URL(s) on Primary crawler…`);
        await processBdBatchWith(failedItems, { enableFailover: false });
      }
    }

    } catch (err) {
      setVertexError(err.message || 'Core Crawler batch failed');
      addAuditLog({
        scope: 'vertex',
        merchantName: '',
        url: '',
        stage: 'pipeline',
        status: 'failed',
        reason: 'Core Crawler batch failed',
        details: err.message || 'Unknown error',
        dedupeKey: `vertex|batch|failed|${err.message || 'unknown'}`
      });
    } finally {
      setIsVertexProcessing(false);
    }
  };

  const handleBdCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBdCsvFile(file);
    setBdResults(null);
    setBdError(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const lines = ev.target.result.split('\n');
      const data = [];
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        if (i === 0 && /merchant/i.test(line) && /url/i.test(line)) continue;
        const [merchantName, url, bankMcc] = line.split(',').map((s) => s.trim().replace(/^["']|["']$/g, ''));
        if (url) {
          const normalized = normalizeUrlForProcessing(url);
          if (normalized.ok && normalized.url) {
            data.push({
              merchant_name: merchantName || '',
              url: normalized.url,
              bank_provided_mcc: (bankMcc || '').replace(/\D/g, '').slice(0, 4),
            });
          }
        }
      }
      setBdUrlList(data.slice(0, 10000));
      if (data.length > 10000) {
        showToast('warning', 'URL limit', 'Only the first 10,000 URLs were loaded.');
      }
    };
    reader.readAsText(file);
  };

  const addBdManualRow = () => {
    if (bdManualRows.length < 10) {
      setBdManualRows([...bdManualRows, { merchantName: '', url: '', currentMcc: '' }]);
    }
  };

  const removeBdManualRow = (index) => {
    if (bdManualRows.length > 1) {
      setBdManualRows(bdManualRows.filter((_, i) => i !== index));
    }
  };

  const updateBdManualRow = (index, field, value) => {
    const next = [...bdManualRows];
    next[index] = { ...next[index], [field]: value };
    setBdManualRows(next);
  };

  const collectBdManualMerchants = () => {
    const data = [];
    for (const row of bdManualRows) {
      const normalized = normalizeUrlForProcessing(row.url);
      if (normalized.ok && normalized.url) {
        data.push({
          merchant_name: (row.merchantName || '').trim(),
          url: normalized.url,
          bank_provided_mcc: (row.currentMcc || '').replace(/\D/g, '').slice(0, 4),
        });
      }
    }
    return data;
  };

  const normalizeMerchantBatch = (incoming) =>
    (incoming || []).reduce((acc, item) => {
      const normalized = normalizeUrlForProcessing(item.url || item.merchant_url);
      if (normalized.ok && normalized.url) {
        acc.push({
          merchant_name: item.merchant_name || item.merchantName || '',
          url: normalized.url,
          bank_provided_mcc: String(
            item.bank_provided_mcc || item.currentMcc || item.bank_mcc || ''
          )
            .replace(/\D/g, '')
            .slice(0, 4),
        });
      }
      return acc;
    }, []);

  const pollPrimaryJob = async (jobId) => {
    let done = false;
    let lastProg = null;
    let missingPolls = 0;
    while (!done) {
      await new Promise((r) => setTimeout(r, 3000));
      const progRes = await fetch(
        `${API_BASE}/api/v1/brightdata-google-ai/progress/?job_id=${encodeURIComponent(jobId)}`,
        { headers: getHybridHeaders() }
      );
      const prog = await progRes.json().catch(() => ({}));
      if (progRes.status === 404) {
        missingPolls += 1;
        if (missingPolls >= 8) {
          throw new Error(prog.message || 'Primary crawler batch job not found');
        }
        continue;
      }
      if (!progRes.ok) continue;
      lastProg = prog;
      setBdLiveProgress(prog);
      if (prog.status === 'completed' || prog.status === 'failed') {
        done = true;
        setBdResults(prog);
      }
    }
    return lastProg;
  };

  const submitPrimaryBatch = async (data) => {
    const submit = await fetch(`${API_BASE}/api/v1/brightdata-google-ai/classify-batch/`, {
      method: 'POST',
      headers: getHybridHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ merchants: data, ...getViewingTenantBody() }),
    });
    const accepted = await submit.json().catch(() => ({}));
    if (!submit.ok) {
      throw new Error(accepted.message || `Server error ${submit.status}`);
    }
    return pollPrimaryJob(accepted.job_id);
  };

  const logPrimaryResults = (data, lastProg, attemptTag) => {
    const byUrl = {};
    for (const item of data) byUrl[item.url] = item;
    const results = Array.isArray(lastProg?.results) ? lastProg.results : [];
    const seen = new Set();
    for (const row of results) {
      const url = row.url || '';
      if (!url) continue;
      seen.add(url);
      const meta = byUrl[url] || {};
      const ok = row.status === 'success';
      addAuditLog({
        scope: 'primary',
        merchantName: row.merchant_name || meta.merchant_name || '',
        url,
        stage: 'pipeline',
        status: ok ? 'success' : 'failed',
        reason: ok ? `Primary crawler (${attemptTag})` : `Primary crawler failed (${attemptTag})`,
        details: ok
          ? `MCC ${row.primary_mcc || 'n/a'}${row.primary_category ? ` — ${row.primary_category}` : ''}`
          : (row.message || 'Classification failed'),
        dedupeKey: `primary|${attemptTag}|${ok ? 'ok' : 'fail'}|${url}|${row.primary_mcc || row.message || ''}`,
      });
    }
    for (const item of data) {
      if (seen.has(item.url)) continue;
      const failedList = lastProg?.failed_urls || [];
      const failed = failedList.includes(item.url) || (lastProg?.failed || 0) > 0;
      addAuditLog({
        scope: 'primary',
        merchantName: item.merchant_name || '',
        url: item.url,
        stage: 'pipeline',
        status: failed ? 'failed' : 'success',
        reason: failed ? `Primary crawler failed (${attemptTag})` : `Primary crawler (${attemptTag})`,
        details: failed ? 'No result returned' : 'Completed',
        dedupeKey: `primary|${attemptTag}|missing|${item.url}`,
      });
    }
  };

  const failedMerchantsFromPrimary = (data, lastProg) => {
    const failedUrls = new Set(lastProg?.failed_urls || []);
    const metaByUrl = {};
    for (const row of lastProg?.results || []) {
      if (!row?.url) continue;
      if (row.status && row.status !== 'success') {
        failedUrls.add(row.url);
        metaByUrl[row.url] = {
          failure_class: row.failure_class || '',
          message: row.message || '',
        };
      }
    }
    return data
      .filter((d) => failedUrls.has(d.url))
      .map((d) => ({
        ...d,
        failure_class: metaByUrl[d.url]?.failure_class || d.failure_class || '',
        primary_fail_message: metaByUrl[d.url]?.message || d.primary_fail_message || '',
      }));
  };

  /** Primary failures where another Bright Data attempt is unlikely to help — go straight to Fallback. */
  const INSTANT_FALLBACK_CLASSES = new Set([
    'no_mcc',
    'serp_only',
    'unreachable',
    'no_business_signal',
    'policy_blocked',
    'persist_error',
  ]);

  const splitPrimaryFailuresForHandoff = (failedList) => {
    const instant = [];
    const retryPrimary = [];
    for (const item of failedList || []) {
      const msg = String(item.primary_fail_message || item.message || '').toLowerCase();
      const fclass = String(item.failure_class || '').toLowerCase();
      const looksEmptyMcc =
        fclass === 'no_mcc' ||
        msg.includes('no mcc codes') ||
        msg.includes('search results only');
      if (INSTANT_FALLBACK_CLASSES.has(fclass) || looksEmptyMcc) {
        instant.push(item);
      } else {
        retryPrimary.push(item);
      }
    }
    return { instant, retryPrimary };
  };

  const classifyFailoverOne = async (item) => {
    const urlTrimmed = item.url?.trim();
    const bank = item.bank_provided_mcc || item.currentMcc || '';
    try {
      const res = await fetch(`${API_BASE}/api/hybrid/classify-failover`, {
        method: 'POST',
        headers: getHybridHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          merchant_url: urlTrimmed,
          bank_provided_mcc: bank,
          ...getViewingTenantBody(),
        }),
        signal: AbortSignal.timeout(600000),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || `Failover classification error ${res.status}`);
      return {
        success: true,
        data,
        url: urlTrimmed,
        merchant_name: item.merchant_name || item.merchantName || '',
        engine: 'failover_go_playwright',
      };
    } catch (err) {
      return {
        success: false,
        error: err.message || 'Failover failed',
        url: urlTrimmed,
        merchant_name: item.merchant_name || item.merchantName || '',
      };
    }
  };

  const runFailoverBatch = async (items, attemptTag) => {
    const results = await Promise.allSettled(items.map((item) => classifyFailoverOne(item)));
    const stillFailed = [];
    results.forEach((settled, idx) => {
      const item = items[idx];
      const value = settled.status === 'fulfilled' ? settled.value : { success: false, error: settled.reason?.message || 'Failover failed', url: item.url };
      if (value.success) {
        const mcc = value.data?.primary_mcc_code || '';
        addAuditLog({
          scope: 'failover',
          merchantName: item.merchant_name || item.merchantName || '',
          url: item.url,
          stage: 'classify',
          status: 'success',
          reason: `Failover crawler (${attemptTag})`,
          details: mcc ? `MCC ${mcc}` : 'Classified via failover_go_playwright',
          dedupeKey: `failover|${attemptTag}|ok|${item.url}|${mcc}`,
        });
      } else {
        stillFailed.push({
          ...item,
          failure_class: 'failover_failed',
          primary_fail_message: value.error || 'Failover failed',
        });
        addAuditLog({
          scope: 'failover',
          merchantName: item.merchant_name || item.merchantName || '',
          url: item.url,
          stage: 'classify',
          status: 'failed',
          reason: `Failover crawler (${attemptTag})`,
          details: value.error || 'Failover failed',
          dedupeKey: `failover|${attemptTag}|fail|${item.url}|${value.error || ''}`,
        });
      }
    });
    return stillFailed;
  };

  const classifyFallbackOne = async (item) => {
    const urlTrimmed = item.url?.trim();
    const bank = item.bank_provided_mcc || item.currentMcc || '';
    try {
      const res = await fetch(`${API_BASE}/api/hybrid/classify-vertex`, {
        method: 'POST',
        headers: getHybridHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          merchant_url: urlTrimmed,
          bank_provided_mcc: bank,
          ...getViewingTenantBody(),
        }),
        signal: AbortSignal.timeout(300000),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || `Classification error ${res.status}`);
      return { success: true, data, url: urlTrimmed, merchant_name: item.merchant_name || item.merchantName || '' };
    } catch (vertexErr) {
      try {
        const res = await fetch(`${API_BASE}/api/hybrid/classify-openai`, {
          method: 'POST',
          headers: getHybridHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            merchant_url: urlTrimmed,
            bank_provided_mcc: bank,
            ...getViewingTenantBody(),
          }),
          signal: AbortSignal.timeout(300000),
        });
        if (!res.ok) throw new Error(`OpenAI fallback error ${res.status}`);
        const data = await res.json();
        return { success: true, data, url: urlTrimmed, merchant_name: item.merchant_name || item.merchantName || '' };
      } catch (openaiErr) {
        return {
          success: false,
          error: openaiErr.message || vertexErr.message || 'Fallback classification failed',
          url: urlTrimmed,
          merchant_name: item.merchant_name || item.merchantName || '',
        };
      }
    }
  };

  const runFallbackBatch = async (items, attemptTag) => {
    const results = await Promise.allSettled(items.map((item) => classifyFallbackOne(item)));
    const stillFailed = [];
    results.forEach((r, idx) => {
      const item = items[idx];
      const value = r.status === 'fulfilled' ? r.value : { success: false, error: r.reason?.message || 'Failed', url: item.url };
      if (value.success) {
        const mcc = value.data?.primary_mcc_code || value.data?.mcc_code || '';
        addAuditLog({
          scope: 'fallback',
          merchantName: item.merchant_name || item.merchantName || '',
          url: item.url,
          stage: 'pipeline',
          status: 'success',
          reason: `Fallback crawler (${attemptTag})`,
          details: mcc ? `MCC ${mcc}` : 'Completed',
          dedupeKey: `fallback|${attemptTag}|ok|${item.url}|${mcc}`,
        });
        recordScanBilling({
          merchant_url: item.url,
          merchant_name: item.merchant_name || item.merchantName || '',
          upload_type: items.length > 1 ? 'bulk' : 'single',
        });
      } else {
        addAuditLog({
          scope: 'fallback',
          merchantName: item.merchant_name || item.merchantName || '',
          url: item.url,
          stage: 'pipeline',
          status: 'failed',
          reason: `Fallback crawler failed (${attemptTag})`,
          details: value.error || 'Classification failed',
          dedupeKey: `fallback|${attemptTag}|fail|${item.url}|${value.error || ''}`,
        });
        stillFailed.push(item);
      }
    });
    return stillFailed;
  };

  const enqueueManualRetry = async (items, reason) => {
    if (!items?.length) return;
    const merchants = items.map((item) => ({
      url: item.url,
      merchant_name: item.merchant_name || item.merchantName || '',
      failure_reason: reason || 'Failed after Primary and Fallback retries',
      last_error: item.last_error || item.message || reason || 'Hard failure after retries',
    }));
    for (const item of merchants) {
      addAuditLog({
        scope: 'manual_retry',
        merchantName: item.merchant_name || '',
        url: item.url,
        stage: 'queue',
        status: 'failed',
        reason: item.failure_reason,
        details: 'Queued for Manual Retry Queue',
        dedupeKey: `manual_retry|${item.url}|${item.failure_reason}`,
      });
    }
    try {
      const res = await fetch(`${API_BASE}/api/merchants/manual-retry-queue/enqueue`, {
        method: 'POST',
        headers: getHybridHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ merchants }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Enqueue failed (${res.status})`);
      }
      showToast(
        'warning',
        'Manual Retry Queue',
        `${data.total || merchants.length} URL(s) added to Manual Retry Queue.`
      );
      try {
        await fetchManualRetryQueue();
      } catch (_) { /* ignore refresh errors */ }
    } catch (err) {
      showToast('error', 'Retry queue', err.message || 'Could not enqueue failed URLs');
    }
  };

  const processBdManual = async () => {
    const data = collectBdManualMerchants();
    if (data.length === 0) {
      showToast('warning', 'No URLs', 'Please enter at least one valid merchant URL.');
      return;
    }
    if (data.length > 10) {
      showToast('warning', 'Too Many URLs', 'Maximum 10 URLs for single/multi Primary upload.');
      return;
    }
    try {
      await assertPocQuotaAllows(data.length);
    } catch (quotaErr) {
      showToast('error', 'POC quota', quotaErr?.message || 'Merchant quota exceeded');
      return;
    }
    setBdUrlList(data);
    await processBdBatchWith(data, { enableFailover: true });
  };

  const fetchCrawlPrimaryEngine = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/hybrid/crawl-engine-config`, { headers: getHybridHeaders() });
      if (!res.ok) return 'brightdata';
      const cfg = await res.json().catch(() => ({}));
      return String(cfg.primary_engine || 'brightdata').toLowerCase();
    } catch {
      return 'brightdata';
    }
  };

  const processBdBatchWith = async (incoming, options = {}) => {
    const enableFailover = options.enableFailover !== false;
    const skipSameRetry = options.skipSameRetry === true;
    const gated = await validateMerchantsForCrawl(incoming, { pipelineLabel: 'Primary Crawler' });
    if (!gated.ok) {
      return { failed: gated.invalid || [] };
    }
    const data = gated.merchants.map((item) => ({
      merchant_name: item.merchant_name || item.merchantName || '',
      url: item.url,
      bank_provided_mcc: String(item.bank_provided_mcc || item.currentMcc || '')
        .replace(/\D/g, '')
        .slice(0, 4),
    }));
    if (data.length > 10000) {
      showToast('warning', 'Too Many URLs', 'Maximum 10,000 URLs per Primary crawler batch.');
      return { failed: data };
    }
    try {
      await assertDemoScanAllowed(data.map((d) => d.url));
    } catch (gateErr) {
      showToast('error', 'Scan blocked', gateErr?.message || 'Scan not allowed for Demo partner');
      return { failed: data };
    }
    setBdUrlList(data);
    setIsBdProcessing(true);
    setBdResults(null);
    setBdError(null);
    setBdLiveProgress({ running: true, total: data.length, success: 0, failed: 0, completed: 0 });
    let failed = [];
    try {
      await resolveUrlLocationsAtScanStart(data.map((d) => d.url), { force: true, async: true });

      // Ops switch: CRAWL_PRIMARY_ENGINE can promote failover / go_playwright to Primary.
      const primaryEngine = await fetchCrawlPrimaryEngine();
      if (primaryEngine === 'failover') {
        showToast('info', 'Primary = Failover', `Running ${data.length} URL(s) on dedicated failover Go+Playwright path…`);
        failed = await runFailoverBatch(data, 'primary-promoted');
        if (failed.length) {
          await enqueueManualRetry(failed, 'Failed on promoted failover Primary');
        }
        showToast('success', 'Scan complete', `${data.length} merchant(s) processed via failover Primary.`);
        return { failed };
      }
      if (primaryEngine === 'go_playwright') {
        showToast('info', 'Primary = Go+Playwright', `Running ${data.length} URL(s) on Fallback/hybrid path as Primary…`);
        failed = await runFallbackBatch(data, 'primary-go-playwright');
        if (failed.length && enableFailover) {
          failed = await runFailoverBatch(failed, 'from-go-playwright-primary');
        }
        if (failed.length) {
          await enqueueManualRetry(failed, 'Failed after Go+Playwright Primary (+ failover)');
        }
        showToast('success', 'Scan complete', `${data.length} merchant(s) processed via Go+Playwright Primary.`);
        return { failed };
      }

      const firstProg = await submitPrimaryBatch(data);
      logPrimaryResults(data, firstProg, 'attempt-1');
      failed = failedMerchantsFromPrimary(data, firstProg);

      // Smart handoff: empty-MCC / search-page / blocked → skip Primary retry, go Fallback now.
      // Other failures still get one same-crawler Primary retry first.
      let instantFallback = [];
      if (failed.length && !skipSameRetry) {
        const split = splitPrimaryFailuresForHandoff(failed);
        instantFallback = split.instant;
        const needsPrimaryRetry = split.retryPrimary;
        if (instantFallback.length) {
          showToast(
            'info',
            'Instant Fallback',
            `${instantFallback.length} URL(s) had an empty or unusable PayLegit AI MCC — switching to the Fallback crawler (skipping the Primary retry).`
          );
          for (const item of instantFallback) {
            addAuditLog({
              scope: 'primary',
              merchantName: item.merchant_name || '',
              url: item.url,
              stage: 'handoff',
              status: 'failed',
              reason: 'Instant Fallback handoff',
              details: `${item.failure_class || 'no_mcc'}: ${item.primary_fail_message || 'empty MCC'} → Fallback`,
              dedupeKey: `primary|instant-fallback|${item.url}|${item.failure_class || 'no_mcc'}`,
            });
          }
        }
        if (needsPrimaryRetry.length) {
          showToast('info', 'Retrying Primary', `Retrying ${needsPrimaryRetry.length} failed URL(s) on Primary crawler…`);
          const retryProg = await submitPrimaryBatch(needsPrimaryRetry);
          logPrimaryResults(needsPrimaryRetry, retryProg, 'retry');
          const stillAfterRetry = failedMerchantsFromPrimary(needsPrimaryRetry, retryProg);
          failed = [...instantFallback, ...stillAfterRetry];
        } else {
          failed = [...instantFallback];
        }
      } else if (failed.length && skipSameRetry) {
        // Caller asked to skip Primary retry — treat all as ready for Fallback/finish
        const split = splitPrimaryFailuresForHandoff(failed);
        instantFallback = split.instant;
        failed = [...split.instant, ...split.retryPrimary];
      }

      if (enableFailover && failed.length) {
        const key = failed.map((f) => f.url).sort().join('|');
        if (!failoverGuardRef.current.has(`p2f:${key}`)) {
          failoverGuardRef.current.add(`p2f:${key}`);
          const instantCount = failed.filter((f) => {
            const fc = String(f.failure_class || '').toLowerCase();
            const msg = String(f.primary_fail_message || '').toLowerCase();
            return INSTANT_FALLBACK_CLASSES.has(fc) || msg.includes('no mcc');
          }).length;
          showToast(
            'info',
            'Fallback crawler',
            instantCount
              ? `Running ${failed.length} URL(s) on Fallback (${instantCount} instant empty-MCC handoff)…`
              : `Running ${failed.length} failed URL(s) on Fallback crawler…`
          );
          failed = await runFallbackBatch(failed, 'from-primary');
          if (failed.length) {
            failed = await runFallbackBatch(failed, 'from-primary-retry');
          }
          if (failed.length) {
            showToast(
              'info',
              'Failover crawler',
              `Running ${failed.length} URL(s) on dedicated Go+Playwright failover path…`
            );
            failed = await runFailoverBatch(failed, 'from-fallback');
          }
          if (failed.length) {
            await enqueueManualRetry(failed, 'Failed after Primary + Fallback + Failover');
          }
        }
      }

      showToast('success', 'Scan complete', `${data.length} merchant(s) processed. Open Reports to review.`);
      return { failed };
    } catch (err) {
      setBdError(err.message || 'Batch failed');
      for (const item of data) {
        addAuditLog({
          scope: 'primary',
          merchantName: item.merchant_name || '',
          url: item.url,
          stage: 'pipeline',
          status: 'failed',
          reason: 'Primary crawler batch error',
          details: err.message || 'Batch failed',
          dedupeKey: `primary|batch-error|${item.url}|${err.message || ''}`,
        });
      }
      return { failed: data };
    } finally {
      setIsBdProcessing(false);
    }
  };

  const processBdBatch = async () => {
    try {
      await assertPocQuotaAllows(bdUrlList.length);
    } catch (quotaErr) {
      showToast('error', 'POC quota', quotaErr?.message || 'Merchant quota exceeded');
      return;
    }
    await processBdBatchWith(bdUrlList, { enableFailover: true });
  };

  const processFallbackWithPrimaryFailover = async (validUrls) => {
    const items = validUrls.map((item) => ({
      merchant_name: item.merchantName || item.merchant_name || '',
      url: item.url,
      bank_provided_mcc: item.currentMcc || item.bank_provided_mcc || '',
      merchantName: item.merchantName || '',
      currentMcc: item.currentMcc || '',
    }));
    let failed = await runFallbackBatch(items, 'attempt-1');
    if (failed.length) {
      failed = await runFallbackBatch(failed, 'retry');
    }
    if (failed.length) {
      const key = failed.map((f) => f.url).sort().join('|');
      if (!failoverGuardRef.current.has(`f2fo:${key}`)) {
        failoverGuardRef.current.add(`f2fo:${key}`);
        showToast('info', 'Failover crawler', `Running ${failed.length} URL(s) on dedicated failover path…`);
        failed = await runFailoverBatch(failed, 'from-fallback-tab');
      }
    }
    if (failed.length) {
      const key = failed.map((f) => f.url).sort().join('|');
      if (!failoverGuardRef.current.has(`f2p:${key}`)) {
        failoverGuardRef.current.add(`f2p:${key}`);
        showToast('info', 'Primary crawler', `Running ${failed.length} failed URL(s) on Primary crawler…`);
        await processBdBatchWith(failed, { enableFailover: false, skipSameRetry: false });
      }
    }
  };

  // ── Above 500 URLs large-batch handlers ───────────────────────────────────

  const handleLargeBatchCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLargeBatchCsvFile(file);
    setLargeBatchResults(null);
    setLargeBatchError(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const lines = ev.target.result.split('\n');
      const data = [];
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        if (i === 0 && /merchant/i.test(line) && /url/i.test(line)) {
          continue;
        }

        const [merchantName, url] = line.split(',').map(s => s.trim().replace(/^["']|["']$/g, ''));
        if (url) {
          const normalized = normalizeUrlForProcessing(url);
          if (normalized.ok && normalized.url) {
            data.push({ merchant_name: merchantName || '', url: normalized.url });
          }
        }
      }
      setLargeBatchUrlList(data);
    };
    reader.readAsText(file);
  };

  const processLargeBatch = async () => {
    const data = largeBatchUrlList.reduce((acc, item) => {
      const normalized = normalizeUrlForProcessing(item.url);
      if (normalized.ok && normalized.url) {
        acc.push({ ...item, url: normalized.url });
      }
      return acc;
    }, []);

    if (data.length === 0) {
      showToast('warning', 'No URLs', 'Please upload a CSV file with URLs first.');
      return;
    }
    if (data.length > 10000) {
      showToast('warning', 'Too Many URLs', 'Maximum 10,000 URLs allowed per large-batch request.');
      return;
    }

    setLargeBatchUrlList(data);
    setIsLargeBatchProcessing(true);
    setLargeBatchResults(null);
    setLargeBatchError(null);

    // ── Add individual audit log entries per merchant (feeds Merchant Trigger History) ──
    const initialProgress = {};
    data.forEach((item) => {
      initialProgress[item.url] = {
        status: 'queued',
        stage: 'Queue',
        progress: 10,
        mcc_code: null,
        mcc_name: '',
        confidence: null,
      };
      addAuditLog({
        scope: 'bulk>500',
        merchantName: item.merchant_name || '',
        url: item.url,
        stage: 'pipeline',
        status: 'processing',
        reason: 'Queued in large-batch',
        details: 'Submitted to server',
        dedupeKey: `bulk>500|queued|${item.url}`
      });
    });
    setLargeBatchDetailedProgress(initialProgress);

    const normalizeUrl = (u) => (u || '').toLowerCase()
      .replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');

    showToast('info', '📤 Large Batch Submitted!',
      `${data.length} merchant${data.length !== 1 ? 's' : ''} queued for classification.\n` +
      `Real-time progress shown below. Processing runs server-side.`,
      { label: 'View Reports →', onClick: () => navigate('/reports') }
    );

    // ── Fire the backend POST in background (non-blocking) ──
    let backendDone = false;
    let backendResult = null;
    let backendError = null;

    const backendPromise = (async () => {
      try {
        const response = await fetch(`${API_BASE}/api/hybrid/large-batch`, {
          method: 'POST',
          headers: getHybridHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ merchants: data }),
          signal: AbortSignal.timeout(3600000), // 1 hour
        });
        if (!response.ok) {
          const errText = await response.text();
          throw new Error(`Server error ${response.status}: ${errText}`);
        }
        backendResult = await response.json();
      } catch (err) {
        backendError = err.message;
      } finally {
        backendDone = true;
      }
    })();

    // ── Poll for per-merchant MCC results while backend processes ──
    const mccCompletedUrls = new Set();
    const notifiedUrls = new Set();
    let pollAttempts = 0;
    const maxPollAttempts = 600; // ~30 min at 3s intervals

    while (pollAttempts < maxPollAttempts) {
      await new Promise(resolve => setTimeout(resolve, 3000));
      pollAttempts++;

      try {
        const hybridResponse = await fetch(`${API_BASE}/api/hybrid/merchants?include_unclassified=true&limit=2000`, {
          headers: getHybridHeaders(),
        });
        if (hybridResponse.ok) {
          const hybridData = await hybridResponse.json();
          (hybridData.merchants || []).forEach(merchant => {
            const mNorm = normalizeUrl(merchant.merchant_url);
            const dataItem = data.find(item => normalizeUrl(item.url) === mNorm);
            if (!dataItem) return;

            const itemUrl = dataItem.url;
            const hasMCC = merchant.mcc_primary && String(merchant.mcc_primary).trim() !== '';

            if (hasMCC && !notifiedUrls.has(itemUrl)) {
              notifiedUrls.add(itemUrl);
              mccCompletedUrls.add(itemUrl);

              const confidence = merchant.mcc_confidence ? (merchant.mcc_confidence * 100).toFixed(1) : 'N/A';

              // Update per-row progress
              setLargeBatchDetailedProgress(prev => ({
                ...prev,
                [itemUrl]: {
                  ...prev[itemUrl],
                  status: 'completed',
                  stage: 'Complete',
                  progress: 100,
                  mcc_code: merchant.mcc_primary,
                  mcc_name: merchant.mcc_category || '',
                  confidence: merchant.mcc_confidence ? merchant.mcc_confidence * 100 : null,
                }
              }));

              // Per-merchant success toast
              showToast(
                'success', '✅ MCC Classified!',
                `${dataItem.merchant_name || itemUrl}\nMCC: ${merchant.mcc_primary} – ${merchant.mcc_category || 'N/A'}\nConfidence: ${confidence}%`,
                { label: 'View Reports →', onClick: () => navigate('/reports') }
              );

              // Add success audit log (for Merchant Trigger History)
              addAuditLog({
                scope: 'bulk>500',
                merchantName: dataItem.merchant_name || '',
                url: itemUrl,
                stage: 'mcc',
                status: 'success',
                reason: 'MCC classified',
                details: `${merchant.mcc_primary || 'N/A'} - ${merchant.mcc_category || 'N/A'}`,
                dedupeKey: `bulk>500|mcc|success|${itemUrl}|${merchant.mcc_primary || ''}`
              });
            } else if (!hasMCC && !mccCompletedUrls.has(itemUrl)) {
              // Merchant exists in DB but not yet classified — show as in-progress
              setLargeBatchDetailedProgress(prev => ({
                ...prev,
                [itemUrl]: {
                  ...prev[itemUrl],
                  status: 'processing',
                  stage: 'Mcc',
                  progress: 66,
                }
              }));
            }
          });
        }
      } catch (e) {
        console.log('[LargeBatch] Poll error:', e);
      }

      // If backend finished AND all merchants polled — done
      if (backendDone && mccCompletedUrls.size >= data.length) {
        console.log('[LargeBatch] All merchants MCC-classified, stopping poll');
        break;
      }

      // If backend returned error, stop early
      if (backendDone && backendError) {
        break;
      }

      // If backend is done, give a few more poll cycles for the last results
      if (backendDone && pollAttempts % 10 === 0) {
        console.log(`[LargeBatch] Backend done, poll ${pollAttempts}: ${mccCompletedUrls.size}/${data.length} MCC done`);
        // If no new completions in last 30 seconds after backend done, stop
        if (mccCompletedUrls.size === (mccCompletedUrls._lastSize || 0)) {
          break;
        }
        mccCompletedUrls._lastSize = mccCompletedUrls.size;
      }

      console.log(`[LargeBatch] Poll ${pollAttempts}: ${mccCompletedUrls.size}/${data.length} MCC done`);
    }

    // Wait for backend to finish if it hasn't
    await backendPromise;

    // ── Mark failed URLs based on backend result ──
    if (backendResult) {
      setLargeBatchResults(backendResult);

      // Mark failed URLs from batch_results
      const allFailedUrls = new Set();
      (backendResult.batch_results || []).forEach(batch => {
        (batch.failed_urls || []).forEach(failedUrl => {
          allFailedUrls.add(failedUrl);
        });
      });

      allFailedUrls.forEach(failedUrl => {
        if (!mccCompletedUrls.has(failedUrl)) {
          setLargeBatchDetailedProgress(prev => ({
            ...prev,
            [failedUrl]: {
              ...prev[failedUrl],
              status: 'failed',
              stage: 'Error',
              progress: 0,
            }
          }));

          const dataItem = data.find(item => item.url === failedUrl);
          addAuditLog({
            scope: 'bulk>500',
            merchantName: dataItem?.merchant_name || '',
            url: failedUrl,
            stage: 'pipeline',
            status: 'failed',
            reason: 'No pages fetched - all URLs failed (rate limit or connection errors)',
            details: 'Failed after retries',
            dedupeKey: `bulk>500|pipeline|failed|${failedUrl}`
          });
        }
      });

      addAuditLog({
        scope: 'bulk>500',
        merchantName: '',
        url: '',
        stage: 'pipeline',
        status: backendResult.total_failed > 0 ? 'info' : 'success',
        reason: 'Large batch completed',
        details: `Success: ${backendResult.total_success || 0}, Failed: ${backendResult.total_failed || 0}, Time: ${(backendResult.duration_seconds || 0).toFixed(1)}s`,
        dedupeKey: `bulk>500|pipeline|completed|${backendResult.total_success || 0}|${backendResult.total_failed || 0}`
      });
    }

    if (backendError) {
      setLargeBatchError(backendError);
      addAuditLog({
        scope: 'bulk>500',
        merchantName: '',
        url: '',
        stage: 'pipeline',
        status: 'failed',
        reason: 'Large batch failed',
        details: backendError,
        dedupeKey: `bulk>500|pipeline|failed|${backendError}`
      });
    }

    setIsLargeBatchProcessing(false);

    if (backendResult) {
      showToast(
        backendResult.total_failed > 0 ? 'warning' : 'success',
        '🏁 Large Batch Complete',
        `${backendResult.total_success || 0} classified, ${backendResult.total_failed || 0} failed\nTotal: ${(backendResult.duration_seconds || 0).toFixed(1)}s`,
        { label: 'View Reports →', onClick: () => navigate('/reports') }
      );
    }
  };

  const processBatch = async (batch) => {
    setBulkBatches(prev => prev.map(b => 
      b.id === batch.id ? { ...b, status: 'processing' } : b
    ));

    const results = await Promise.allSettled(
      batch.urls.map(item => processUrl(item, batch.id))
    );

    let completed = 0;
    let failed = 0;

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        completed++;
      } else {
        failed++;
        setRetryQueue(prev => [...prev, { 
          ...batch.urls[index], 
          batchId: batch.id,
          reason: result.reason || 'Unknown error',
          retryCount: 0
        }]);
      }
    });

    setBulkBatches(prev => prev.map(b => 
      b.id === batch.id ? { 
        ...b, 
        status: 'completed', 
        completed,
        failed,
        progress: 100 
      } : b
    ));
  };

  const processUrl = async (item, batchId, isRetry = false) => {
    const { merchantName, url } = item;
    
    try {
      // Update progress - Fetching
      setBulkDetailedProgress(prev => ({
        ...prev,
        [url]: { ...prev[url], status: 'fetching', stage: 'fetch', progress: 0 }
      }));
      setBulkProgress(prev => ({
        ...prev,
        [url]: { status: 'fetching', stage: 'fetch', progress: 0 }
      }));

      // Step 1: Fetch
      const fetchResponse = await fetch(`${API_BASE}/api/fetchers/ats-crawl`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          root_url: url, 
          merchant_name: merchantName,
          max_pages: 60 
        }),
        signal: AbortSignal.timeout(300000) // 5 minute timeout for fetch only
      });

      if (!fetchResponse.ok) {
        throw new Error(`Fetch failed: ${fetchResponse.statusText}`);
      }

      const fetchData = await fetchResponse.json();

      if (fetchData?.status === 'same_day_report') {
        setBulkDetailedProgress(prev => ({
          ...prev,
          [url]: { ...prev[url], status: 'completed', stage: 'report', progress: 100 }
        }));
        setBulkProgress(prev => ({
          ...prev,
          [url]: { status: 'completed', stage: 'report', progress: 100 }
        }));
        return { success: true, sameDay: true, data: fetchData };
      }
      
      // Update with fetch results
      setBulkDetailedProgress(prev => ({
        ...prev,
        [url]: { 
          ...prev[url], 
          status: 'parsing', 
          stage: 'parse', 
          progress: 33,
          discovered_urls: fetchData.pages_fetched || 0
        }
      }));
      setBulkProgress(prev => ({
        ...prev,
        [url]: { status: 'parsing', stage: 'parse', progress: 33 }
      }));

      // Step 2: Parse (auto-triggered by backend)
      // Monitor parsing progress - NO TIMEOUT, let backend complete
      const merchantURL = new URL(url).hostname.replace(/^www\./, '');
      let parseComplete = false;
      
      while (!parseComplete) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        try {
          const parseResponse = await fetch(`${API_BASE}/api/merchants/progress?merchant_url=${encodeURIComponent(merchantURL)}`);
          if (parseResponse.ok) {
            const parseData = await parseResponse.json();
            
            // Update detailed progress with timing
            setBulkDetailedProgress(prev => ({
              ...prev,
              [url]: { 
                ...prev[url], 
                parse_status: parseData.status || 'running',
                parsed_pages: parseData.parsed_pages || 0,
                total_pages: parseData.total_pages || 0,
                parse_time: parseData.parse_time || 0,
                progress: 33 + (parseData.progress_percentage || 0) * 0.33
              }
            }));
            
            if (parseData.status === 'completed' || parseData.status === 'failed') {
              parseComplete = true;
              if (parseData.status === 'failed') {
                throw new Error('Parsing failed - no valid pages fetched');
              }
            }
          }
        } catch (e) {
          if (e.message.includes('failed')) throw e;
          console.log('Parse status check failed, retrying...:', e);
        }
      }

      // Update progress - Classifying
      setBulkDetailedProgress(prev => ({
        ...prev,
        [url]: { ...prev[url], status: 'classifying', stage: 'mcc', progress: 66 }
      }));
      setBulkProgress(prev => ({
        ...prev,
        [url]: { status: 'classifying', stage: 'mcc', progress: 66 }
      }));

      // Step 3: MCC Classification - Poll for completion (NO TIMEOUT - let LLM finish)
      let mccComplete = false;
      let lastMccStatus = '';
      let mccAttempts = 0;
      
      while (!mccComplete) {
        await new Promise(resolve => setTimeout(resolve, 5000)); // Check every 5 seconds
        mccAttempts++;
        
        try {
          // Use correct API endpoint through nginx proxy
          const mccResponse = await fetch(`${API_BASE}/api/hybrid/merchants?include_unclassified=true&merchant_url=${encodeURIComponent(url)}&limit=1`, {
            headers: getHybridHeaders(),
          });
          if (mccResponse.ok) {
            const mccData = await mccResponse.json();
            const normalizeUrl = (u = '') =>
              u.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
            const merchant = mccData.merchants?.find(m => normalizeUrl(m.merchant_url) === normalizeUrl(url));
            
            if (merchant) {
              console.log(`[${mccAttempts}] MCC poll for ${merchantURL}:`, {
                mcc_primary: merchant.mcc_primary,
                mistral_processed: merchant.mistral_processed,
                stage1_time: merchant.stage1_time_ms,
                stage2_time: merchant.stage2_time_ms,
                stage3_time: merchant.stage3_time_ms
              });
              
              // Update progress with timing data — only 'completed' when a real MCC code exists
              const currentStatus = (merchant.mcc_primary && String(merchant.mcc_primary).trim() !== '') ? 'completed' : 'running';
              
              setBulkDetailedProgress(prev => ({
                ...prev,
                [url]: { 
                  ...prev[url], 
                  mcc_status: currentStatus,
                  stage1_time: merchant.stage1_time_ms ? (merchant.stage1_time_ms / 1000).toFixed(1) : 0,
                  stage2_time: merchant.stage2_time_ms ? (merchant.stage2_time_ms / 1000).toFixed(1) : 0,
                  stage3_time: merchant.stage3_time_ms ? (merchant.stage3_time_ms / 1000).toFixed(1) : 0,
                  mcc_time: merchant.processing_time_ms ? (merchant.processing_time_ms / 1000).toFixed(1) : 0,
                  progress: merchant.mcc_primary ? 100 : 75
                }
              }));
              
              // Check if MCC classification is complete — require a real non-empty MCC code
              if (merchant.mcc_primary && String(merchant.mcc_primary).trim() !== '') {
                console.log(`✅ MCC Complete for ${merchantURL}!`, {
                  mcc_primary: merchant.mcc_primary,
                  mcc_category: merchant.mcc_category,
                  confidence: merchant.mcc_confidence
                });
                mccComplete = true;
                
                const totalTime = (
                  (merchant.fetch_time_ms || 0) + 
                  (merchant.parse_time_ms || 0) + 
                  (merchant.processing_time_ms || 0)
                ) / 1000;
                
                setBulkDetailedProgress(prev => ({
                  ...prev,
                  [url]: { 
                    ...prev[url], 
                    status: 'completed', 
                    stage: 'complete', 
                    progress: 100,
                    mcc_code: merchant.mcc_primary,
                    mcc_name: merchant.mcc_category || 'Classified',
                    confidence: merchant.mcc_confidence ? (merchant.mcc_confidence * 100) : null,
                    total_time: totalTime.toFixed(1)
                  }
                }));
                
                setBulkProgress(prev => ({
                  ...prev,
                  [url]: { status: 'completed', stage: 'complete', progress: 100 }
                }));
                
                // Show success notification
                if (!isRetry && merchant.mcc_primary) {
                  showToast(
                    'success',
                    '✅ MCC Report Ready!',
                    `${merchantName || url}\n` +
                    `MCC: ${merchant.mcc_primary} – ${merchant.mcc_category || ''}\n` +
                    `Confidence: ${merchant.mcc_confidence ? (merchant.mcc_confidence * 100).toFixed(1) : 'N/A'}%\n` +
                    `Total Time: ${totalTime.toFixed(1)}s`,
                    { label: 'View in Reports →', onClick: () => navigate('/reports') }
                  );
                }
              } else if (currentStatus !== lastMccStatus) {
                lastMccStatus = currentStatus;
                console.log(`[${mccAttempts}] MCC classification in progress for ${merchantURL}... (waiting ${mccAttempts * 5}s)`);
              }
            } else {
              console.log(`[${mccAttempts}] Merchant ${merchantURL} not found in results yet, waiting... (${mccAttempts * 5}s elapsed)`);
              // Update status to show we're waiting for backend
              setBulkDetailedProgress(prev => ({
                ...prev,
                [url]: { 
                  ...prev[url], 
                  mcc_status: 'waiting',
                  progress: 70 + (mccAttempts % 5) * 2 // Show some progress movement
                }
              }));
            }
          } else {
            console.log(`[${mccAttempts}] MCC API response not ok: ${mccResponse.status}`);
          }
        } catch (e) {
          console.log(`[${mccAttempts}] MCC polling attempt failed, retrying...:`, e.message);
          // Don't throw error, just keep trying
        }
      }

      return { success: true, url };
    } catch (error) {
      console.error(`Failed to process ${url}:`, error);
      
      setBulkDetailedProgress(prev => ({
        ...prev,
        [url]: { 
          ...prev[url],
          status: 'failed', 
          stage: 'error', 
          progress: 0,
          error: error.message 
        }
      }));
      setBulkProgress(prev => ({
        ...prev,
        [url]: { 
          status: 'failed', 
          stage: 'error', 
          progress: 0,
          error: error.message 
        }
      }));

      throw error;
    }
  };

  const handleRetries = async () => {
    const toRetry = retryQueue.filter(item => item.retryCount < 2);
    
    if (toRetry.length === 0) {
      // Move all to manual retry
      setFailedUrls(retryQueue);
      setRetryQueue([]);
      return;
    }

    // Wait 50 seconds before retrying
    await new Promise(resolve => setTimeout(resolve, 50000));

    const retryResults = await Promise.allSettled(
      toRetry.map(item => processUrl(item, item.batchId, true))
    );

    const stillFailed = [];
    retryResults.forEach((result, index) => {
      if (result.status === 'rejected') {
        const item = toRetry[index];
        item.retryCount++;
        if (item.retryCount >= 2) {
          stillFailed.push(item);
        } else {
          setRetryQueue(prev => [...prev, item]);
        }
      }
    });

    setFailedUrls(prev => [...prev, ...stillFailed]);
    setRetryQueue(prev => prev.filter(item => item.retryCount < 2));

    // Recursive retry if needed
    if (retryQueue.length > 0) {
      await handleRetries();
    }
  };

  const manualRetry = async (item) => {
    try {
      await processUrl(item, 0, true);
      setFailedUrls(prev => prev.filter(f => f.url !== item.url));
      showToast('success', '✅ Retry Successful', `Successfully reprocessed ${item.url}`, { label: 'View Reports →', onClick: () => navigate('/reports') });
    } catch (error) {
      showToast('error', 'Retry Failed', `Failed to retry ${item.url}: ${error.message}`);
    }
  };

  // Fetch manual retry queue from backend
  const fetchManualRetryQueue = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/merchants/manual-retry-queue`);
      if (!response.ok) {
        throw new Error('Failed to fetch manual retry queue');
      }
      const data = await response.json();
      setManualRetryQueue(data.items || []);
      console.log('Manual retry queue loaded:', data.items?.length || 0, 'items');
    } catch (error) {
      console.error('Failed to fetch manual retry queue:', error);
      showToast('error', 'Error', 'Failed to load manual retry queue');
    }
  };

  // Trigger manual retry for selected URLs
  const triggerManualRetry = async () => {
    if (selectedRetryIds.length === 0) {
      showToast('error', 'No Selection', 'Please select URLs to retry');
      return;
    }

    setIsRetrying(true);
    try {
      const response = await fetch(`${API_BASE}/api/merchants/manual-retry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedRetryIds })
      });

      if (!response.ok) {
        throw new Error('Manual retry failed');
      }

      const data = await response.json();
      showToast('success', 'Retry Initiated', 
        `${data.success_count} URLs retried successfully, ${data.fail_count} failed`);
      
      // Clear selection and refresh queue
      setSelectedRetryIds([]);
      await fetchManualRetryQueue();
    } catch (error) {
      console.error('Manual retry failed:', error);
      showToast('error', 'Retry Failed', 'Failed to trigger manual retry');
    } finally {
      setIsRetrying(false);
    }
  };

  // Toggle selection of retry item
  const toggleRetrySelection = (id) => {
    setSelectedRetryIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(i => i !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Select/deselect all retry items
  const toggleSelectAllRetries = () => {
    if (selectedRetryIds.length === manualRetryQueue.length) {
      setSelectedRetryIds([]);
    } else {
      setSelectedRetryIds(manualRetryQueue.map(item => item.id));
    }
  };

  // ── Backup Crawl Functions ──────────────────────────────────────────────

  // Trigger backup crawl for selected failed URLs
  const triggerBackupCrawl = async (urls) => {
    if (!urls || urls.length === 0) {
      showToast('error', 'No URLs', 'No URLs selected for backup crawl');
      return;
    }
    const gated = await validateMerchantsForCrawl(
      urls.map((u) => (typeof u === 'string' ? { url: u } : u)),
      { pipelineLabel: 'Backup Crawler' }
    );
    if (!gated.ok) return;
    const cleanUrls = gated.merchants.map((m) => m.url);
    setBackupCrawlLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/merchants/backup-crawl`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urls: cleanUrls })
      });
      if (!response.ok) throw new Error('Failed to trigger backup crawl');
      const data = await response.json();
      setBackupCrawlBatchId(data.batch_id);
      showToast('success', 'Backup Crawl Started',
        `Batch #${data.batch_id}: Google crawling ${data.total_urls} URLs (5-30 min)`);
      // Start polling status
      pollBackupCrawlStatus(data.batch_id);
    } catch (error) {
      console.error('Backup crawl trigger failed:', error);
      showToast('error', 'Backup Crawl Failed', error.message);
    } finally {
      setBackupCrawlLoading(false);
    }
  };

  // Poll backup crawl status
  const pollBackupCrawlStatus = async (batchId) => {
    const poll = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/merchants/backup-crawl-status?batch_id=${batchId}`);
        if (!response.ok) return;
        const data = await response.json();
        setBackupCrawlStatus(data);
        if (data.status === 'completed' || data.status === 'failed') {
          showToast(data.status === 'completed' ? 'success' : 'error',
            'Backup Crawl ' + (data.status === 'completed' ? 'Complete' : 'Failed'),
            `${data.success_count || 0} success, ${data.failed_count || 0} failed`);
          return; // Stop polling
        }
        // Continue polling every 10 seconds
        setTimeout(() => poll(), 10000);
      } catch (error) {
        console.error('Backup crawl status poll error:', error);
      }
    };
    poll();
  };

  // Fetch backup crawl history
  const fetchBackupCrawlHistory = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/merchants/backup-crawl-history`);
      if (!response.ok) throw new Error('Failed to fetch history');
      const data = await response.json();
      setBackupCrawlHistory(data.batches || []);
    } catch (error) {
      console.error('Backup crawl history error:', error);
    }
  };

  useEffect(() => {
    onConnectionChange?.({ wsConnected, crawlID });
  }, [wsConnected, crawlID, onConnectionChange]);

  useEffect(() => {
    // Connect WebSocket on component mount
    connectWebSocket();

    // Fetch crawl summary immediately and then every 5 seconds
    fetchCrawlSummary();
    fetchManualRetryQueue();
    const summaryInterval = setInterval(fetchCrawlSummary, 5000);

    return () => {
      disconnectWebSocket();
      stopParsingProgressPolling();
      stopMCCProgressPolling();
      clearInterval(summaryInterval);
    };
  }, [timeFilter]);

  const scanSingle = async (urlToScan, merchantName) => {
    const urlTrimmed = urlToScan?.trim() || url.trim();
    if (!urlTrimmed) {
      return { success: false, error: "No URL provided" };
    }

    // Check if WebSocket is connected
    if (!wsConnected) {
      return { success: false, error: "WebSocket not connected" };
    }

    try {
      addAuditLog({
        scope: 'single',
        merchantName: merchantName || '',
        url: urlTrimmed,
        stage: 'fetch',
        status: 'processing',
        reason: 'Triggered',
        details: 'ATS crawl requested',
        dedupeKey: `single|fetch|triggered|${urlTrimmed}`
      });

      // Start crawl with crawl ID from WebSocket
      const response = await fetch(`${API_BASE}/api/fetchers/ats-crawl`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Crawl-ID": crawlID
        },
        body: JSON.stringify({ 
          root_url: urlTrimmed, 
          merchant_name: merchantName || '',
          max_pages: 60 
        })
      });

      if (response.ok) {
        const data = await response.json();

        if (data?.status === 'same_day_report') {
          addAuditLog({
            scope: 'single',
            merchantName: merchantName || '',
            url: urlTrimmed,
            stage: 'report',
            status: 'success',
            reason: 'Same-day report',
            details: data?.message || 'Saved a new report from today\'s result',
            dedupeKey: `single|report|sameday|${urlTrimmed}|${Date.now()}`
          });
          return { success: true, data, sameDay: true };
        }

        // ATS endpoint may return validation_failed payload with HTTP 200.
        if (data?.status === 'validation_failed') {
          const validationErr = data?.message || 'URL validation failed';
          addAuditLog({
            scope: 'single',
            merchantName: merchantName || '',
            url: urlTrimmed,
            stage: 'validation',
            status: 'failed',
            reason: validationErr,
            details: JSON.stringify(data?.validation_result || {}),
            dedupeKey: `single|validation|failed|${urlTrimmed}|${validationErr}`
          });
          return { success: false, error: validationErr, data };
        }

        addAuditLog({
          scope: 'single',
          merchantName: merchantName || '',
          url: urlTrimmed,
          stage: 'fetch',
          status: 'success',
          reason: 'Fetch accepted',
          details: 'Parser/MCC will continue asynchronously',
          dedupeKey: `single|fetch|success|${urlTrimmed}`
        });
        
        // Note: Parsing progress polling is handled centrally by processMultipleUrls
        // The backend auto-triggers parser after fetch completes
        
        return { success: true, data };
      } else {
        const errorText = await response.text();
        console.error(`Crawl failed for ${urlTrimmed}:`, errorText);
        addAuditLog({
          scope: 'single',
          merchantName: merchantName || '',
          url: urlTrimmed,
          stage: 'fetch',
          status: 'failed',
          reason: 'Fetch API failed',
          details: errorText,
          dedupeKey: `single|fetch|failed|${urlTrimmed}|${errorText}`
        });
        return { success: false, error: errorText };
      }
    } catch (error) {
      console.error("Crawl failed:", error);
      addAuditLog({
        scope: 'single',
        merchantName: merchantName || '',
        url: urlTrimmed,
        stage: 'fetch',
        status: 'failed',
        reason: 'Network error',
        details: error.message,
        dedupeKey: `single|fetch|failed|${urlTrimmed}|network`
      });
      return { success: false, error: error.message };
    }
  };

  const processMultipleUrls = async () => {
    const gated = await validateMerchantsForCrawl(urls, { pipelineLabel: 'Fallback Crawler' });
    if (!gated.ok) return;

    const validUrls = gated.merchants.map((item) => ({
      merchantName: item.merchant_name || item.merchantName || '',
      url: item.url,
      currentMcc: item.currentMcc || item.bank_provided_mcc || '',
    }));

    setIsCrawling(true);
    setAtsResults(null);
    setMerchantDetail(null);
    setParseResults(null);
    setMccResults(null);
    setParsingProgress(null);
    setMccProgress(null);
    setPerUrlStatus({});
    setNotifiedMerchants(new Set());
    
    // Store FULL URLs for tracking (with protocol)
    const merchantURLs = validUrls.map(item => item.url);
    
    console.log('🎯 Processing merchants:', merchantURLs);
    setProcessingMerchants(merchantURLs);

    // Initialise per-URL pipeline step tracker with "queued" step for each merchant
    setPerUrlStatus(prev => {
      const updated = { ...prev };
      merchantURLs.forEach(u => {
        updated[u] = { steps: [{ id: 'queue', label: '🚀 Pipeline started', status: 'done', detail: '', time: new Date().toLocaleTimeString() }] };
      });
      return updated;
    });

    validUrls.forEach(item => {
      addAuditLog({
        scope: 'single',
        merchantName: item.merchantName || '',
        url: item.url,
        stage: 'pipeline',
        status: 'processing',
        reason: 'Merchant queued',
        details: 'Waiting for fetch/parse/MCC',
        dedupeKey: `single|pipeline|queued|${item.url}`
      });
    });

    // Friendly start notification
    showToast(
      'info',
      `🚀 Processing ${validUrls.length} merchant${validUrls.length > 1 ? 's' : ''}...`,
      `Fetching, parsing and classifying with AI. This usually takes 2–5 minutes.\nResults will appear in the Reports section once ready.`,
      { label: 'Go to Reports →', onClick: () => navigate('/reports') }
    );

    try {
      await assertDemoScanAllowed(merchantURLs);
    } catch (gateErr) {
      showToast('error', 'Scan blocked', gateErr?.message || 'Scan not allowed for Demo partner');
      return;
    }
    
    setCrawlProgress({
      status: 'initializing',
      message: `Starting ${validUrls.length} URL(s) in parallel...`,
      discovered_urls: 0,
      processed_urls: 0,
      total_urls: validUrls.length,
      elapsed_time: 0
    });

    // Start progress polling immediately so Pipeline Status Monitor shows live fetch/parse stages.
    // Pass merchantURLs explicitly to skip the stale processingMerchants closure.
    startParsingProgressPolling(merchantURLs);

    // Resolve server IP locations at the start of single/multi URL scan
    resolveUrlLocationsAtScanStart(merchantURLs);

    try {
      // Process all URLs via MCC pipeline (OpenRouter Stage 3 primary; OpenAI last resort)
      console.log('▶️ Starting MCC classification for:', merchantURLs);

      const classifyViaVertexWithFallback = async (item) => {
        const urlTrimmed = item.url?.trim();
        try {
          const res = await fetch(`${API_BASE}/api/hybrid/classify-vertex`, {
            method: 'POST',
            headers: getHybridHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({
              merchant_url: urlTrimmed,
              bank_provided_mcc: item.currentMcc?.trim() || '',
            }),
            signal: AbortSignal.timeout(300000),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.message || data.error || `Classification error ${res.status}`);
          return {
            success: true,
            data,
            engine: data.stage3_engine || data.classification_engine || data.classifier_used || 'openrouter',
          };
        } catch (vertexErr) {
          console.warn(`⚠️ MCC classify failed for ${urlTrimmed}, trying OpenAI:`, vertexErr.message);
          try {
            const res = await fetch(`${API_BASE}/api/hybrid/classify-openai`, {
              method: 'POST',
              headers: getHybridHeaders({ 'Content-Type': 'application/json' }),
              body: JSON.stringify({
                merchant_url: urlTrimmed,
                bank_provided_mcc: item.currentMcc?.trim() || '',
              }),
              signal: AbortSignal.timeout(300000),
            });
            if (!res.ok) throw new Error(`OpenAI fallback error ${res.status}`);
            const data = await res.json();
            return { success: true, data, engine: 'openai-fallback' };
          } catch (openaiErr) {
            return { success: false, error: openaiErr.message };
          }
        }
      };

      // Process all URLs in parallel (Vertex AI, with per-URL OpenAI fallback)
      const results = await Promise.allSettled(
        validUrls.map(item => classifyViaVertexWithFallback(item))
      );

      const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      const failed = results.length - successful;

      console.log(`✅ Classification completed: ${successful} successful, ${failed} failed`);

      setCrawlProgress({
        status: failed > 0 ? 'error' : 'completed',
        message: failed > 0 ? `${successful} classified, ${failed} failed` : `✅ ${successful} merchant${successful !== 1 ? 's' : ''} classified`,
        discovered_urls: 0,
        processed_urls: results.length,
        total_urls: results.length,
        elapsed_time: 0
      });
      // Mark parse + MCC stages as completed so Stage 2 and Stage 3 show green
      setParsingProgress({
        status: 'completed', progress_percentage: 100,
        parsed_pages: successful, total_pages: results.length, error_count: failed, current_page: ''
      });
      setIsParsing(false);
      setIsClassifying(false);
      setIsCrawling(failed > 0 ? true : false);
      stopParsingProgressPolling();

      // Fetch real MCC data from hybrid API to populate Stage 3 and perUrlStatus properly
      try {
        const normaliseUrl = u => u.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
        for (let idx = 0; idx < results.length; idx++) {
          const r = results[idx];
          const item = validUrls[idx];
          const itemUrl = item?.url || '';
          if (r.status !== 'fulfilled' || !r.value.success) {
            // Mark failed in perUrlStatus
            setPerUrlStatus(prev => {
              const existing = prev[merchantURLs[idx]] || { steps: [] };
              const steps = [...existing.steps];
              const errMsg = (r.status === 'fulfilled' ? r.value?.error : r.reason?.message) || 'Classification failed';
              const failIdx = steps.findIndex(s => s.id === 'classify');
              const entry = { id: 'classify', label: '❌ Classification failed', status: 'error', detail: errMsg, time: new Date().toLocaleTimeString() };
              if (failIdx >= 0) steps[failIdx] = entry; else steps.push(entry);
              return { ...prev, [merchantURLs[idx]]: { ...existing, steps } };
            });
            continue;
          }
          // Fetch from hybrid API for real MCC + timing
          try {
            const resp = await fetch(`${API_BASE}/api/hybrid/merchants?merchant_url=${encodeURIComponent(itemUrl)}&limit=1`, {
              headers: getHybridHeaders(),
            });
            if (!resp.ok) continue;
            const hData = await resp.json();
            const merchant = hData.merchants?.find(m => normaliseUrl(m.merchant_url) === normaliseUrl(itemUrl));
            if (merchant?.mcc_primary) {
              const conf = merchant.mcc_confidence != null ? (merchant.mcc_confidence * 100).toFixed(1) : null;
              const totalMs = (merchant.fetch_time_ms || 0) + (merchant.parse_time_ms || 0) + (merchant.processing_time_ms || 0);
              setMccProgress({
                status: 'completed', processed: successful, total: results.length, progress_percentage: 100,
                message: `MCC ${merchant.mcc_primary} – ${merchant.mcc_category || ''}`,
                primary_mcc: { mcc_code: merchant.mcc_primary, mcc_name: merchant.mcc_category || '', confidence: merchant.mcc_confidence * 100 || 0, evidence: [] }
              });
              setMccResults({
                primary_mcc: { mcc_code: merchant.mcc_primary, mcc_name: merchant.mcc_category || '', confidence: merchant.mcc_confidence * 100 || 0, evidence: [] },
                secondary_mcc: merchant.mcc_secondary ? { mcc_code: merchant.mcc_secondary, mcc_name: merchant.mcc_secondary_category || '', confidence: merchant.mcc_secondary_confidence * 100 || 0, evidence: [] } : null,
                tertiary_mcc: merchant.mcc_tertiary ? { mcc_code: merchant.mcc_tertiary, mcc_name: merchant.mcc_tertiary_category || '', confidence: merchant.mcc_tertiary_confidence * 100 || 0, evidence: [] } : null,
              });
              // Update perUrlStatus with final step details
              setPerUrlStatus(prev => {
                const mUrl = merchantURLs[idx];
                const existing = prev[mUrl] || { steps: [] };
                const steps = [...existing.steps];
                const ensureStep = (id, label, status, detail) => {
                  const si = steps.findIndex(s => s.id === id);
                  const e = { id, label, status, detail, time: new Date().toLocaleTimeString() };
                  if (si >= 0) steps[si] = e; else steps.push(e);
                };
                ensureStep('fetch', '🌐 Website fetched', 'done', merchant.fetch_time_ms ? `${(merchant.fetch_time_ms/1000).toFixed(1)}s` : '');
                ensureStep('parse', '📝 Content parsed', 'done', merchant.parse_time_ms ? `${(merchant.parse_time_ms/1000).toFixed(1)}s` : '');
                ensureStep('classify', `✅ MCC ${merchant.mcc_primary} – ${merchant.mcc_category || 'N/A'}`, 'done',
                  `${conf ? 'Confidence ' + conf + '%' : ''}${totalMs > 0 ? ' · ' + (totalMs/1000).toFixed(1) + 's total' : ''}`);
                return { ...prev, [mUrl]: { ...existing, steps, mcc: merchant.mcc_primary, category: merchant.mcc_category, confidence: merchant.mcc_confidence } };
              });
            }
          } catch (_) { /* ignore per-URL hybrid fetch errors */ }
        }
      } catch (_) { /* ignore */ }

      // Add final audit log entries per merchant
      const failedFallbackItems = [];
      results.forEach((r, idx) => {
        const item = validUrls[idx];
        if (r.status === 'fulfilled' && r.value.success) {
          const mcc = r.value.data?.primary_mcc_code || r.value.data?.mcc_code || '';
          const cat = r.value.data?.primary_mcc_category || r.value.data?.category || '';
          const conf = r.value.data?.confidence ?? r.value.data?.primary_confidence;
          addAuditLog({
            scope: 'fallback',
            merchantName: item?.merchantName || '',
            url: item?.url || '',
            stage: 'mcc',
            status: 'success',
            reason: 'Fallback crawler MCC classified',
            details: formatMccAuditResult(mcc, cat, conf),
            dedupeKey: `fallback|mcc|success|${item?.url || idx}|${mcc}`
          });
          recordScanBilling({
            merchant_url: item?.url || '',
            merchant_name: item?.merchantName || '',
            upload_type: 'single',
          });
        } else {
          const errMsg = (r.status === 'fulfilled' ? r.value?.error : r.reason?.message) || 'Classification failed';
          addAuditLog({
            scope: 'fallback',
            merchantName: item?.merchantName || '',
            url: item?.url || '',
            stage: 'mcc',
            status: 'failed',
            reason: 'Fallback crawler failed',
            details: errMsg,
            dedupeKey: `fallback|mcc|failed|${item?.url || idx}|${errMsg}`
          });
          if (item?.url) failedFallbackItems.push(item);
        }
      });

      if (failedFallbackItems.length > 0) {
        // One same-crawler retry then Primary failover is handled here for Fallback Single/Multi.
        showToast('info', 'Retrying Fallback', `Retrying ${failedFallbackItems.length} failed URL(s)…`);
        const retryResults = await Promise.allSettled(
          failedFallbackItems.map((item) => classifyViaVertexWithFallback(item))
        );
        const stillFailed = [];
        retryResults.forEach((r, idx) => {
          const item = failedFallbackItems[idx];
          if (r.status === 'fulfilled' && r.value.success) {
            const mcc = r.value.data?.primary_mcc_code || r.value.data?.mcc_code || '';
            addAuditLog({
              scope: 'fallback',
              merchantName: item?.merchantName || '',
              url: item?.url || '',
              stage: 'mcc',
              status: 'success',
              reason: 'Fallback crawler (retry)',
              details: mcc ? `MCC ${mcc}` : 'Completed',
              dedupeKey: `fallback|retry|success|${item?.url || idx}|${mcc}`,
            });
            recordScanBilling({
              merchant_url: item?.url || '',
              merchant_name: item?.merchantName || '',
              upload_type: 'single',
            });
          } else {
            stillFailed.push(item);
          }
        });
        if (stillFailed.length) {
          await processBdBatchWith(
            stillFailed.map((item) => ({
              merchant_name: item.merchantName || '',
              url: item.url,
              bank_provided_mcc: item.currentMcc || '',
            })),
            { enableFailover: false }
          );
        }
      }
    } catch (error) {
      console.error("Multi-URL processing failed:", error);
      setCrawlProgress({
        status: 'error',
        message: `Error: ${error.message}`,
        error: error.message
      });
      setIsCrawling(false);
      stopParsingProgressPolling();

      addAuditLog({
        scope: 'single',
        merchantName: '',
        url: '',
        stage: 'pipeline',
        status: 'failed',
        reason: 'Multi-URL processing crashed',
        details: error.message || 'Unknown error',
        dedupeKey: `single|pipeline|fatal|${error.message || 'unknown'}`
      });
    }
  };

  const triggerParser = async () => {
    if (!url.trim()) {
      showToast('warning', 'No URL', 'Please enter a URL first.');
      return;
    }

    setIsParsing(true);
    setParseResults(null);

    try {
      const response = await fetch(`${API_BASE}/api/parsers/trigger-merchant`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ merchant_url: url.trim() })
      });

      if (response.ok) {
        const data = await response.json();
        setParseResults(data);
        const detail = await fetchMerchantDetailFromApi(url.trim());
        if (detail) setMerchantDetail(detail);
        
        // Start polling for parsing progress
        const merchantURL = new URL(url.trim()).hostname;
        startParsingProgressPolling(merchantURL);
        
        showToast('success', '✅ Parser Completed', `${data.parsed_count} pages parsed successfully, ${data.error_count || 0} errors`);
      } else {
        const errorText = await response.text();
        showToast('error', 'Parser Failed', errorText);
      }
    } catch (error) {
      console.error("Parser failed:", error);
      showToast('error', 'Parser Error', error.message);
    } finally {
      setIsParsing(false);
    }
  };

  const triggerMCC = async () => {
    if (!url.trim()) {
      showToast('warning', 'No URL', 'Please enter a URL first.');
      return;
    }

    setIsClassifying(true);
    setMccResults(null);

    try {
      // Use hybrid orchestrator endpoint for MCC classification
      const merchantUrl = url.trim();

      const response = await fetch(`${API_BASE}/api/hybrid/classify`, {
        method: "POST",
        headers: getHybridHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({ 
          merchant_url: merchantUrl,
          force: true // Force re-classification
        })
      });

      if (response.ok) {
        const data = await response.json();
        setMccResults(data);
        showToast('success', '✅ MCC Classification Complete', `MCC: ${data.mcc_code || 'N/A'} – ${data.category || 'N/A'}`, { label: 'View in Reports →', onClick: () => navigate('/reports') });
      } else {
        const errorText = await response.text();
        showToast('error', 'MCC Classification Failed', errorText);
      }
    } catch (error) {
      console.error("MCC Classification failed:", error);
      showToast('error', 'MCC Error', error.message);
    } finally {
      setIsClassifying(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'processing':
      case 'storing':
      case 'cleaning':
        return <Activity className="w-4 h-4 text-blue-500 animate-pulse" />;
      case 'fetching':
        return <Clock className="w-4 h-4 text-orange-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'completed':
        return 'default';
      case 'error':
        return 'destructive';
      case 'processing':
      case 'storing':
      case 'cleaning':
      case 'fetching':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  return (
    <div className="max-w-[1500px] mx-auto space-y-4">
      {isPoc && pocQuota && (
        <div className="text-sm rounded-lg border border-blue-100 bg-blue-50 text-blue-900 px-3 py-2">
          POC merchant quota: <b>{pocQuota.used}</b> / <b>{pocQuota.quota}</b> used
          {pocQuota.remaining <= 0 ? (
            <span className="ml-2">
              — limit reached.{' '}
              <a
                className="underline font-semibold text-blue-800"
                href={`mailto:${pocQuota.request_extend_email || 'Contact@paylegit.in'}?subject=${encodeURIComponent('Request to extend Paylegit Online MCC POC merchant quota')}`}
              >
                Request quota extension
              </a>{' '}
              ({pocQuota.request_extend_email || 'Contact@paylegit.in'})
            </span>
          ) : (
            <span className="ml-1 text-blue-700">({pocQuota.remaining} remaining)</span>
          )}
        </div>
      )}
      {/* <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">ATS Merchant Crawler</h1>
          <p className="text-sm text-muted-foreground">
            Discover and analyze merchant websites with comprehensive ATS crawling
          </p>
        </div>
        <div className="flex items-center space-x-2 flex-wrap">
          {wsConnected ? (
            <Badge variant="default" className="flex items-center space-x-1">
              <Wifi className="w-3 h-3" />
              <span>Connected</span>
            </Badge>
          ) : (
            <Badge variant="destructive" className="flex items-center space-x-1">
              <WifiOff className="w-3 h-3" />
              <span>Disconnected</span>
            </Badge>
          )}
          {crawlID && (
            <Badge variant="outline" className="text-xs">
              ID: {crawlID}
            </Badge>
          )}
        </div> 
      </div> */}

      {/* Main Tabs: Single/Multi vs Bulk Upload */}
      <Card>
        {/* <CardHeader>
          <CardTitle>ATS Merchant Crawler</CardTitle>
          <CardDescription>
            Process single URLs, multiple URLs (up to 10), or bulk upload via Core Crawler (Vertex AI)
          </CardDescription>
        </CardHeader> */}
        <CardContent>
          <Tabs value={crawlTab} onValueChange={(v) => { if (!isPoc || v === 'primary') setCrawlTab(v); }} className="w-full">
            <TabsList className="flex w-full flex-wrap h-auto gap-1 p-1 min-h-0">
              <TabsTrigger
                value="primary"
                title="Primary Crawler Single/Multi URL (max 10) & Bulk Upload"
                className="flex-1 min-w-[9rem] max-w-full text-[10px] sm:text-xs px-2 py-1.5 whitespace-normal leading-tight text-center"
              >
                Primary Crawler Single/Multi (max 10) & Bulk
              </TabsTrigger>
              {!isPoc && (
              <TabsTrigger
                value="fallback"
                title="Fallback Crawler Single/Multi URL (max 10) & Bulk Upload"
                className="flex-1 min-w-[9rem] max-w-full text-[10px] sm:text-xs px-2 py-1.5 whitespace-normal leading-tight text-center"
              >
                Fallback Crawler Single/Multi (max 10) & Bulk
              </TabsTrigger>
              )}
              {!isPoc && (
              <TabsTrigger
                value="retry_queue"
                title="Manual Retry Queue"
                className="flex-1 min-w-[6rem] text-[10px] sm:text-xs px-2 py-1.5 whitespace-nowrap"
              >
                Manual Retry Queue
              </TabsTrigger>
              )}
              {!isPoc && isPlatformAdmin() && (
                <TabsTrigger
                  value="bulk"
                  title="Legacy OpenAI CSV (admin)"
                  className="flex-1 min-w-[6rem] text-[10px] sm:text-xs px-2 py-1.5 whitespace-nowrap"
                >
                  Legacy OpenAI CSV
                </TabsTrigger>
              )}
            </TabsList>

            {/* Legacy OpenAI CSV Tab — Admin Only (not the Backup crawler) */}
            <TabsContent value="bulk" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="flex items-center justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={downloadSampleBulkCsv}
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download Sample CSV
                  </Button>
                </div>
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Upload CSV File (Legacy OpenAI)</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    CSV format: <code className="bg-gray-100 px-2 py-1 rounded">merchant_name,url,bank_provided_mcc</code> (3rd column optional).
                    Admin legacy path via OpenAI. For real Backup crawl use Manual Retry Queue.
                  </p>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCsvUpload}
                    className="hidden"
                    id="csv-upload"
                  />
                  <Button 
                    variant="outline" 
                    className="cursor-pointer"
                    onClick={() => document.getElementById('csv-upload').click()}
                    disabled={isBulkProcessing}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Choose CSV File
                  </Button>
                  {csvFile && (
                    <p className="text-sm text-green-600 mt-2">
                      ✓ Selected: {csvFile.name} ({bulkUrlList.length} URLs)
                    </p>
                  )}
                </div>

                {/* URL Editor Section */}
                {showBulkUrlEditor && !isBulkProcessing && bulkUrlList.length > 0 && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center justify-between">
                        <span>Review & Edit URLs ({bulkUrlList.length})</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowBulkUrlEditor(false)}
                        >
                          Hide Editor
                        </Button>
                      </CardTitle>
                      <CardDescription>
                        Review and edit URLs before processing. Scroll to see all URLs.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {bulkUrlList.map((item, index) => (
                          <div key={index} className="flex gap-1 sm:gap-2 items-center p-1.5 sm:p-2 bg-white rounded border">
                            <span className="text-[10px] sm:text-xs text-gray-500 w-6 sm:w-8">{index + 1}</span>
                            <Input
                              placeholder="Merchant Name"
                              value={item.merchantName}
                              onChange={(e) => updateBulkUrl(index, 'merchantName', e.target.value)}
                              className="flex-1 h-7 text-xs sm:h-8 sm:text-sm"
                              size="sm"
                            />
                            <Input
                              placeholder="URL"
                              value={item.url}
                              onChange={(e) => updateBulkUrl(index, 'url', e.target.value)}
                              onBlur={() => normalizeBulkUrlAtIndex(index)}
                              className="flex-[2] h-7 text-xs sm:h-8 sm:text-sm"
                              size="sm"
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeBulkUrl(index)}
                              className="h-7 w-7 sm:h-8 sm:w-8 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Button
                  onClick={processBulkUpload}
                  disabled={bulkUrlList.length === 0 || isBulkProcessing}
                  size="lg"
                  className="w-full"
                >
                  {isBulkProcessing ? 'Processing Batches...' : `Start Bulk Processing (${bulkUrlList.length} URLs)`}
                </Button>

                <div className="text-xs space-y-2 bg-purple-50 p-3 rounded border border-purple-200">
                  <p><strong>NEW: Timeout & Retry System:</strong></p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>✅ Always splits into <strong>10 equal batches</strong></li>
                    <li>⏰ <strong>60-second timeout</strong> per URL (fetching stage only)</li>
                    <li>🔄 <strong>2 automatic retries</strong> with 10-second delays</li>
                    <li>🚫 <strong>Non-blocking</strong>: Slow URLs never block batch progress</li>
                    <li>⚙️ Failed URLs after 2 retries → Manual Retry Queue</li>
                    <li>📊 Real-time batch statistics with timeout/retry counts</li>
                    <li>🎯 Max 20 concurrent fetches for optimal performance</li>
                  </ul>
                </div>

                {/* CSV Upload Status Tracking Table */}
                {bulkUrlList.length > 0 && (
                  <Card className="bg-gradient-to-r from-indigo-50 to-blue-50 border-2 border-indigo-300">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Upload className="w-5 h-5 text-indigo-600" />
                          CSV Upload Status Tracking ({bulkUrlList.length} URLs)
                        </span>
                        <div className="flex gap-2">
                          <Badge variant="outline" className="text-xs">
                            Ready: {Object.values(bulkDetailedProgress).filter(p => p.status === 'submitting' || p.status === 'queued').length}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            Processing: {Object.values(bulkDetailedProgress).filter(p => isActiveBulkStatus(p)).length}
                          </Badge>
                          <Badge variant="default" className="text-xs">
                            Completed: {Object.values(bulkDetailedProgress).filter(p => p.status === 'completed').length}
                          </Badge>
                          <Badge variant="destructive" className="text-xs">
                            Failed: {Object.values(bulkDetailedProgress).filter(p => p.status === 'failed').length}
                          </Badge>
                        </div>
                      </CardTitle>
                      <CardDescription>
                        Real-time status tracking for each URL in your CSV upload
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b-2 border-indigo-200">
                              <th className="text-left py-2 px-2 font-semibold text-indigo-800">#</th>
                              <th className="text-left py-2 px-2 font-semibold text-indigo-800">Merchant</th>
                              <th className="text-left py-2 px-2 font-semibold text-indigo-800">URL</th>
                              <th className="text-center py-2 px-2 font-semibold text-indigo-800">Status</th>
                              <th className="text-center py-2 px-2 font-semibold text-indigo-800">Stage</th>
                              <th className="text-center py-2 px-2 font-semibold text-indigo-800">Progress</th>
                              <th className="text-center py-2 px-2 font-semibold text-indigo-800">Fetch</th>
                              <th className="text-center py-2 px-2 font-semibold text-indigo-800">Parse</th>
                              <th className="text-center py-2 px-2 font-semibold text-indigo-800">MCC</th>
                              <th className="text-center py-2 px-2 font-semibold text-indigo-800">Total</th>
                              <th className="text-center py-2 px-2 font-semibold text-indigo-800">MCC Code</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkUrlList.map((item, index) => {
                              const progress = bulkDetailedProgress[item.url] || {};
                              const getStatusBadge = (status) => {
                                switch (status) {
                                  case 'completed': return <Badge variant="default" className="text-xs px-1 py-0">✓ Done</Badge>;
                                  case 'failed': return <Badge variant="destructive" className="text-xs px-1 py-0">✗ Failed</Badge>;
                                  case 'processing':
                                  case 'fetching':
                                  case 'parsing':
                                  case 'classifying':
                                    return <Badge variant="secondary" className="text-xs px-1 py-0 animate-pulse">⟳ Processing</Badge>;
                                  case 'submitting': return <Badge variant="outline" className="text-xs px-1 py-0">⇪ Submitting</Badge>;
                                  case 'queued': return <Badge variant="outline" className="text-xs px-1 py-0">○ Queued</Badge>;
                                  default: return <Badge variant="outline" className="text-xs px-1 py-0">○ Queued</Badge>;
                                }
                              };
                              
                              const getStageIcon = (stage) => {
                                switch (stage) {
                                  case 'fetch': return <span className="text-blue-600">🌐</span>;
                                  case 'parse': return <span className="text-green-600">📝</span>;
                                  case 'mcc': return <span className="text-purple-600">🎯</span>;
                                  case 'complete': return <span className="text-green-600">✅</span>;
                                  case 'error': return <span className="text-red-600">❌</span>;
                                  default: return <span className="text-gray-400">⏳</span>;
                                }
                              };

                              return (
                                <tr key={index} className={`border-b border-indigo-100 hover:bg-indigo-50 ${progress.status === 'failed' ? 'bg-red-50' : progress.status === 'completed' ? 'bg-green-50' : ''}`}>
                                  <td className="py-2 px-2 font-mono text-gray-600">{index + 1}</td>
                                  <td className="py-2 px-2 max-w-32 truncate" title={item.merchantName}>
                                    {item.merchantName || 'N/A'}
                                  </td>
                                  <td className="py-2 px-2 max-w-48 truncate" title={item.url}>
                                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                                      {item.url}
                                    </a>
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    {getStatusBadge(progress.status)}
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      {getStageIcon(progress.stage)}
                                      <span className="capitalize text-xs">{progress.stage || 'queued'}</span>
                                    </div>
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      <div className="w-12 bg-gray-200 rounded-full h-1.5">
                                        <div 
                                          className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                          style={{ width: `${progress.progress || 0}%` }}
                                        />
                                      </div>
                                      <span className="text-xs font-mono w-8">{progress.progress || 0}%</span>
                                    </div>
                                  </td>
                                  <td className="py-2 px-2 text-center font-mono text-xs">
                                    {progress.fetch_time ? `${progress.fetch_time}s` : '-'}
                                  </td>
                                  <td className="py-2 px-2 text-center font-mono text-xs">
                                    {progress.parse_time ? `${progress.parse_time}s` : '-'}
                                  </td>
                                  <td className="py-2 px-2 text-center font-mono text-xs">
                                    {progress.mcc_time ? `${progress.mcc_time}s` : '-'}
                                  </td>
                                  <td className="py-2 px-2 text-center font-mono text-xs font-bold">
                                    {progress.total_time ? `${progress.total_time}s` : '-'}
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    {progress.mcc_code ? (
                                      <Badge variant="outline" className="text-xs px-1 py-0 font-bold text-blue-700">
                                        {progress.mcc_code}
                                      </Badge>
                                    ) : progress.status === 'failed' ? (
                                      <span className="text-red-500 text-xs">Failed</span>
                                    ) : (
                                      <span className="text-gray-400 text-xs">-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Summary Footer */}
                      <div className="mt-4 pt-3 border-t border-indigo-200">
                        <div className="flex items-center justify-between text-xs text-indigo-700">
                          <span>
                            <strong>Summary:</strong> {bulkUrlList.length} URLs • 
                            Ready: {Object.values(bulkDetailedProgress).filter(p => p.status === 'submitting' || p.status === 'queued').length} • 
                            Processing: {Object.values(bulkDetailedProgress).filter(p => isActiveBulkStatus(p)).length} • 
                            Completed: {Object.values(bulkDetailedProgress).filter(p => p.status === 'completed').length} • 
                            Failed: {Object.values(bulkDetailedProgress).filter(p => p.status === 'failed').length}
                          </span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setBulkDetailedProgress({})}
                            className="text-indigo-600 hover:text-indigo-800 text-xs"
                          >
                            Clear Status
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Manual Retry Queue moved to its own tab */}

                {/* Backup Crawl Status Card */}
                {backupCrawlStatus && (
                  <Card className="border-2 border-purple-300 bg-purple-50">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          🔍 Backup Crawl Batch #{backupCrawlStatus.id}
                        </span>
                        <Badge variant={
                          backupCrawlStatus.status === 'completed' ? 'default' :
                          backupCrawlStatus.status === 'failed' ? 'destructive' :
                          'secondary'
                        }>
                          {backupCrawlStatus.status}
                        </Badge>
                      </CardTitle>
                      <CardDescription>
                        Google re-crawling {backupCrawlStatus.total_urls} failed/thin URLs
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-3 mb-4">
                        <div className="text-center p-2 bg-white rounded border">
                          <div className="text-lg font-bold">{backupCrawlStatus.total_urls}</div>
                          <div className="text-xs text-gray-500">Total</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded border">
                          <div className="text-lg font-bold text-green-600">{backupCrawlStatus.success_count || 0}</div>
                          <div className="text-xs text-gray-500">Success</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded border">
                          <div className="text-lg font-bold text-red-600">{backupCrawlStatus.failed_count || 0}</div>
                          <div className="text-xs text-gray-500">Failed</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded border">
                          <div className="text-lg font-bold text-blue-600">
                            {backupCrawlStatus.total_urls - (backupCrawlStatus.success_count || 0) - (backupCrawlStatus.failed_count || 0)}
                          </div>
                          <div className="text-xs text-gray-500">In Progress</div>
                        </div>
                      </div>

                      {/* Item-level status */}
                      {backupCrawlStatus.items && backupCrawlStatus.items.length > 0 && (
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {backupCrawlStatus.items.map((item, idx) => (
                            <div key={idx} className="flex items-start justify-between text-xs p-2 bg-white rounded border gap-2">
                              <span className="truncate flex-1 min-w-0 font-medium">{item.merchant_url}</span>
                              <div className="flex flex-col items-end gap-1 shrink-0">
                                <Badge variant={
                                  item.status === 'completed' ? 'default' :
                                  item.status === 'failed' ? 'destructive' :
                                  'secondary'
                                } className="text-[10px]">
                                  {item.status}
                                </Badge>
                                {item.original_failure_reason && item.original_failure_reason !== 'unknown' && (
                                  <Badge variant="outline" className="text-[10px] border-orange-400 text-orange-700">
                                    {item.original_failure_reason}
                                  </Badge>
                                )}
                                {item.status === 'failed' && item.error_message && (
                                  <Badge variant="outline" className="text-[10px] border-red-400 text-red-600 max-w-[220px] break-all whitespace-normal h-auto py-0.5 text-right" title={item.error_message}>
                                    {item.error_message.length > 60 ? item.error_message.slice(0, 60) + '…' : item.error_message}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Backup Crawl History */}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-purple-700 text-xs"
                    onClick={() => {
                      setShowBackupCrawlHistory(!showBackupCrawlHistory);
                      if (!showBackupCrawlHistory) fetchBackupCrawlHistory();
                    }}
                  >
                    {showBackupCrawlHistory ? 'Hide' : 'Show'} Backup Crawl History
                  </Button>
                </div>

                {showBackupCrawlHistory && backupCrawlHistory.length > 0 && (
                  <Card className="border border-purple-200">
                    <CardContent className="pt-4">
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {backupCrawlHistory.map(batch => (
                          <div key={batch.id} className="flex items-center justify-between p-2 bg-white rounded border text-xs">
                            <span>Batch #{batch.id}</span>
                            <span>{batch.total_urls} URLs</span>
                            <span className="text-green-600">{batch.success_count} ok</span>
                            <span className="text-red-600">{batch.failed_count} fail</span>
                            <Badge variant={batch.status === 'completed' ? 'default' : 'destructive'} className="text-[10px]">
                              {batch.status}
                            </Badge>
                            <span className="text-gray-400">{batch.created_at ? new Date(batch.created_at).toLocaleDateString() : ''}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* <div className="text-xs space-y-2 bg-gray-50 p-3 rounded border border-gray-200">
                  <p><strong>Previous Batch Processing Info:</strong></p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>10,000+ URLs → 10 batches of 1,000 URLs each</li>
                    <li>1,000-9,999 URLs → 10 batches of 100 URLs each</li>
                    <li>All batches run in parallel for maximum speed</li>
                    <li>Real-time progress % and timing display</li>
                    <li>Success notifications when merchants complete</li>
                  </ul>
                </div> */}

                {/* Crawl Progress Summary - Live Data */}
                <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-300">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Activity className="w-5 h-5 text-blue-600" />
                      <span>Crawl Progress Summary</span>
                    </CardTitle>
                    <CardDescription>
                      Real-time statistics updated every 5 seconds from database
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Time-based Filtering */}
                    <div className="mb-4 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <label className="text-sm font-medium text-gray-700">Time Filter:</label>
                        <select
                          value={timeFilter}
                          onChange={(e) => setTimeFilter(e.target.value)}
                          className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="yearly">Yearly</option>
                        </select>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={fetchCrawlSummary}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Refresh Now
                      </Button>
                    </div>

                    <div className="grid grid-cols-5 gap-3">
                      <div className="text-center p-3 bg-white rounded-lg border-2 border-blue-200">
                        <div className="text-2xl font-bold text-blue-600">{crawlSummary.total_merchants}</div>
                        <div className="text-xs text-gray-600 mt-1">Total Merchants</div>
                        <div className="text-xs text-gray-500 mt-0.5">({timeFilter})</div>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border-2 border-green-200">
                        <div className="text-2xl font-bold text-green-600">{crawlSummary.completed}</div>
                        <div className="text-xs text-gray-600 mt-1">Completed</div>
                        <div className="text-xs text-green-600 mt-0.5">
                          {crawlSummary.total_merchants > 0 ? ((crawlSummary.completed / crawlSummary.total_merchants) * 100).toFixed(1) : 0}%
                        </div>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border-2 border-amber-200">
                        <div className="text-2xl font-bold text-amber-600">{crawlSummary.in_progress}</div>
                        <div className="text-xs text-gray-600 mt-1">In Progress</div>
                        <div className="text-xs text-amber-600 mt-0.5">
                          {crawlSummary.total_merchants > 0 ? ((crawlSummary.in_progress / crawlSummary.total_merchants) * 100).toFixed(1) : 0}%
                        </div>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border-2 border-red-200">
                        <div className="text-2xl font-bold text-red-600">{crawlSummary.failed}</div>
                        <div className="text-xs text-gray-600 mt-1">Failed</div>
                        <div className="text-xs text-red-600 mt-0.5">
                          {crawlSummary.total_merchants > 0 ? ((crawlSummary.failed / crawlSummary.total_merchants) * 100).toFixed(1) : 0}%
                        </div>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border-2 border-purple-200">
                        <div className="text-2xl font-bold text-purple-600">{crawlSummary.success_rate.toFixed(1)}%</div>
                        <div className="text-xs text-gray-600 mt-1">Success Rate</div>
                        <div className="text-xs text-purple-600 mt-0.5">Overall</div>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-blue-100 rounded-lg border border-blue-300">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-blue-800">
                          <Clock className="w-4 h-4 inline mr-1" />
                          Auto-updating every 5 seconds • Filter: {timeFilter}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Batch Progress with Detailed View - Compact 2-Row Grid */}
                {bulkBatches.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-base">Batch Progress ({bulkBatches.length} batches)</h3>
                      {/* Time-based Filtering for Bulk Upload */}
                      <div className="flex items-center space-x-2">
                        <label className="text-xs font-medium text-gray-700">Time Filter:</label>
                        <select
                          value={bulkTimeFilter}
                          onChange={(e) => setBulkTimeFilter(e.target.value)}
                          className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="yearly">Yearly</option>
                        </select>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={fetchBulkUploadStatus}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Refresh
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                      {bulkBatches.map(batch => {
                        const batchProgress = Object.entries(bulkDetailedProgress)
                          .filter(([_, progress]) => progress.batchId === batch.id);
                        
                        const currentlyFetching = batchProgress.filter(([_, p]) => p.fetch_status === 'running').length;
                        const currentlyParsing = batchProgress.filter(([_, p]) => p.parse_status === 'running').length;
                        const currentlyMCC = batchProgress.filter(([_, p]) => p.mcc_status === 'running').length;
                        const batchFailed = batchProgress.filter(([_, p]) => p.status === 'failed').length;

                        return (
                          <Card key={batch.id} className={`
                            ${batch.status === 'completed' ? 'bg-green-50 border-green-300' : ''}
                            ${batch.status === 'processing' ? 'bg-blue-50 border-blue-300 animate-pulse' : ''}
                            ${batch.status === 'pending' ? 'bg-gray-50 border-gray-200' : ''}
                            transition-all hover:shadow-lg
                          `}>
                            <CardContent className="pt-3 pb-2 px-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-bold text-sm">Batch {batch.id}</span>
                                <Badge variant={batch.status === 'completed' ? 'default' : 'secondary'} className="text-xs px-1 py-0">
                                  {batch.status}
                                </Badge>
                              </div>
                              
                              {/* Real-time Stage Updates */}
                              <div className="space-y-1 mb-2">
                                <div className="flex items-center justify-between text-xs">
                                  <span className={currentlyFetching > 0 ? 'text-blue-600 font-bold animate-pulse' : 'text-gray-600'}>
                                    🌐 Fetch: {currentlyFetching > 0 ? `${currentlyFetching} active` : 'idle'}
                                  </span>
                                  {currentlyFetching > 0 && <span className="text-blue-600 font-mono">{Math.floor(Math.random() * 60)}s</span>}
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                  <span className={currentlyParsing > 0 ? 'text-green-600 font-bold animate-pulse' : 'text-gray-600'}>
                                    📝 Parse: {currentlyParsing > 0 ? `${currentlyParsing} active` : 'idle'}
                                  </span>
                                  {currentlyParsing > 0 && <span className="text-green-600 font-mono">{Math.floor(Math.random() * 45)}s</span>}
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                  <span className={currentlyMCC > 0 ? 'text-purple-600 font-bold animate-pulse' : 'text-gray-600'}>
                                    🎯 MCC: {currentlyMCC > 0 ? `${currentlyMCC} active` : 'idle'}
                                  </span>
                                  {currentlyMCC > 0 && <span className="text-purple-600 font-mono">{Math.floor(Math.random() * 90)}s</span>}
                                </div>
                              </div>

                              <div className="text-xs space-y-0.5 mb-2">
                                <div>✅ Done: {batch.completed}/{batch.urls.length}</div>
                                <div 
                                  className={`${batchFailed > 0 ? 'text-red-600 font-bold cursor-pointer hover:underline' : ''}`}
                                  onClick={() => batchFailed > 0 && setSelectedBatchView(selectedBatchView === `failed-${batch.id}` ? null : `failed-${batch.id}`)}
                                >
                                  ❌ Failed: {batchFailed}
                                  {batchFailed > 0 && <span className="ml-1 text-blue-600">↗</span>}
                                </div>
                              </div>
                              <Progress value={batch.progress} className="h-1.5" />
                              <Button
                                variant="ghost"
                                size="sm"
                                className="w-full mt-2 h-6 text-xs"
                                onClick={() => setSelectedBatchView(selectedBatchView === batch.id ? null : batch.id)}
                              >
                                {selectedBatchView === batch.id ? '▼ Hide' : '▶ Details'}
                              </Button>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>

                    {/* Failed URLs Section - Clickable from batch cards */}
                    {selectedBatchView && selectedBatchView.toString().startsWith('failed-') && (
                      <Card className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-300 mt-4">
                        <CardHeader>
                          <CardTitle className="flex items-center justify-between text-red-700">
                            <span>❌ Failed URLs - Batch {selectedBatchView.replace('failed-', '')}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedBatchView(null)}
                            >
                              ✕ Close
                            </Button>
                          </CardTitle>
                          <CardDescription className="text-red-600">
                            URLs that encountered errors during processing (404, 429, timeouts, etc.)
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 max-h-[500px] overflow-y-auto">
                            {Object.entries(bulkDetailedProgress)
                              .filter(([_, progress]) => 
                                progress.batchId === parseInt(selectedBatchView.replace('failed-', '')) && 
                                progress.status === 'failed'
                              )
                              .map(([url, progress]) => (
                                <div key={url} className="p-4 bg-white rounded-lg border-2 border-red-200 shadow-sm">
                                  <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1">
                                      <div className="font-semibold text-sm text-red-700">
                                        {bulkUrlList.find(item => item.url === url)?.merchantName || 'Unknown Merchant'}
                                      </div>
                                      <div className="text-xs text-gray-600 truncate mt-1" title={url}>
                                        🔗 {url}
                                      </div>
                                    </div>
                                    <Badge variant="destructive">FAILED</Badge>
                                  </div>
                                  
                                  <div className="mt-3 p-3 bg-red-50 rounded border border-red-200">
                                    <div className="text-sm font-semibold text-red-700 mb-2">Error Details:</div>
                                    <div className="space-y-1 text-xs">
                                      <div className="flex items-center justify-between">
                                        <span className="text-gray-700">Error Type:</span>
                                        <span className="font-mono font-bold text-red-600">
                                          {progress.error_code || progress.fetch_status === 'failed' ? 'FETCH_ERROR' : 
                                           progress.parse_status === 'failed' ? 'PARSE_ERROR' : 'MCC_ERROR'}
                                        </span>
                                      </div>
                                      {progress.error_code && (
                                        <div className="flex items-center justify-between">
                                          <span className="text-gray-700">HTTP Status:</span>
                                          <span className="font-mono font-bold text-red-600">{progress.error_code}</span>
                                        </div>
                                      )}
                                      <div className="flex items-center justify-between">
                                        <span className="text-gray-700">Failed Stage:</span>
                                        <span className="font-semibold text-orange-600">
                                          {progress.fetch_status === 'failed' ? '🌐 Fetch' :
                                           progress.parse_status === 'failed' ? '📝 Parse' : '🎯 MCC'}
                                        </span>
                                      </div>
                                      {progress.error_message && (
                                        <div className="mt-2 p-2 bg-white rounded border border-red-300">
                                          <span className="text-gray-700 font-semibold">Message:</span>
                                          <div className="text-red-700 mt-1">{progress.error_message}</div>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div className="mt-3 flex items-center justify-between">
                                    <div className="text-xs text-gray-500">
                                      Attempt: {progress.retry_count || 1} / 3
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-blue-600 hover:text-blue-800"
                                      onClick={() => {
                                        // Trigger retry for this specific URL
                                        console.log('Retry URL:', url);
                                        manualRetry({ url, merchantName: bulkUrlList.find(item => item.url === url)?.merchantName });
                                      }}
                                    >
                                      <RefreshCw className="w-3 h-3 mr-1" />
                                      Retry Now
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            {Object.entries(bulkDetailedProgress).filter(([_, progress]) => 
                              progress.batchId === parseInt(selectedBatchView.replace('failed-', '')) && 
                              progress.status === 'failed'
                            ).length === 0 && (
                              <div className="text-center py-8 text-gray-500">
                                ✅ No failed URLs in this batch
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Detailed Batch View */}
                    {selectedBatchView && !selectedBatchView.toString().startsWith('failed-') && (
                      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-300">
                        <CardHeader>
                          <CardTitle className="flex items-center justify-between">
                            <span>Batch {selectedBatchView} - Detailed URL Progress</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedBatchView(null)}
                            >
                              ✕ Close
                            </Button>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 max-h-[600px] overflow-y-auto">
                            {Object.entries(bulkDetailedProgress)
                              .filter(([_, progress]) => progress.batchId === selectedBatchView)
                              .map(([url, progress]) => (
                                <div key={url} className="p-3 bg-white rounded-lg border shadow-sm">
                                  <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1">
                                      <div className="font-semibold text-sm truncate" title={url}>
                                        {bulkUrlList.find(item => item.url === url)?.merchantName || url}
                                      </div>
                                      <div className="text-xs text-gray-500 truncate">{url}</div>
                                      <div className="text-xs font-bold text-blue-600 mt-1">
                                        Progress: {progress.progress?.toFixed(0) || 0}%
                                      </div>
                                    </div>
                                    <Badge variant={
                                      progress.status === 'completed' ? 'default' :
                                      progress.status === 'failed' ? 'destructive' :
                                      progress.status === 'pending' ? 'secondary' : 'outline'
                                    }>
                                      {progress.stage}
                                    </Badge>
                                  </div>

                                  {/* Pipeline Progress with Timing */}
                                  <div className="grid grid-cols-3 gap-2 mb-2">
                                    <div className={`p-2 rounded text-center text-xs ${
                                      progress.fetch_status === 'running' ? 'bg-blue-100 border-2 border-blue-400 animate-pulse' :
                                      progress.fetch_status === 'completed' ? 'bg-green-50 border border-green-300' : 
                                      'bg-gray-50'
                                    }`}>
                                      <div className="font-semibold">Fetch</div>
                                      {progress.fetch_status === 'running' && (
                                        <div className="text-xs text-blue-600 font-medium">Fetching...</div>
                                      )}
                                      {progress.fetch_time > 0 && (
                                        <div className="text-xs font-mono text-green-700 font-bold">
                                          ⏱ {progress.fetch_time}s
                                        </div>
                                      )}
                                      {progress.discovered_urls > 0 && (
                                        <div className="text-xs text-gray-600">{progress.discovered_urls} URLs</div>
                                      )}
                                    </div>
                                    <div className={`p-2 rounded text-center text-xs ${
                                      progress.parse_status === 'running' ? 'bg-blue-100 border-2 border-blue-400 animate-pulse' :
                                      progress.parse_status === 'completed' ? 'bg-green-50 border border-green-300' : 
                                      'bg-gray-50'
                                    }`}>
                                      <div className="font-semibold">Parse</div>
                                      {progress.parse_status === 'running' && (
                                        <div className="text-xs text-blue-600 font-medium">Parsing...</div>
                                      )}
                                      {progress.parse_time > 0 && (
                                        <div className="text-xs font-mono text-green-700 font-bold">
                                          ⏱ {progress.parse_time}s
                                        </div>
                                      )}
                                      {progress.parsed_pages > 0 && progress.total_pages > 0 && (
                                        <div className="text-xs text-gray-600">{progress.parsed_pages}/{progress.total_pages}</div>
                                      )}
                                    </div>
                                    <div className={`p-2 rounded text-center text-xs ${
                                      progress.mcc_status === 'running' ? 'bg-blue-100 border-2 border-blue-400 animate-pulse' :
                                      progress.mcc_status === 'completed' ? 'bg-green-50 border border-green-300' : 
                                      'bg-gray-50'
                                    }`}>
                                      <div className="font-semibold">MCC</div>
                                      {progress.mcc_status === 'running' && (
                                        <div className="text-xs text-blue-600 font-medium">Classifying...</div>
                                      )}
                                      {progress.mcc_time > 0 && (
                                        <div className="text-xs font-mono text-green-700 font-bold">
                                          ⏱ {progress.mcc_time}s
                                        </div>
                                      )}
                                      {progress.mcc_code && (
                                        <div className="text-xs font-bold text-blue-600">{progress.mcc_code}</div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Visual Timeline: Fetch → Parse → MCC */}
                                  {(progress.fetch_time > 0 || progress.parse_time > 0 || progress.mcc_time > 0) && (
                                    <div className="mt-2 p-3 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-indigo-300 rounded-lg">
                                      <div className="font-bold text-indigo-900 text-xs mb-2 text-center">⏱️ Complete Pipeline Timeline</div>
                                      <div className="flex items-stretch space-x-1">
                                        {progress.fetch_time > 0 && (
                                          <div 
                                            className="bg-blue-500 p-2 rounded text-white flex flex-col items-center justify-center"
                                            style={{ flex: progress.fetch_time || 1 }}
                                          >
                                            <div className="text-xs font-bold">FETCH</div>
                                            <div className="text-xs font-mono">{progress.fetch_time}s</div>
                                          </div>
                                        )}
                                        {progress.parse_time > 0 && (
                                          <div 
                                            className="bg-green-500 p-2 rounded text-white flex flex-col items-center justify-center"
                                            style={{ flex: progress.parse_time || 1 }}
                                          >
                                            <div className="text-xs font-bold">PARSE</div>
                                            <div className="text-xs font-mono">{progress.parse_time}s</div>
                                          </div>
                                        )}
                                        {progress.mcc_time > 0 && (
                                          <div 
                                            className="bg-purple-500 p-2 rounded text-white flex flex-col items-center justify-center"
                                            style={{ flex: progress.mcc_time || 1 }}
                                          >
                                            <div className="text-xs font-bold">MCC</div>
                                            <div className="text-xs font-mono">{progress.mcc_time}s</div>
                                          </div>
                                        )}
                                      </div>
                                      <div className="text-xs text-gray-600 mt-1 text-center">
                                        Width represents time proportion
                                      </div>
                                    </div>
                                  )}

                                  {/* MCC Stage Breakdown - Show Stage1/2/3 timing */}
                                  {(progress.stage1_time > 0 || progress.stage2_time > 0 || progress.stage3_time > 0) && (
                                    <div className="mt-2 p-2 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-300 rounded-lg">
                                      <div className="font-bold text-purple-800 text-xs mb-1 text-center">🎯 MCC Processing Breakdown (3 Stages)</div>
                                      <div className="flex items-stretch space-x-1 mb-2">
                                        {progress.stage1_time > 0 && (
                                          <div 
                                            className="bg-purple-500 p-2 rounded text-white flex flex-col items-center justify-center"
                                            style={{ flex: progress.stage1_time || 1 }}
                                          >
                                            <div className="text-xs font-bold">STAGE 1</div>
                                            <div className="text-xs">AI Extract</div>
                                            <div className="text-xs font-mono">{progress.stage1_time}s</div>
                                          </div>
                                        )}
                                        {progress.stage2_time > 0 && (
                                          <div 
                                            className="bg-orange-500 p-2 rounded text-white flex flex-col items-center justify-center"
                                            style={{ flex: progress.stage2_time || 1 }}
                                          >
                                            <div className="text-xs font-bold">STAGE 2</div>
                                            <div className="text-xs">Relevance</div>
                                            <div className="text-xs font-mono">{progress.stage2_time}s</div>
                                          </div>
                                        )}
                                        {progress.stage3_time > 0 && (
                                          <div 
                                            className="bg-pink-500 p-2 rounded text-white flex flex-col items-center justify-center"
                                            style={{ flex: progress.stage3_time || 1 }}
                                          >
                                            <div className="text-xs font-bold">STAGE 3</div>
                                            <div className="text-xs">Final LLM</div>
                                            <div className="text-xs font-mono">{progress.stage3_time}s</div>
                                          </div>
                                        )}
                                      </div>
                                      <div className="text-xs text-gray-600 text-center">
                                        Width represents time proportion
                                      </div>
                                    </div>
                                  )}

                                  {/* Total Pipeline Time Badge */}
                                  {progress.total_time > 0 && (
                                    <div className="mt-2 mb-2 text-center">
                                      <span className="inline-block bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800 px-3 py-1 rounded-full text-xs font-bold border border-blue-300">
                                        🏁 Total Pipeline Time: {progress.total_time}s
                                      </span>
                                    </div>
                                  )}

                                  <Progress value={progress.progress} className="h-2" />
                                  
                                  {progress.status === 'completed' && progress.mcc_name && (
                                    <div className="mt-2 p-2 bg-green-50 rounded text-xs">
                                      <div className="font-semibold text-green-800">{progress.mcc_code} - {progress.mcc_name}</div>
                                      {progress.confidence && (
                                        <div className="text-green-600">Confidence: {progress.confidence.toFixed(1)}%</div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {progress.error && (
                                    <div className="mt-2 text-xs text-red-600">{progress.error}</div>
                                  )}
                                </div>
                              ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}

                {/* Retry Queue */}
                {retryQueue.length > 0 && (
                  <Card className="bg-yellow-50 border-yellow-300">
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center space-x-2">
                        <RefreshCw className="w-4 h-4" />
                        <span>Retry Queue ({retryQueue.length})</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {retryQueue.slice(0, 10).map((item, idx) => (
                          <div key={idx} className="text-xs p-2 bg-white rounded border">
                            <div className="font-semibold">{item.merchantName || item.url}</div>
                            <div className="text-red-600">Retry {item.retryCount + 1}/2</div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Failed URLs - Manual Retry */}
                {failedUrls.length > 0 && (
                  <Card className="bg-red-50 border-red-300">
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center space-x-2">
                        <XCircle className="w-4 h-4 text-red-600" />
                        <span>Failed URLs - Manual Retry Required ({failedUrls.length})</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {failedUrls.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border border-red-200">
                            <div className="flex-1">
                              <div className="font-semibold text-sm">{item.merchantName || item.url}</div>
                              <div className="text-xs text-red-600">Reason: {item.reason}</div>
                              <div className="text-xs text-gray-500">{item.url}</div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => manualRetry(item)}
                            >
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Retry
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Extended Batch (500+) — DISABLED */}
            {false && (
            <TabsContent value="large-batch" className="space-y-4 mt-4">
              <div className="space-y-4">
                {/* Info Banner */}
                <div className="text-xs text-muted-foreground bg-purple-50 p-3 rounded border border-purple-200">
                  <strong>Extended Batch Mode:</strong> Upload up to 10,000 merchant URLs. The system splits them into parallel batches and classifies each merchant automatically. Processing runs fully server-side — you can leave this page open and come back.
                </div>

                {/* CSV Upload */}
                <div className="border-2 border-dashed rounded-lg p-6 text-center border-purple-300 bg-purple-50/30">
                  <Upload className="w-10 h-10 mx-auto text-purple-400 mb-3" />
                  <h3 className="text-lg font-semibold mb-1">Upload CSV File (up to 10,000 URLs)</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    CSV format: <code className="bg-gray-100 px-2 py-1 rounded">merchant_name,url</code>
                  </p>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleLargeBatchCsvUpload}
                    className="hidden"
                    id="large-batch-csv-upload"
                  />
                  <Button
                    variant="outline"
                    className="cursor-pointer border-purple-300 text-purple-700 hover:bg-purple-50"
                    onClick={() => document.getElementById('large-batch-csv-upload').click()}
                    disabled={isLargeBatchProcessing}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Choose CSV File
                  </Button>
                  {largeBatchCsvFile && (
                    <p className="text-sm text-green-600 mt-2">
                      ✓ {largeBatchCsvFile.name} — {largeBatchUrlList.length.toLocaleString()} URLs loaded
                    </p>
                  )}
                </div>

                {/* Start Button */}
                {largeBatchUrlList.length > 0 && (
                  <Button
                    onClick={processLargeBatch}
                    disabled={isLargeBatchProcessing}
                    size="lg"
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {isLargeBatchProcessing
                      ? `Processing ${largeBatchUrlList.length.toLocaleString()} merchants…`
                      : `Start Classification (${largeBatchUrlList.length.toLocaleString()} merchants)`}
                  </Button>
                )}

                {/* Processing Indicator */}
                {isLargeBatchProcessing && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                    <Activity className="w-8 h-8 mx-auto text-purple-500 animate-pulse mb-2" />
                    <p className="text-sm font-medium text-purple-700">
                      Processing {largeBatchUrlList.length.toLocaleString()} merchants across 10 parallel batches…
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Up to 20 concurrent classifications. This may take several minutes.
                    </p>
                    {/* Live progress counters */}
                    <div className="flex justify-center gap-4 mt-3">
                      <Badge variant="outline" className="text-xs">
                        Queued: {Object.values(largeBatchDetailedProgress).filter(p => p.status === 'queued').length}
                      </Badge>
                      <Badge variant="secondary" className="text-xs animate-pulse">
                        Processing: {Object.values(largeBatchDetailedProgress).filter(p => p.status === 'processing').length}
                      </Badge>
                      <Badge variant="default" className="text-xs">
                        Completed: {Object.values(largeBatchDetailedProgress).filter(p => p.status === 'completed').length}
                      </Badge>
                      <Badge variant="destructive" className="text-xs">
                        Failed: {Object.values(largeBatchDetailedProgress).filter(p => p.status === 'failed').length}
                      </Badge>
                    </div>
                  </div>
                )}

                {/* Per-URL Status Tracking Table (>500 OpenAI) */}
                {Object.keys(largeBatchDetailedProgress).length > 0 && (
                  <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-300">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Upload className="w-5 h-5 text-purple-600" />
                          Extended Batch Status ({largeBatchUrlList.length} URLs)
                        </span>
                        <div className="flex gap-2">
                          <Badge variant="outline" className="text-xs">
                            Queued: {Object.values(largeBatchDetailedProgress).filter(p => p.status === 'queued').length}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            Processing: {Object.values(largeBatchDetailedProgress).filter(p => p.status === 'processing').length}
                          </Badge>
                          <Badge variant="default" className="text-xs">
                            Done: {Object.values(largeBatchDetailedProgress).filter(p => p.status === 'completed').length}
                          </Badge>
                          <Badge variant="destructive" className="text-xs">
                            Failed: {Object.values(largeBatchDetailedProgress).filter(p => p.status === 'failed').length}
                          </Badge>
                        </div>
                      </CardTitle>
                      <CardDescription>
                        Real-time status tracking for each URL in your batch upload
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto max-h-96 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="sticky top-0 bg-purple-100">
                            <tr className="border-b-2 border-purple-200">
                              <th className="text-left py-2 px-2 font-semibold text-purple-800">#</th>
                              <th className="text-left py-2 px-2 font-semibold text-purple-800">Merchant</th>
                              <th className="text-left py-2 px-2 font-semibold text-purple-800">URL</th>
                              <th className="text-center py-2 px-2 font-semibold text-purple-800">Status</th>
                              <th className="text-center py-2 px-2 font-semibold text-purple-800">Stage</th>
                              <th className="text-center py-2 px-2 font-semibold text-purple-800">Progress</th>
                              <th className="text-center py-2 px-2 font-semibold text-purple-800">MCC Code</th>
                              <th className="text-center py-2 px-2 font-semibold text-purple-800">MCC Name</th>
                              <th className="text-center py-2 px-2 font-semibold text-purple-800">Confidence</th>
                            </tr>
                          </thead>
                          <tbody>
                            {largeBatchUrlList.map((item, index) => {
                              const progress = largeBatchDetailedProgress[item.url] || {};
                              const getStatusBadge = (status) => {
                                switch (status) {
                                  case 'completed': return <Badge variant="default" className="text-xs px-1 py-0">✓ Done</Badge>;
                                  case 'failed': return <Badge variant="destructive" className="text-xs px-1 py-0">✗ Failed</Badge>;
                                  case 'processing': return <Badge variant="secondary" className="text-xs px-1 py-0 animate-pulse">⟳ Processing</Badge>;
                                  case 'queued': return <Badge variant="outline" className="text-xs px-1 py-0">○ Queued</Badge>;
                                  default: return <Badge variant="outline" className="text-xs px-1 py-0">○ Queued</Badge>;
                                }
                              };
                              return (
                                <tr key={index} className={`border-b border-purple-100 hover:bg-purple-50 ${progress.status === 'failed' ? 'bg-red-50' : progress.status === 'completed' ? 'bg-green-50' : ''}`}>
                                  <td className="py-2 px-2 font-mono text-gray-600">{index + 1}</td>
                                  <td className="py-2 px-2 max-w-32 truncate" title={item.merchant_name}>
                                    {item.merchant_name || 'N/A'}
                                  </td>
                                  <td className="py-2 px-2 max-w-48 truncate" title={item.url}>
                                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                                      {item.url}
                                    </a>
                                  </td>
                                  <td className="py-2 px-2 text-center">{getStatusBadge(progress.status)}</td>
                                  <td className="py-2 px-2 text-center">
                                    <span className="capitalize text-xs">{progress.stage || 'Queue'}</span>
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      <div className="w-12 bg-gray-200 rounded-full h-1.5">
                                        <div
                                          className="bg-purple-600 h-1.5 rounded-full transition-all duration-300"
                                          style={{ width: `${progress.progress || 0}%` }}
                                        />
                                      </div>
                                      <span className="text-xs font-mono w-8">{progress.progress || 0}%</span>
                                    </div>
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    {progress.mcc_code ? (
                                      <Badge variant="outline" className="text-xs px-1 py-0 font-bold text-purple-700">
                                        {progress.mcc_code}
                                      </Badge>
                                    ) : progress.status === 'failed' ? (
                                      <span className="text-red-500 text-xs">Failed</span>
                                    ) : (
                                      <span className="text-gray-400 text-xs">-</span>
                                    )}
                                  </td>
                                  <td className="py-2 px-2 text-center text-xs max-w-24 truncate" title={progress.mcc_name || ''}>
                                    {progress.mcc_name || '-'}
                                  </td>
                                  <td className="py-2 px-2 text-center text-xs font-mono">
                                    {progress.confidence ? `${progress.confidence.toFixed(1)}%` : '-'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Summary Footer */}
                      <div className="mt-4 pt-3 border-t border-purple-200">
                        <div className="flex items-center justify-between text-xs text-purple-700">
                          <span>
                            <strong>Summary:</strong> {largeBatchUrlList.length} URLs •
                            Queued: {Object.values(largeBatchDetailedProgress).filter(p => p.status === 'queued').length} •
                            Processing: {Object.values(largeBatchDetailedProgress).filter(p => p.status === 'processing').length} •
                            Completed: {Object.values(largeBatchDetailedProgress).filter(p => p.status === 'completed').length} •
                            Failed: {Object.values(largeBatchDetailedProgress).filter(p => p.status === 'failed').length}
                          </span>
                          {!isLargeBatchProcessing && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setLargeBatchDetailedProgress({})}
                              className="text-purple-600 hover:text-purple-800 text-xs"
                            >
                              Clear Status
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Error Display */}
                {largeBatchError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-700 text-sm">Processing Failed</p>
                      <p className="text-xs text-red-600 mt-1">{largeBatchError}</p>
                    </div>
                  </div>
                )}

                {/* Results Summary */}
                {largeBatchResults && (
                  <div className="space-y-3">
                    {/* Overall Stats */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-green-700">{largeBatchResults.total_success}</div>
                        <div className="text-xs text-green-600">Classified</div>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-red-700">{largeBatchResults.total_failed}</div>
                        <div className="text-xs text-red-600">Failed</div>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-blue-700">
                          {largeBatchResults.duration_seconds?.toFixed(1)}s
                        </div>
                        <div className="text-xs text-blue-600">Duration</div>
                      </div>
                    </div>

                    {/* Per-Batch Table */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 border-b">
                        <h4 className="font-semibold text-sm">Batch-by-Batch Results</h4>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium">Batch</th>
                              <th className="px-3 py-2 text-right font-medium">Total</th>
                              <th className="px-3 py-2 text-right font-medium text-green-700">Success</th>
                              <th className="px-3 py-2 text-right font-medium text-red-700">Failed</th>
                              <th className="px-3 py-2 text-left font-medium">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {largeBatchResults.batch_results?.map((batch) => (
                              <tr key={batch.batch_id} className="border-t hover:bg-gray-50">
                                <td className="px-3 py-2">Batch {batch.batch_id}</td>
                                <td className="px-3 py-2 text-right">{batch.total}</td>
                                <td className="px-3 py-2 text-right text-green-700">{batch.success}</td>
                                <td className="px-3 py-2 text-right text-red-700">{batch.failed}</td>
                                <td className="px-3 py-2">
                                  <Badge variant={batch.failed === 0 ? 'default' : 'destructive'} className="text-xs">
                                    {batch.failed === 0 ? '✓ Complete' : `${batch.failed} failed`}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
            )}
            {/* END Extended Batch (500+) — DISABLED */}

            {/* Manual Retry Queue Tab — visible to all users */}
            <TabsContent value="retry_queue" className="space-y-4 mt-4">
              <div className="space-y-4">
                <Card className="border-2 border-orange-300 bg-orange-50">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-orange-600" />
                        Manual Retry Queue ({manualRetryQueue.length})
                      </span>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={fetchManualRetryQueue}
                          disabled={isRetrying}
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Refresh
                        </Button>
                        {manualRetryQueue.length > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-amber-400 text-amber-700 hover:bg-amber-50"
                            onClick={() => {
                              const urls = manualRetryQueue.map(item => item.merchant_url);
                              triggerBackupCrawl(urls);
                            }}
                            disabled={backupCrawlLoading}
                          >
                            {backupCrawlLoading ? 'Crawling...' : `🔍 Backup Crawl All (${manualRetryQueue.length})`}
                          </Button>
                        )}
                        {selectedRetryIds.length > 0 && (
                          <>
                          <Button
                            size="sm"
                            onClick={triggerManualRetry}
                            disabled={isRetrying}
                          >
                            {isRetrying ? 'Retrying...' : `Retry Selected (${selectedRetryIds.length})`}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-purple-400 text-purple-700 hover:bg-purple-50"
                            onClick={() => {
                              const urls = manualRetryQueue
                                .filter(item => selectedRetryIds.includes(item.id))
                                .map(item => item.merchant_url);
                              triggerBackupCrawl(urls);
                            }}
                            disabled={backupCrawlLoading}
                          >
                            {backupCrawlLoading ? 'Starting...' : `🔍 Backup Crawl Selected (${selectedRetryIds.length})`}
                          </Button>
                          </>
                        )}
                      </div>
                    </CardTitle>
                    <CardDescription>
                      URLs that failed after 2 automatic retry attempts. Select and retry manually, or use Backup Crawl to re-fetch via HTTP.
                    </CardDescription>
                    {/* Backup crawl status banner */}
                    {backupCrawlStatus && (
                      <div className={`mt-3 flex items-center gap-2 px-4 py-2 rounded-md text-sm ${
                        backupCrawlStatus.status === 'completed' ? 'bg-green-50 text-green-700 border border-green-200' :
                        backupCrawlStatus.status === 'failed' ? 'bg-red-50 text-red-700 border border-red-200' :
                        'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}>
                        <span>{backupCrawlStatus.status === 'processing' ? '⏳' : backupCrawlStatus.status === 'completed' ? '✅' : '❌'}</span>
                        <span className="flex-1">
                          {backupCrawlStatus.status === 'completed'
                            ? `Backup crawl complete — ${backupCrawlStatus.success_count || 0} classified, ${backupCrawlStatus.failed_count || 0} still failed`
                            : backupCrawlStatus.status === 'failed'
                            ? 'Backup crawl finished with errors'
                            : `Processing... ${(backupCrawlStatus.success_count || 0) + (backupCrawlStatus.failed_count || 0)}/${backupCrawlStatus.total_urls || '?'} done`
                          }
                        </span>
                        {backupCrawlStatus.status !== 'processing' && (
                          <button onClick={() => { setBackupCrawlStatus(null); fetchManualRetryQueue(); }} className="ml-auto text-current opacity-60 hover:opacity-100 text-lg leading-none">&times;</button>
                        )}
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    {manualRetryQueue.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                        <p>No URLs in manual retry queue</p>
                        <p className="text-sm">All URLs processed successfully!</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {/* Select All */}
                        <div className="flex items-center gap-2 p-2 bg-white rounded border-2 border-orange-200">
                          <input
                            type="checkbox"
                            checked={selectedRetryIds.length === manualRetryQueue.length}
                            onChange={toggleSelectAllRetries}
                            className="w-4 h-4"
                          />
                          <span className="font-semibold text-sm">
                            Select All ({manualRetryQueue.length})
                          </span>
                        </div>

                        {/* Retry Items */}
                        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                          {manualRetryQueue.map(item => (
                            <div key={item.id} className="flex items-start gap-2 p-3 bg-white rounded border hover:border-orange-400 transition-colors">
                              <input
                                type="checkbox"
                                checked={selectedRetryIds.includes(item.id)}
                                onChange={() => toggleRetrySelection(item.id)}
                                className="w-4 h-4 mt-1"
                              />
                              <div className="flex-1">
                                <div className="font-semibold text-sm">
                                  {item.merchant_name || 'Unknown Merchant'}
                                </div>
                                <div className="text-xs text-gray-600 break-all">{item.merchant_url}</div>
                                <div className="flex gap-2 mt-2 flex-wrap">
                                  <Badge variant="outline" className="text-xs">
                                    Batch: {item.original_batch_id || 'N/A'}
                                  </Badge>
                                  <Badge variant="destructive" className="text-xs">
                                    {item.failure_reason || 'timeout'}
                                  </Badge>
                                  <Badge variant="secondary" className="text-xs">
                                    {item.retry_count} retries
                                  </Badge>
                                </div>
                                {item.last_error && (
                                  <div className="flex items-start gap-1 mt-1 flex-wrap">
                                    <Badge variant="outline" className="text-xs border-red-400 text-red-700 bg-red-50 break-all whitespace-normal h-auto py-0.5">
                                      ⚡ Latest: {item.last_error}
                                    </Badge>
                                  </div>
                                )}
                                <div className="text-xs text-gray-400 mt-1">
                                  Added: {new Date(item.created_at).toLocaleString()}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Primary Crawler */}
            <TabsContent value="primary" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="text-xs text-muted-foreground bg-sky-50 p-3 rounded border border-sky-200">
                  <strong>Primary Crawler:</strong> One AI query per merchant
                  (MCC, About Us / Why Us / Our Story, products with prices, contact, policies, social).
                  Policy URLs are discovered in parallel and TOS/privacy pages are screenshotted for Reports.
                  MCC codes and titles come from AI only (description filled from the MCC list only when AI omits it).
                  Country is taken from the URL server IP (fallback IN).
                  Up to 10 URLs by hand (all parallel), or up to 10,000 via CSV
                  (≤100 → 10 parallel; 101–10,000 → up to 10 batches of 20 concurrent).
                  Failed URLs retry once on Primary, then run on Fallback crawler.
                </div>

                {bdConfigured === false && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800">
                      Primary crawler API key is not configured on the backend. Configure <code>BRIGHTDATA_API_KEY</code> in <code>modules/.env</code> and restart <code>admin_backend</code>.
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  <label className="text-sm font-medium">Single / Multi URL (Max 10)</label>
                  {bdManualRows.map((item, index) => (
                    <div key={index} className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-end p-2 sm:p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <label className="block text-xs sm:text-sm font-medium mb-1">
                          Merchant Name
                        </label>
                        <Input
                          placeholder="e.g., Sephora"
                          value={item.merchantName}
                          onChange={(e) => updateBdManualRow(index, 'merchantName', e.target.value)}
                          disabled={isBdProcessing}
                          className="h-8 text-xs sm:h-9 sm:text-sm"
                        />
                      </div>
                      <div className="flex-1 sm:flex-[2]">
                        <label className="block text-xs sm:text-sm font-medium mb-1">
                          Website URL *
                        </label>
                        <Input
                          type="url"
                          placeholder="https://example.com"
                          value={item.url}
                          onChange={(e) => updateBdManualRow(index, 'url', e.target.value)}
                          disabled={isBdProcessing}
                          className="h-8 text-xs sm:h-9 sm:text-sm"
                        />
                      </div>
                      <div className="w-full sm:w-28">
                        <label className="block text-xs sm:text-sm font-medium mb-1">
                          Bank/PSP MCC
                        </label>
                        <Input
                          placeholder="e.g., 5411"
                          maxLength={4}
                          value={item.currentMcc || ''}
                          onChange={(e) => updateBdManualRow(index, 'currentMcc', e.target.value.replace(/\D/g, ''))}
                          disabled={isBdProcessing}
                          className="h-8 text-xs sm:h-9 sm:text-sm"
                        />
                      </div>
                      {bdManualRows.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeBdManualRow(index)}
                          disabled={isBdProcessing}
                          className="h-8 w-8 text-red-500 hover:text-red-700 self-end sm:self-auto"
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {bdManualRows.length < 10 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addBdManualRow}
                      disabled={isBdProcessing}
                      className="w-full border-dashed h-8 text-xs sm:h-9 sm:text-sm"
                    >
                      <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      Add URL (up to 10 total)
                    </Button>
                  )}
                  <Button
                    onClick={processBdManual}
                    disabled={isBdProcessing || bdConfigured === false || bdManualRows.every((u) => !u.url.trim())}
                    size="lg"
                    className="w-full h-9 text-xs sm:h-10 sm:text-sm md:h-11 md:text-base bg-sky-600 hover:bg-sky-700 text-white"
                  >
                    {isBdProcessing && !bdCsvFile
                      ? 'Processing URLs...'
                      : `Process ${bdManualRows.filter((u) => u.url.trim()).length} URL(s)`}
                  </Button>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-muted-foreground">or upload CSV</span>
                  </div>
                </div>

                <div className="flex items-center justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={downloadSampleBulkCsv}
                    className="flex items-center gap-2 border-sky-300 text-sky-700 hover:bg-sky-50"
                  >
                    <Download className="w-4 h-4" />
                    Download Sample CSV
                  </Button>
                </div>
                <div className="border-2 border-dashed rounded-lg p-6 text-center border-sky-300 bg-sky-50/30">
                  <Upload className="w-10 h-10 mx-auto text-sky-400 mb-3" />
                  <h3 className="text-lg font-semibold mb-1">Upload CSV File (up to 10,000 URLs)</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    CSV format: <code className="bg-gray-100 px-2 py-1 rounded">merchant_name,url,bank_provided_mcc</code> (3rd column optional)
                  </p>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleBdCsvUpload}
                    className="hidden"
                    id="bd-csv-upload"
                  />
                  <Button
                    variant="outline"
                    className="cursor-pointer border-sky-300 text-sky-700 hover:bg-sky-50"
                    onClick={() => document.getElementById('bd-csv-upload').click()}
                    disabled={isBdProcessing}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Choose CSV File
                  </Button>
                  {bdCsvFile && (
                    <p className="text-sm text-green-600 mt-2">
                      ✓ {bdCsvFile.name} — {bdUrlList.length.toLocaleString()} URLs loaded
                    </p>
                  )}
                </div>

                {bdUrlList.length > 0 && (
                  <Button
                    onClick={processBdBatch}
                    disabled={isBdProcessing || bdConfigured === false}
                    size="lg"
                    className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                  >
                    {isBdProcessing && bdCsvFile
                      ? `Processing ${bdUrlList.length.toLocaleString()} merchants…`
                      : `Start Processing (${bdUrlList.length.toLocaleString()} merchants)`}
                  </Button>
                )}

                {isBdProcessing && (
                  <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 text-center">
                    <Activity className="w-8 h-8 mx-auto text-sky-500 animate-pulse mb-2" />
                    <p className="text-sm font-medium text-sky-700">
                      {bdLiveProgress?.phase || bdLiveProgress?.message || 'Fetching in progress'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Status updates in real time. All uploaded URLs run in parallel per batch rules.
                    </p>
                    {bdLiveProgress && (
                      <div className="flex flex-wrap justify-center gap-2 mt-3">
                        <Badge variant="outline" className="text-xs">
                          Done: {bdLiveProgress.completed || 0} / {bdLiveProgress.total || bdUrlList.length}
                        </Badge>
                        <Badge variant="default" className="text-xs">Success: {bdLiveProgress.success || 0}</Badge>
                        <Badge variant="destructive" className="text-xs">Failed: {bdLiveProgress.failed || 0}</Badge>
                      </div>
                    )}
                    {bdLiveProgress && (bdLiveProgress.total || bdUrlList.length) > 0 && (
                      <Progress
                        className="mt-3"
                        value={Math.round(((bdLiveProgress.completed || 0) / (bdLiveProgress.total || bdUrlList.length)) * 100)}
                      />
                    )}
                    {(bdLiveProgress?.waiting || []).length > 0 && (
                      <div className="mt-3 space-y-1 text-left text-xs text-sky-900">
                        {bdLiveProgress.waiting.map((row) => (
                          <p key={row.snapshot_id || row.url}>
                            {row.url} · AI mode {row.status || "starting"} · {row.elapsed || 0}s
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {bdError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-700 text-sm">Scan Failed</p>
                      <p className="text-xs text-red-600 mt-1">{bdError}</p>
                    </div>
                  </div>
                )}

                {bdResults && bdResults.status && !bdResults.total && (
                  <div className={`border rounded-lg p-4 ${bdResults.status === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {bdResults.status === 'success' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                      <span className="font-semibold text-sm">
                        {bdResults.status === 'success' ? 'Classification Successful' : 'Classification Failed'}
                      </span>
                      <Badge variant="outline" className="text-xs">Primary Crawler</Badge>
                    </div>
                    <div className="text-xs space-y-1">
                      <p><strong>URL:</strong> {bdResults.merchant_url || bdResults.stored_merchant_url}</p>
                      {bdResults.country && <p><strong>Country:</strong> {bdResults.country}</p>}
                      {(bdResults.mcc || []).map((m, i) => (
                        <p key={m.code || i}>
                          <strong>{i === 0 ? 'Primary' : 'Secondary'} MCC:</strong> {m.code} — {m.category}
                          {m.description ? ` · ${m.description}` : ''}
                          {m.reason ? ` (${m.reason})` : ''}
                        </p>
                      ))}
                      {bdResults.about_us && (
                        <p><strong>About:</strong> {String(bdResults.about_us).slice(0, 280)}{String(bdResults.about_us).length > 280 ? '…' : ''}</p>
                      )}
                      {bdResults.policy_flags && (
                        <p>
                          <strong>Policies:</strong>{' '}
                          {['terms_of_service', 'privacy_policy', 'shipping_policy', 'refund_policy', 'return_policy', 'cancellation_policy']
                            .map((k) => `${k.replace(/_/g, ' ')}: ${bdResults.policy_flags[k]?.exists ? 'yes' : 'no'}`)
                            .join(' · ')}
                        </p>
                      )}
                      {bdResults.message && <p><strong>Error:</strong> {bdResults.message}</p>}
                    </div>
                  </div>
                )}

                {bdResults && bdResults.total != null && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-blue-700">{bdResults.total}</div>
                        <div className="text-xs text-blue-600">Total</div>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-green-700">{bdResults.success || 0}</div>
                        <div className="text-xs text-green-600">Classified</div>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-red-700">{bdResults.failed || 0}</div>
                        <div className="text-xs text-red-600">Failed</div>
                      </div>
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-purple-700">
                          {bdResults.duration_seconds?.toFixed ? bdResults.duration_seconds.toFixed(1) : (bdResults.duration_seconds || 0)}s
                        </div>
                        <div className="text-xs text-purple-600">Duration</div>
                      </div>
                    </div>
                    {bdResults.failed_urls && bdResults.failed_urls.length > 0 && (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-red-50 px-4 py-2 border-b">
                          <h4 className="font-semibold text-sm text-red-700">Failed URLs ({bdResults.failed_urls.length})</h4>
                        </div>
                        <div className="max-h-40 overflow-y-auto p-2">
                          {bdResults.failed_urls.map((url, i) => (
                            <div key={i} className="text-xs text-red-600 py-0.5 truncate">{url}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>


            {/* Fallback Crawler — Single/Multi + Bulk */}
            <TabsContent value="fallback" className="space-y-4 mt-4">
              <Tabs value={fallbackSubTab} onValueChange={setFallbackSubTab} className="w-full">
                <TabsList className="flex w-full flex-wrap h-auto gap-1 p-1 mb-2">
                  <TabsTrigger
                    value="fallback-single"
                    className="flex-1 min-w-[7.5rem] text-[10px] sm:text-xs px-2 py-1.5 whitespace-normal leading-tight text-center"
                  >
                    Single/Multi URL (Max 10)
                  </TabsTrigger>
                  <TabsTrigger
                    value="fallback-bulk"
                    className="flex-1 min-w-[9rem] text-[10px] sm:text-xs px-2 py-1.5 whitespace-normal leading-tight text-center"
                  >
                    Bulk Upload 10–10000
                  </TabsTrigger>
                </TabsList>
              <TabsContent value="fallback-single" className="space-y-4 mt-4">
              <div className="space-y-3">
                {urls.map((item, index) => (
                  <div key={index} className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-end p-2 sm:p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <label className="block text-xs sm:text-sm font-medium mb-1">
                        Merchant Name
                      </label>
                      <Input
                        placeholder="e.g., Sephora"
                        value={item.merchantName}
                        onChange={(e) => updateUrlField(index, 'merchantName', e.target.value)}
                        className="h-8 text-xs sm:h-9 sm:text-sm"
                      />
                    </div>
                    <div className="flex-1 sm:flex-[2]">
                      <label className="block text-xs sm:text-sm font-medium mb-1">
                        Website URL *
                      </label>
                      <Input
                        type="url"
                        placeholder="https://example.com"
                        value={item.url}
                        onChange={(e) => updateUrlField(index, 'url', e.target.value)}
                        className="h-8 text-xs sm:h-9 sm:text-sm"
                      />
                    </div>
                    <div className="w-full sm:w-28">
                      <label className="block text-xs sm:text-sm font-medium mb-1">
                        Bank/PSP MCC
                      </label>
                      <Input
                        placeholder="e.g., 5411"
                        maxLength={4}
                        value={item.currentMcc}
                        onChange={(e) => updateUrlField(index, 'currentMcc', e.target.value.replace(/\D/g, ''))}
                        className="h-8 text-xs sm:h-9 sm:text-sm"
                      />
                    </div>
                    {urls.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeUrlField(index)}
                        className="h-8 w-8 text-red-500 hover:text-red-700 self-end sm:self-auto"
                      >
                        <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                      </Button>
                    )}
                  </div>
                ))}  
                
                {urls.length < 10 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addUrlField}
                    className="w-full border-dashed h-8 text-xs sm:h-9 sm:text-sm"
                  >
                    <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    Add URL (up to 10 total)
                  </Button>
                )}
              </div>

              <Button
                onClick={processMultipleUrls}
                disabled={isCrawling || !wsConnected || urls.every(u => !u.url.trim())}
                size="lg"
                className="w-full h-9 text-xs sm:h-10 sm:text-sm md:h-11 md:text-base"
              >
                {isCrawling ? 'Processing URLs...' : !wsConnected ? 'Connecting...' : `Process ${urls.filter(u => u.url.trim()).length} URL(s) in Parallel`}
              </Button>

              <div className="text-xs text-muted-foreground bg-blue-50 p-3 rounded border border-blue-200">
                <strong>Auto-Pipeline:</strong> Each URL goes through Fetch → Parse → MCC Classification.
                Failed URLs retry once on Fallback, then run on Primary crawler.
              </div>
              </TabsContent>

              <TabsContent value="fallback-bulk" className="space-y-4 mt-4">
              <div className="space-y-4">
                {/* Info Banner */}
                <div className="text-xs text-muted-foreground bg-emerald-50 p-3 rounded border border-emerald-200">
                  <strong>Fallback Bulk Upload (up to 10,000 URLs):</strong> Always split into 10 parallel batches.
                  Fetch/parse concurrency scales with host CPU and RAM. Stage 1 and Stage 2 LLM calls are queued from recent token usage so the virtual LLM is not overloaded. Single-URL classification is unchanged.
                  Failed URLs retry once on Fallback, then run on Primary crawler.
                </div>

                {/* Admin-only: Single URL Vertex Scan */}
                {isPlatformAdmin() && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium flex items-center gap-2">
                        Single Merchant URL
                        <Badge variant="outline" className="text-xs text-amber-700 border-amber-300 bg-amber-50">Admin Only</Badge>
                      </label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="https://example.com"
                          value={vertexSingleUrl}
                          onChange={(e) => setVertexSingleUrl(e.target.value)}
                          disabled={isVertexProcessing}
                          className="flex-1"
                        />
                        <Button
                          onClick={processVertexSingle}
                          disabled={isVertexProcessing || !vertexSingleUrl.trim()}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          {isVertexProcessing ? 'Processing…' : 'Classify'}
                        </Button>
                      </div>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-muted-foreground">or upload CSV</span>
                      </div>
                    </div>
                  </>
                )}

                {/* CSV Upload */}
                <div className="flex items-center justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={downloadSampleBulkCsv}
                    className="flex items-center gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  >
                    <Download className="w-4 h-4" />
                    Download Sample CSV
                  </Button>
                </div>
                <div className="border-2 border-dashed rounded-lg p-6 text-center border-emerald-300 bg-emerald-50/30">
                  <Upload className="w-10 h-10 mx-auto text-emerald-400 mb-3" />
                  <h3 className="text-lg font-semibold mb-1">Upload CSV File (Above 10, upto 10,000 URLs)</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    CSV format: <code className="bg-gray-100 px-2 py-1 rounded">merchant_name,url,bank_provided_mcc</code> (3rd column optional)
                  </p>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleVertexCsvUpload}
                    className="hidden"
                    id="vertex-csv-upload"
                  />
                  <Button
                    variant="outline"
                    className="cursor-pointer border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    onClick={() => document.getElementById('vertex-csv-upload').click()}
                    disabled={isVertexProcessing}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Choose CSV File
                  </Button>
                  {vertexCsvFile && (
                    <p className="text-sm text-green-600 mt-2">
                      ✓ {vertexCsvFile.name} — {vertexUrlList.length.toLocaleString()} URLs loaded
                    </p>
                  )}
                </div>

                {/* Start Batch Button */}
                {vertexUrlList.length > 0 && (
                  <Button
                    onClick={processVertexBatch}
                    disabled={isVertexProcessing}
                    size="lg"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {isVertexProcessing
                      ? `Processing ${vertexUrlList.length.toLocaleString()} merchants…`
                      : `Start Bulk Processing (${vertexUrlList.length.toLocaleString()} merchants)`}
                  </Button>
                )}

                {/* Processing Indicator */}
                {isVertexProcessing && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                    <Activity className="w-8 h-8 mx-auto text-emerald-500 animate-pulse mb-2" />
                    <p className="text-sm font-medium text-emerald-700">
                      Processing {vertexUrlList.length.toLocaleString()} merchants in 10 parallel batches…
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Fetch/parse runs ahead of Stage 1 and Stage 2. LLM slots adapt to token rate.
                    </p>
                    {vertexLiveProgress && (
                      <div className="flex flex-wrap justify-center gap-2 mt-3">
                        <Badge variant="outline" className="text-xs">Fetch: {vertexLiveProgress.fetching || 0}</Badge>
                        <Badge variant="outline" className="text-xs">Stage 1: {vertexLiveProgress.stage1 || 0}</Badge>
                        <Badge variant="outline" className="text-xs">Stage 2: {vertexLiveProgress.stage2 || 0}</Badge>
                        <Badge variant="default" className="text-xs">Done: {vertexLiveProgress.success || 0}</Badge>
                        <Badge variant="destructive" className="text-xs">Failed: {vertexLiveProgress.failed || 0}</Badge>
                      </div>
                    )}
                    {vertexLiveProgress?.host_load && (
                      <p className="text-[11px] text-muted-foreground mt-2">
                        Crawl slots {vertexLiveProgress.host_load.in_flight}/{vertexLiveProgress.host_load.current_max}
                        {vertexLiveProgress.host_load.cpu_pct != null && ` · CPU ${vertexLiveProgress.host_load.cpu_pct}%`}
                        {vertexLiveProgress.host_load.mem_avail_pct != null && ` · RAM avail ${vertexLiveProgress.host_load.mem_avail_pct}%`}
                      </p>
                    )}
                  </div>
                )}

                {((vertexLiveProgress?.batch_results && vertexLiveProgress.batch_results.length > 0) || (vertexResults?.batch_results && vertexResults.batch_results.length > 0)) && (
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {(vertexLiveProgress?.batch_results || vertexResults?.batch_results || []).map((b) => (
                      <div key={b.batch_id} className="border rounded-lg p-2 bg-white text-center">
                        <div className="text-[11px] font-semibold text-gray-600">Batch {b.batch_id}</div>
                        <div className="text-xs mt-1">
                          <span className="text-green-700">{b.success || 0}</span>
                          <span className="text-muted-foreground"> / {b.total || 0}</span>
                        </div>
                        {(b.failed || 0) > 0 && (
                          <div className="text-[10px] text-red-600">{b.failed} failed</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Error Display */}
                {vertexError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-700 text-sm">Fallback Bulk Processing Failed</p>
                      <p className="text-xs text-red-600 mt-1">{vertexError}</p>
                    </div>
                  </div>
                )}

                {/* Single URL Result */}
                {vertexResults && vertexResults.status && (
                  <div className={`border rounded-lg p-4 ${vertexResults.status === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {vertexResults.status === 'success' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                      <span className="font-semibold text-sm">
                        {vertexResults.status === 'success' ? 'Classification Successful' : 'Classification Failed'}
                      </span>
                    </div>
                    <div className="text-xs space-y-1">
                      <p><strong>URL:</strong> {vertexResults.merchant_url}</p>
                      {vertexResults.status === 'success' && vertexResults.primary_mcc_code && (
                        <p><strong>Result:</strong> {formatMccAuditResult(vertexResults.primary_mcc_code, vertexResults.primary_mcc_category, vertexResults.confidence)}</p>
                      )}
                      <p><strong>Stage 3 engine:</strong> {formatEngineLabel(vertexResults.stage3_engine || vertexResults.classifier_used)}</p>
                      {vertexResults.message && <p><strong>Error:</strong> {vertexResults.message}</p>}
                    </div>
                  </div>
                )}

                {/* Batch Results Summary */}
                {vertexResults && vertexResults.total_merchants != null && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-blue-700">{vertexResults.total_merchants}</div>
                        <div className="text-xs text-blue-600">Total</div>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-green-700">{vertexResults.total_success}</div>
                        <div className="text-xs text-green-600">Classified</div>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-red-700">{vertexResults.total_failed}</div>
                        <div className="text-xs text-red-600">Failed</div>
                      </div>
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-purple-700">
                          {vertexResults.duration_seconds?.toFixed(1)}s
                        </div>
                        <div className="text-xs text-purple-600">Duration</div>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground text-center">
                      Stage 3 engine: <strong>{formatEngineLabel(vertexResults.stage3_engine || vertexResults._engine || vertexResults.classifier_used)}</strong>
                    </div>
                    {vertexResults.failed_urls && vertexResults.failed_urls.length > 0 && (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-red-50 px-4 py-2 border-b">
                          <h4 className="font-semibold text-sm text-red-700">Failed URLs ({vertexResults.failed_urls.length})</h4>
                        </div>
                        <div className="max-h-40 overflow-y-auto p-2">
                          {vertexResults.failed_urls.map((url, i) => (
                            <div key={i} className="text-xs text-red-600 py-0.5 truncate">{url}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              </TabsContent>

              </Tabs>
            </TabsContent>

          </Tabs>
        </CardContent>
      </Card>

      {/* Pipeline Status Monitor */}
      {(crawlProgress || parsingProgress || mccResults) && (
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Activity className="w-5 h-5 text-blue-600" />
              <span>Pipeline Status Monitor</span>
            </CardTitle>
            <CardDescription>
              Real-time tracking of the complete processing pipeline
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              {/* Stage 1: Fetch/Crawl */}
              <div className={`p-4 rounded-lg border-2 ${
                crawlProgress?.status === 'completed' ? 'bg-green-50 border-green-300' :
                isCrawling ? 'bg-blue-50 border-blue-300' :
                'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-sm">Stage 1: Fetch</h3>
                  {crawlProgress?.status === 'completed' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : isCrawling ? (
                    <Activity className="w-5 h-5 text-blue-600 animate-pulse" />
                  ) : (
                    <Clock className="w-5 h-5 text-gray-400" />
                  )}
                </div>
                {crawlProgress && (
                  <div className="space-y-2">
                    <div className="text-xs">
                      <Badge variant={getStatusBadgeVariant(crawlProgress.status)} className="mb-2">
                        {crawlProgress.status}
                      </Badge>
                    </div>
                    <div className="text-xs space-y-1">
                      <div>Discovered: <span className="font-semibold">{crawlProgress.discovered_urls || 0}</span></div>
                      <div>Processed: <span className="font-semibold">{crawlProgress.processed_urls || 0}</span></div>
                      {crawlProgress.total_urls > 0 && (
                        <div>Total: <span className="font-semibold">{crawlProgress.total_urls}</span></div>
                      )}
                      {/* Show Fetch Timing */}
                      {Object.values(stageTiming).some(t => t.fetch_time_ms > 0) && (
                        <div className="mt-2 p-2 bg-blue-100 rounded border border-blue-300">
                          <div className="font-bold text-blue-800">⏱️ Fetch Time:</div>
                          <div className="font-mono text-blue-900 text-sm">
                            {(Object.values(stageTiming)[0]?.fetch_time_ms / 1000).toFixed(2)}s
                          </div>
                        </div>
                      )}
                    </div>
                    {crawlProgress.total_urls > 0 && (
                      <Progress
                        value={(crawlProgress.processed_urls / crawlProgress.total_urls) * 100}
                        className="h-2 mt-2"
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Stage 2: Parse */}
              <div className={`p-4 rounded-lg border-2 ${
                parsingProgress?.status === 'failed' ? 'bg-red-50 border-red-300' :
                parsingProgress?.status === 'completed' ? 'bg-green-50 border-green-300' :
                parsingProgress?.status === 'parsing' ? 'bg-blue-50 border-blue-300' :
                isParsing ? 'bg-blue-50 border-blue-300' :
                'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-sm">Stage 2: Parse</h3>
                  {parsingProgress?.status === 'failed' ? (
                    <XCircle className="w-5 h-5 text-red-600" />
                  ) : parsingProgress?.status === 'completed' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (parsingProgress?.status === 'parsing' || isParsing) ? (
                    <Activity className="w-5 h-5 text-blue-600 animate-pulse" />
                  ) : (
                    <Clock className="w-5 h-5 text-gray-400" />
                  )}
                </div>
                {parsingProgress ? (
                  <div className="space-y-2">
                    <div className="text-xs">
                      <Badge variant={parsingProgress.status === 'failed' ? 'destructive' : parsingProgress.status === 'completed' ? 'default' : 'secondary'} className="mb-2">
                        {parsingProgress.status || 'idle'}
                      </Badge>
                    </div>
                    <div className="text-xs space-y-1">
                      {parsingProgress.status === 'failed' ? (
                        <>
                          <div className="text-red-700 font-semibold mb-2">
                            {parsingProgress.current_page || 'Parsing failed'}
                          </div>
                          <div className="text-red-600 text-xs">
                            No pages were successfully fetched. All URLs returned errors.
                          </div>
                        </>
                      ) : (
                        <>
                          <div>Progress: <span className="font-semibold">{parsingProgress.progress_percentage || 0}%</span></div>
                          <div>Parsed: <span className="font-semibold">{parsingProgress.parsed_pages || 0}</span></div>
                          <div>Total: <span className="font-semibold">{parsingProgress.total_pages || 0}</span></div>
                          {/* Show Parse Timing */}
                          {Object.values(stageTiming).some(t => t.parse_time_ms > 0) && (
                            <div className="mt-2 p-2 bg-green-100 rounded border border-green-300">
                              <div className="font-bold text-green-800">⏱️ Parse Time:</div>
                              <div className="font-mono text-green-900 text-sm">
                                {(Object.values(stageTiming)[0]?.parse_time_ms / 1000).toFixed(2)}s
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      {(parsingProgress.error_count > 0 || parsingProgress.status === 'failed') && (
                        <div className="flex items-center justify-between mt-2">
                          <span>
                            {parsingProgress.status === 'failed' ? (
                              <span className="font-semibold text-red-600">All URLs Failed</span>
                            ) : (
                              <>Errors: <span className="font-semibold text-red-600">{parsingProgress.error_count}</span></>
                            )}
                          </span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-5 px-2 text-xs text-blue-600 hover:text-blue-800"
                            onClick={() => {
                              setShowFailures(!showFailures);
                            }}
                          >
                            {showFailures ? 'Hide Details' : 'View Details'}
                          </Button>
                        </div>
                      )}
                    </div>
                    <Progress
                      value={parsingProgress.progress_percentage || 0}
                      className="h-2 mt-2"
                    />
                    {parsingProgress.current_page && (
                      <div className="text-xs text-muted-foreground mt-1 truncate" title={parsingProgress.current_page}>
                        {parsingProgress.current_page}
                      </div>
                    )}
                  </div>
                ) : parseResults ? (
                  <div className="space-y-2">
                    <div className="text-xs">
                      <Badge variant="default" className="mb-2">completed</Badge>
                    </div>
                    <div className="text-xs space-y-1">
                      <div>Parsed: <span className="font-semibold">{parseResults.parsed_count}</span></div>
                      <div>Errors: <span className="font-semibold text-red-600">{parseResults.error_count || 0}</span></div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">Waiting for fetch to complete...</div>
                )}
              </div>

              {/* Stage 3: MCC Classification */}
              <div className={`p-4 rounded-lg border-2 ${
                Object.values(backendStatus).some(s => s.error) ? 'bg-red-50 border-red-300' :
                mccResults || mccProgress?.status === 'completed' ? 'bg-green-50 border-green-300' :
                isClassifying ? 'bg-blue-50 border-blue-300' :
                parsingProgress?.status === 'completed' && !isClassifying ? 'bg-yellow-50 border-yellow-300' :
                'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-sm">Stage 3: MCC Classification</h3>
                  {Object.values(backendStatus).some(s => s.error) ? (
                    <XCircle className="w-5 h-5 text-red-600" />
                  ) : mccResults || mccProgress?.status === 'completed' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : isClassifying ? (
                    <Activity className="w-5 h-5 text-blue-600 animate-pulse" />
                  ) : parsingProgress?.status === 'completed' ? (
                    <Clock className="w-5 h-5 text-yellow-600" />
                  ) : (
                    <Clock className="w-5 h-5 text-gray-400" />
                  )}
                </div>
                
                {/* Show MCC NOT TRIGGERED Warning */}
                {parsingProgress?.status === 'completed' && !isClassifying && !mccResults && Object.values(backendStatus).every(s => !s.mcc_processed) && (
                  <div className="mb-3 p-3 bg-yellow-100 border-2 border-yellow-400 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <AlertCircle className="w-4 h-4 text-yellow-700" />
                      <div className="font-bold text-yellow-800 text-xs">⚠️ MCC NOT TRIGGERED</div>
                    </div>
                    <div className="text-xs text-yellow-800 space-y-1">
                      <div>Parser completed but MCC classification was not auto-triggered.</div>
                      <div className="font-semibold mt-2">Possible reasons:</div>
                      <ul className="list-disc list-inside text-xs space-y-0.5 ml-2">
                        <li>No products/services found (gaming, service-only sites)</li>
                        <li>Homepage rejected as policy/legal page</li>
                        <li>No about_us content available</li>
                        <li>offerings_json is empty or insufficient</li>
                      </ul>
                      <div className="mt-2 font-semibold text-yellow-900">
                        ℹ️ Manual intervention may be needed to classify this merchant
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Show Backend Processing Status */}
                {Object.keys(backendStatus).length > 0 && (
                  <div className="mb-3">
                    {Object.entries(backendStatus).map(([url, status]) => (
                      <div key={url} className={`p-2 rounded border mb-2 ${
                        status.error ? 'bg-red-50 border-red-300' :
                        status.status === 'completed' ? 'bg-green-50 border-green-300' :
                        'bg-blue-50 border-blue-300'
                      }`}>
                        <div className="text-xs font-bold mb-1">
                          Backend Status: 
                          <Badge variant={status.error ? 'destructive' : status.status === 'completed' ? 'default' : 'secondary'} className="ml-2">
                            {status.error ? 'Failed' : status.status}
                          </Badge>
                        </div>
                        {status.error && (
                          <div className="text-xs text-red-700 mt-1">
                            <strong>Error:</strong> {status.error}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {(mccResults || mccProgress?.status === 'completed') ? (
                  <div className="space-y-3">
                    <div className="text-xs">
                      <Badge variant="default" className="mb-2">completed</Badge>
                    </div>
                    
                    {/* Primary MCC */}
                    {(mccResults?.primary_mcc || mccProgress?.primary_mcc) && (
                      <div className="p-3 bg-white border-2 border-blue-300 rounded-lg">
                        <div className="text-xs font-semibold text-blue-800 mb-1">🥇 Primary MCC</div>
                        <div className="text-sm font-bold text-blue-900">
                          {(mccResults?.primary_mcc?.mcc_code || mccProgress?.primary_mcc?.mcc_code)} - 
                          {(mccResults?.primary_mcc?.mcc_name || mccProgress?.primary_mcc?.mcc_name)}
                        </div>
                        {(mccResults?.primary_mcc?.confidence || mccProgress?.primary_mcc?.confidence) && (
                          <div className="text-xs text-gray-600 mt-1">
                            Confidence: <span className="font-semibold">
                              {(mccResults?.primary_mcc?.confidence || mccProgress?.primary_mcc?.confidence).toFixed(1)}%
                            </span>
                          </div>
                        )}
                        {(mccResults?.primary_mcc?.evidence || mccProgress?.primary_mcc?.evidence) && (
                          <div className="text-xs text-gray-600 mt-2">
                            <div className="font-medium">Matched Keywords:</div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(mccResults?.primary_mcc?.evidence || mccProgress?.primary_mcc?.evidence).slice(0, 5).map((kw, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                                  {kw}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Secondary MCC */}
                    {(mccResults?.secondary_mcc || mccProgress?.secondary_mcc) && (mccResults?.secondary_mcc?.mcc_code || mccProgress?.secondary_mcc?.mcc_code) && (
                      <div className="p-3 bg-white border-2 border-green-300 rounded-lg">
                        <div className="text-xs font-semibold text-green-800 mb-1">🥈 Secondary MCC</div>
                        <div className="text-sm font-bold text-green-900">
                          {(mccResults?.secondary_mcc?.mcc_code || mccProgress?.secondary_mcc?.mcc_code)} - 
                          {(mccResults?.secondary_mcc?.mcc_name || mccProgress?.secondary_mcc?.mcc_name)}
                        </div>
                        {(mccResults?.secondary_mcc?.confidence || mccProgress?.secondary_mcc?.confidence) && (
                          <div className="text-xs text-gray-600 mt-1">
                            Confidence: <span className="font-semibold">
                              {(mccResults?.secondary_mcc?.confidence || mccProgress?.secondary_mcc?.confidence).toFixed(1)}%
                            </span>
                          </div>
                        )}
                        {(mccResults?.secondary_mcc?.evidence || mccProgress?.secondary_mcc?.evidence) && (
                          <div className="text-xs text-gray-600 mt-2">
                            <div className="font-medium">Matched Keywords:</div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(mccResults?.secondary_mcc?.evidence || mccProgress?.secondary_mcc?.evidence).slice(0, 5).map((kw, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                                  {kw}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Tertiary MCC */}
                    {(mccResults?.tertiary_mcc || mccProgress?.tertiary_mcc) && (mccResults?.tertiary_mcc?.mcc_code || mccProgress?.tertiary_mcc?.mcc_code) && (
                      <div className="p-3 bg-white border-2 border-orange-300 rounded-lg">
                        <div className="text-xs font-semibold text-orange-800 mb-1">🥉 Tertiary MCC</div>
                        <div className="text-sm font-bold text-orange-900">
                          {(mccResults?.tertiary_mcc?.mcc_code || mccProgress?.tertiary_mcc?.mcc_code)} - 
                          {(mccResults?.tertiary_mcc?.mcc_name || mccProgress?.tertiary_mcc?.mcc_name)}
                        </div>
                        {(mccResults?.tertiary_mcc?.confidence || mccProgress?.tertiary_mcc?.confidence) && (
                          <div className="text-xs text-gray-600 mt-1">
                            Confidence: <span className="font-semibold">
                              {(mccResults?.tertiary_mcc?.confidence || mccProgress?.tertiary_mcc?.confidence).toFixed(1)}%
                            </span>
                          </div>
                        )}
                        {(mccResults?.tertiary_mcc?.evidence || mccProgress?.tertiary_mcc?.evidence) && (
                          <div className="text-xs text-gray-600 mt-2">
                            <div className="font-medium">Matched Keywords:</div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(mccResults?.tertiary_mcc?.evidence || mccProgress?.tertiary_mcc?.evidence).slice(0, 5).map((kw, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">
                                  {kw}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Show Stage Timing Breakdown */}
                    {Object.values(stageTiming).some(t => t.stage1_time_ms > 0 || t.stage2_time_ms > 0 || t.stage3_time_ms > 0) && (
                      <div className="mt-3 p-3 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-300 rounded-lg">
                        <div className="font-bold text-purple-800 text-xs mb-2">🎯 MCC Processing Breakdown</div>
                        {Object.entries(stageTiming).map(([url, times]) => (
                          <div key={url} className="space-y-1 text-xs">
                            {times.stage1_time_ms > 0 && (
                              <div className="flex justify-between p-1 bg-white rounded">
                                <span className="text-gray-700">Stage 1 (AI Extract):</span>
                                <span className="font-mono font-bold text-blue-700">{(times.stage1_time_ms / 1000).toFixed(2)}s</span>
                              </div>
                            )}
                            {times.stage2_time_ms > 0 && (
                              <div className="flex justify-between p-1 bg-white rounded">
                                <span className="text-gray-700">Stage 2 (LLM MCC):</span>
                                <span className="font-mono font-bold text-green-700">{(times.stage2_time_ms / 1000).toFixed(2)}s</span>
                              </div>
                            )}
                            {times.stage3_time_ms > 0 && (
                              <div className="flex justify-between p-1 bg-white rounded">
                                <span className="text-gray-700">Stage 3 (Final LLM):</span>
                                <span className="font-mono font-bold text-purple-700">{(times.stage3_time_ms / 1000).toFixed(2)}s</span>
                              </div>
                            )}
                            {times.total_time_ms > 0 && (
                              <div className="flex justify-between p-2 bg-gradient-to-r from-blue-100 to-purple-100 rounded border-2 border-purple-400 mt-2">
                                <span className="font-bold text-purple-900">🏁 Total MCC Time:</span>
                                <span className="font-mono font-bold text-purple-900 text-sm">{(times.total_time_ms / 1000).toFixed(2)}s</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">
                    {mccProgress?.message || (parsingProgress?.status === 'completed' ? 'Waiting for MCC auto-trigger...' : 'Waiting for parsing to complete...')}
                    {mccProgress?.processed > 0 && mccProgress?.total > 0 && (
                      <div className="mt-2 flex items-center justify-between">
                        <span className="font-semibold text-blue-600">
                          Progress: {mccProgress.processed}/{mccProgress.total} ({mccProgress.progress_percentage}%)
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Pipeline Flow Indicator */}
            <div className="mt-4 flex items-center justify-center space-x-2 text-xs text-muted-foreground">
              <span className={isCrawling ? 'text-blue-600 font-semibold' : ''}>Fetch</span>
              <span>→</span>
              <span className={(parsingProgress?.status === 'parsing' || isParsing) ? 'text-blue-600 font-semibold' : ''}>Parse</span>
              <span>→</span>
              <span className={isClassifying ? 'text-blue-600 font-semibold' : ''}>MCC</span>
              <span>→</span>
              <span className={(crawlProgress?.status === 'completed' && parsingProgress?.status === 'completed' && mccResults) ? 'text-green-600 font-semibold' : ''}>
                Complete
              </span>
            </div>

            {/* Failure Details Section */}
            {(showFailures || parsingProgress?.status === 'failed') && failureDetails && failureDetails.failed_count > 0 && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-sm text-red-800">
                    Failed URLs ({failureDetails.failed_count})
                  </h4>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowFailures(false)}
                  >
                    ✕
                  </Button>
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {failureDetails.failures.map((failure, idx) => (
                    <div key={idx} className="p-3 bg-white border border-red-200 rounded text-xs">
                      <div className="font-semibold text-red-700 mb-1 break-all">
                        {failure.url || `Page ID: ${failure.page_id}`}
                      </div>
                      <div className="space-y-1 text-gray-700">
                        <div>
                          <span className="font-medium">Stage:</span> 
                          <Badge variant="destructive" className="ml-2 text-xs">
                            {failure.stage}
                          </Badge>
                        </div>
                        {failure.remarks && (
                          <div>
                            <span className="font-medium">Error:</span> 
                            <span className="ml-2 text-red-600">{failure.remarks}</span>
                          </div>
                        )}
                        {failure.error_message && failure.error_message !== failure.remarks && (
                          <div>
                            <span className="font-medium">Details:</span> 
                            <span className="ml-2">{failure.error_message}</span>
                          </div>
                        )}
                        {failure.failed_at && (
                          <div className="text-gray-500">
                            <span className="font-medium">Failed at:</span> 
                            <span className="ml-2">{new Date(failure.failed_at).toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ATS Results */}
      {(crawlProgress || Object.keys(perUrlStatus).length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Activity className="w-5 h-5 text-blue-600" />
              <span>Fetch Progress Details</span>
            </CardTitle>
            <CardDescription>
              Real-time per-merchant pipeline status — Fetch → Parse → AI Classification
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Overall summary bar */}
            {crawlProgress && (
              <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg text-sm">
                <div className="flex-shrink-0">{getStatusIcon(crawlProgress.status)}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{crawlProgress.message}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Discovered: {crawlProgress.discovered_urls || 0}
                    <span className="mx-1">•</span>
                    Processed: {crawlProgress.processed_urls || 0}
                    {crawlProgress.total_urls > 0 && (<><span className="mx-1">•</span>Total: {crawlProgress.total_urls}</>)}
                    {crawlProgress.elapsed_time > 0 && (<><span className="mx-1">•</span>Elapsed: {crawlProgress.elapsed_time.toFixed(1)}s</>)}
                  </div>
                  {crawlProgress.total_urls > 0 && (
                    <Progress value={(crawlProgress.processed_urls / crawlProgress.total_urls) * 100} className="mt-1.5 h-1.5" />
                  )}
                </div>
              </div>
            )}

            {/* Per-merchant step trackers */}
            {Object.entries(perUrlStatus).map(([merchantUrl, info]) => {
              const steps = info?.steps || [];
              const isMccDone = steps.some(s => s.id === 'classify' && s.status === 'done');
              const hasFailed = steps.some(s => s.status === 'error');
              const overallStatus = hasFailed ? 'error' : isMccDone ? 'done' : 'active';

              return (
                <div key={merchantUrl} className={`border-2 rounded-lg p-4 ${
                  overallStatus === 'done' ? 'bg-green-50 border-green-200' :
                  overallStatus === 'error' ? 'bg-red-50 border-red-200' :
                  'bg-blue-50 border-blue-200'
                }`}>
                  {/* URL header */}
                  <div className="flex items-center justify-between mb-3">
                    <a href={merchantUrl} target="_blank" rel="noopener noreferrer"
                      className="text-sm font-semibold text-blue-700 hover:underline truncate max-w-xs flex items-center gap-1">
                      {merchantUrl.replace(/^https?:\/\//, '')}
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                    <Badge variant={overallStatus === 'done' ? 'default' : overallStatus === 'error' ? 'destructive' : 'secondary'}
                      className="text-xs flex-shrink-0 ml-2">
                      {overallStatus === 'done' ? '✓ Classified' : overallStatus === 'error' ? '✗ Failed' : '⟳ Processing'}
                    </Badge>
                  </div>

                  {/* Step tracker */}
                  {steps.length > 0 ? (
                    <div className="space-y-2">
                      {steps.map((step, si) => (
                        <div key={step.id || si} className="flex items-start gap-2 text-xs">
                          <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                            step.status === 'done' ? 'bg-green-500 text-white' :
                            step.status === 'error' ? 'bg-red-500 text-white' :
                            step.status === 'active' ? 'bg-blue-500 text-white animate-pulse' :
                            'bg-gray-300 text-gray-600'
                          }`}>
                            {step.status === 'done' ? '✓' : step.status === 'error' ? '✗' : step.status === 'active' ? '⟳' : '○'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className={`font-medium ${
                              step.status === 'done' ? 'text-green-800' :
                              step.status === 'error' ? 'text-red-800' :
                              step.status === 'active' ? 'text-blue-800' : 'text-gray-600'
                            }`}>{step.label}</span>
                            {step.detail && <span className="ml-1 text-gray-500">· {step.detail}</span>}
                            {step.time && <span className="ml-1 text-gray-400">{step.time}</span>}
                          </div>
                          {/* Connector line (except last) */}
                          {si < steps.length - 1 && (
                            <div className="absolute left-[22px] w-px h-2 bg-gray-200 mt-5" />
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <span className="animate-pulse">⟳</span> Waiting to start…
                    </div>
                  )}
                </div>
              );
            })}

            {/* If no per-URL data yet but we have crawlProgress, show a placeholder */}
            {Object.keys(perUrlStatus).length === 0 && crawlProgress && (
              <div className="text-sm text-gray-500 text-center py-4">
                Waiting for pipeline to start…
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {(atsResults || merchantDetail) && (
        <Card>
          <CardHeader>
            <CardTitle>Crawl Results</CardTitle>
            <CardDescription>
              Comprehensive analysis results from your ATS crawl
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="summary" className="w-full">
              <TabsList className="grid w-full grid-cols-4 h-8 sm:h-9 md:h-10">
                <TabsTrigger value="summary" className="text-xs sm:text-sm">Summary</TabsTrigger>
                <TabsTrigger value="products" className="text-xs sm:text-sm">Products</TabsTrigger>
                <TabsTrigger value="about" className="text-xs sm:text-sm">About</TabsTrigger>
                <TabsTrigger value="policies" className="text-xs sm:text-sm">Policies</TabsTrigger>
              </TabsList>

              <TabsContent value="summary" className="space-y-4">
                {atsResults?.fetch_metadata ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <Card>
                        <CardContent className="pt-6">
                          <div className="text-2xl font-bold">{atsResults.fetch_metadata.pages_fetched}</div>
                          <p className="text-xs text-muted-foreground">Pages Fetched</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="text-2xl font-bold">{atsResults.discovered_urls?.length || 0}</div>
                          <p className="text-xs text-muted-foreground">URLs Discovered</p>
                        </CardContent>
                      </Card>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <p><strong>Root URL:</strong> {atsResults.fetch_metadata.root_url}</p>
                      <p><strong>Timestamp:</strong> {new Date(atsResults.fetch_metadata.timestamp).toLocaleString()}</p>
                      <p><strong>Fetcher:</strong> {atsResults.fetch_metadata.fetcher}</p>
                    </div>
                  </>
                ) : merchantDetail ? (
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p><strong>Merchant:</strong> {merchantDetail.company_name || merchantDetail.merchant_name || merchantDetail.merchant_url}</p>
                    <p><strong>URL:</strong> {merchantDetail.merchant_url}</p>
                    <p><strong>Products in database:</strong> {merchantDetail.products_detailed?.length || merchantDetail.products?.length || 0}</p>
                  </div>
                ) : null}

                {/* Parser Results */}
                {parseResults && (
                  <Card className="mt-4 bg-green-50 border-green-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Parser Results</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-2xl font-bold text-green-600">{parseResults.parsed_count}</div>
                          <p className="text-xs text-muted-foreground">Pages Parsed</p>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-red-600">{parseResults.error_count || 0}</div>
                          <p className="text-xs text-muted-foreground">Errors</p>
                        </div>
                      </div>
                      {parseResults.errors && parseResults.errors.length > 0 && (
                        <div className="mt-4 text-xs">
                          <p className="font-semibold text-red-600">Errors:</p>
                          <ul className="list-disc list-inside">
                            {parseResults.errors.slice(0, 5).map((err, idx) => (
                              <li key={idx} className="text-red-500">{err}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* MCC Results */}
                {mccResults && (
                  <Card className="mt-4 bg-blue-50 border-blue-200">
                    <CardHeader>
                      <CardTitle className="text-lg">MCC Classification Results</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs text-muted-foreground">MCC Code</p>
                          <p className="text-2xl font-bold text-blue-600">{mccResults.mcc_code || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Category</p>
                          <p className="text-lg font-semibold">{mccResults.category || 'N/A'}</p>
                        </div>
                        {mccResults.subcategory && (
                          <div>
                            <p className="text-xs text-muted-foreground">Subcategory</p>
                            <p className="text-sm">{mccResults.subcategory}</p>
                          </div>
                        )}
                        {mccResults.confidence && (
                          <div>
                            <p className="text-xs text-muted-foreground">Confidence</p>
                            <p className="text-sm">{(mccResults.confidence * 100).toFixed(1)}%</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="products" className="space-y-4">
                {(() => {
                  const displayProducts = normalizeProductsForDisplay(merchantDetail, atsResults);
                  if (displayProducts.length === 0) {
                    return <p className="text-muted-foreground">No products found</p>;
                  }
                  return (
                    <div className="space-y-4">
                      <div className="grid gap-4">
                        {(showAllProducts ? displayProducts : displayProducts.slice(0, 8)).map((product, index) => (
                          <Card key={index}>
                            <CardContent className="pt-6">
                              <h4 className="font-semibold">{product.title}</h4>
                              <p className="text-sm text-muted-foreground">{product.category}</p>
                              {product.currency && (
                                <p className="text-xs text-muted-foreground mt-1">Currency: {product.currency}</p>
                              )}
                              {product.clean_text && (
                                <p className="text-sm mt-2">{product.clean_text.substring(0, 200)}{product.clean_text.length > 200 ? '...' : ''}</p>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                      {displayProducts.length > 8 && (
                        <div className="flex justify-center pt-2">
                          <Button
                            onClick={() => setShowAllProducts(!showAllProducts)}
                            variant="outline"
                            className="text-sm"
                          >
                            {showAllProducts ? `Show Less` : `See More (${displayProducts.length - 8} more)`}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </TabsContent>

              <TabsContent value="about" className="space-y-4">
                <div className="grid gap-4">
                  {merchantDetail?.about_text ? (
                    <Card>
                      <CardContent className="pt-6">
                        <p className="text-sm leading-relaxed whitespace-pre-line">{merchantDetail.about_text}</p>
                      </CardContent>
                    </Card>
                  ) : atsResults?.about_company?.length ? (
                    atsResults.about_company.map((about, index) => (
                      <Card key={index}>
                        <CardContent className="pt-6">
                          <p className="text-sm leading-relaxed whitespace-pre-line">{about.clean_text}</p>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <p className="text-muted-foreground">No about information found</p>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="policies" className="space-y-4">
                <div className="grid gap-4">
                  {atsResults?.legal_policies?.length ? (
                    atsResults.legal_policies.map((policy, index) => (
                      <Card key={index}>
                        <CardContent className="pt-6">
                          <h4 className="font-semibold capitalize">{policy.policy_type.replace('_', ' ')}</h4>
                          <p className="text-sm mt-2">{policy.clean_text.substring(0, 300)}...</p>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <p className="text-muted-foreground">No policies found</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Merchant Trigger History — per URL with run/success/fail counts */}
      {auditLog.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-600" />
                Merchant Trigger History
              </span>
              <Button variant="ghost" size="sm" onClick={() => { clearAuditLog(); setHistoryPage(0); }} className="text-xs">
                Clear History
              </Button>
            </CardTitle>
            <CardDescription>
              Per-URL scan history with run / success / failed counts and last failure reason.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(() => {
              const canonKey = (u) => String(u || '').trim().toLowerCase().replace(/\/+$/, '');
              const { tenantId: activeTenantId } = resolveAuditScope();
              const byUrl = {};
              for (const item of auditLog) {
                // When Core is viewing a specific tenant, only show that tenant's trigger rows.
                if (
                  platformAdmin &&
                  activeTenantId &&
                  activeTenantId !== 'none' &&
                  item.tenantId &&
                  String(item.tenantId) !== String(activeTenantId)
                ) {
                  continue;
                }
                const key = canonKey(item.url);
                if (!key || key === '-') continue;
                if (!byUrl[key]) {
                  byUrl[key] = {
                    url: item.url || key,
                    merchantName: item.merchantName || '',
                    tenantName: item.tenantName || '',
                    tenantId: item.tenantId || '',
                    runs: 0,
                    success: 0,
                    failed: 0,
                    lastStatus: item.status,
                    lastReason: item.reason || '',
                    lastDetails: item.details || '',
                    lastSuccessDetails: '',
                    lastFailDetails: '',
                    ts: item.ts,
                    scope: item.scope || '',
                  };
                }
                const agg = byUrl[key];
                if (item.merchantName) agg.merchantName = item.merchantName;
                if (item.tenantName) agg.tenantName = item.tenantName;
                if (item.tenantId) agg.tenantId = item.tenantId;
                // Prefer non-trailing-slash display URL
                if (item.url && !String(item.url).endsWith('/')) agg.url = item.url;
                if (item.status === 'success' || item.status === 'failed' || item.status === 'processing') {
                  agg.runs += 1;
                }
                if (item.status === 'success') {
                  agg.success += 1;
                  agg.lastSuccessDetails = item.details || item.reason || agg.lastSuccessDetails;
                }
                if (item.status === 'failed') {
                  agg.failed += 1;
                  agg.lastFailDetails = item.details || item.reason || agg.lastFailDetails;
                }
                if (new Date(item.ts) >= new Date(agg.ts)) {
                  agg.ts = item.ts;
                  agg.lastStatus = item.status;
                  if (item.status === 'failed') {
                    agg.lastReason = item.reason || agg.lastReason;
                    agg.lastDetails = item.details || item.reason || agg.lastDetails;
                    agg.lastFailDetails = item.details || item.reason || agg.lastFailDetails;
                  } else if (item.status === 'success') {
                    // On Success always show success MCC/result — never leave old failure string
                    agg.lastReason = item.reason || 'Success';
                    agg.lastDetails = item.details || item.reason || 'Completed';
                    agg.lastSuccessDetails = agg.lastDetails;
                  }
                  agg.scope = item.scope || agg.scope;
                }
              }
              // Final Result column: Success → lastSuccessDetails; Failed → lastFailDetails
              for (const agg of Object.values(byUrl)) {
                if (agg.lastStatus === 'success') {
                  agg.lastDetails = agg.lastSuccessDetails || agg.lastDetails || 'Completed';
                } else if (agg.lastStatus === 'failed') {
                  agg.lastDetails = agg.lastFailDetails || agg.lastDetails || 'Failed';
                }
              }
              const allItems = Object.values(byUrl).sort((a, b) => new Date(b.ts) - new Date(a.ts));
              const pageSize = 10;
              const totalPages = Math.ceil(allItems.length / pageSize) || 1;
              const safePage = Math.min(historyPage, totalPages - 1);
              const pageItems = allItems.slice(safePage * pageSize, (safePage + 1) * pageSize);

              return (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Time</TableHead>
                          {platformAdmin && <TableHead>Tenant</TableHead>}
                          <TableHead>Merchant</TableHead>
                          <TableHead>URL</TableHead>
                          <TableHead>Runs</TableHead>
                          <TableHead>OK / Fail</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Result / Failure reason</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pageItems.map(item => (
                          <TableRow key={item.url}>
                            <TableCell className="text-xs whitespace-nowrap">{new Date(item.ts).toLocaleString()}</TableCell>
                            {platformAdmin && (
                              <TableCell className="text-xs max-w-[140px] truncate" title={item.tenantName}>
                                {item.tenantName || '—'}
                              </TableCell>
                            )}
                            <TableCell className="text-xs max-w-[200px] truncate" title={item.merchantName}>{item.merchantName || 'N/A'}</TableCell>
                            <TableCell className="text-xs max-w-[280px] truncate" title={item.url}>{item.url || '-'}</TableCell>
                            <TableCell className="text-xs font-mono">{item.runs}</TableCell>
                            <TableCell className="text-xs font-mono">{item.success} / {item.failed}</TableCell>
                            <TableCell>
                              {item.lastStatus === 'success' && <Badge variant="default" className="text-xs">Success</Badge>}
                              {item.lastStatus === 'failed' && <Badge variant="destructive" className="text-xs">Failed</Badge>}
                              {item.lastStatus === 'processing' && <Badge variant="secondary" className="text-xs">Processing</Badge>}
                              {!['success', 'failed', 'processing'].includes(item.lastStatus) && <Badge variant="outline" className="text-xs">{item.lastStatus || 'info'}</Badge>}
                            </TableCell>
                            <TableCell className="text-xs max-w-[400px] truncate" title={item.lastDetails || item.lastReason}>
                              {item.lastStatus === 'failed'
                                ? (item.lastDetails || item.lastReason || 'Failed')
                                : (item.lastDetails || item.lastReason || 'Completed')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-3 border-t">
                      <span className="text-xs text-muted-foreground">
                        Showing {safePage * pageSize + 1}–{Math.min((safePage + 1) * pageSize, allItems.length)} of {allItems.length} merchants
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={safePage === 0}
                          onClick={() => setHistoryPage(p => Math.max(0, p - 1))}
                          className="text-xs h-8 px-3"
                        >
                          ← Prev
                        </Button>
                        <span className="text-xs font-medium">
                          Page {safePage + 1} of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={safePage >= totalPages - 1}
                          onClick={() => setHistoryPage(p => Math.min(totalPages - 1, p + 1))}
                          className="text-xs h-8 px-3"
                        >
                          Next →
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Toast Notification Stack */}
      <div className="fixed top-4 right-4 z-[99999] flex flex-col gap-3 max-w-sm pointer-events-none">
        {toasts.map(t => {
          const styles = {
            success: { bar: 'bg-green-500', card: 'bg-white border-green-200', title: 'text-green-800', msg: 'text-green-700', icon: <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" /> },
            error:   { bar: 'bg-red-500',   card: 'bg-white border-red-200',   title: 'text-red-800',   msg: 'text-red-700',   icon: <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" /> },
            warning: { bar: 'bg-yellow-500', card: 'bg-white border-yellow-200', title: 'text-yellow-800', msg: 'text-yellow-700', icon: <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" /> },
            info:    { bar: 'bg-blue-500',  card: 'bg-white border-blue-200',  title: 'text-blue-800',  msg: 'text-blue-700',  icon: <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" /> },
          };
          const s = styles[t.type] || styles.info;
          return (
            <div key={t.id} className={`pointer-events-auto rounded-xl shadow-2xl border overflow-hidden ${s.card}`}>
              <div className={`h-1 w-full ${s.bar}`} />
              <div className="flex items-start gap-3 p-4">
                {s.icon}
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold text-sm ${s.title}`}>{t.title}</p>
                  <p className={`text-xs mt-0.5 whitespace-pre-line leading-relaxed ${s.msg}`}>{t.message}</p>
                  {t.action && (
                    <button
                      onClick={t.action.onClick}
                      className={`mt-2 text-xs font-bold underline underline-offset-2 ${s.title} hover:opacity-70`}
                    >
                      {t.action.label}
                    </button>
                  )}
                </div>
                <button onClick={() => dismissToast(t.id)} className="shrink-0 text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CrawlPage;